import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useNavigation } from '../contexts/NavigationContext';
import * as api from '../services/api';
import {
  AlertCircle, ChevronDown, ChevronUp, Plus, Search, Filter, Shield, ShieldCheck,
  CheckCircle, Clock, AlertTriangle, Eye, Trash2, Lock, MoreVertical,
  RefreshCw, Download, Edit, ToggleLeft, Settings, Radio, Network, GitBranch,
  Play, Pause, Zap, Activity, Layers, Target, TrendingUp, Loader, X, ChevronRight,
  ArrowUpRight, Bot, Cpu, Globe2, Wifi, Server, Database, Workflow, Lock as LockIcon,
  Unlock, Sparkles, FileText, Hash, Link2, ShieldAlert, BarChart3, ExternalLink
} from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
/* G101 — full v2 redesign of WorkflowDiscoveryPage. Replaces 100vh
 * flex layout + gradient tab bar with V2PageShell + V2TabNav.
 * v1 hooks/handlers/api calls preserved verbatim. */
import {
  V2PageShell,
  TabNav as V2TabNav,
  Pill as V2Pill,
} from '../components/v2';

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — Workflow Discovery (matches ShadowDiscoveryPage)
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)',
  mid: '#d4453a', soft: '#f8d7d4', muted: '#eab3ad', tint: '#fff5f4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};

const S = { // Stone palette
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

const RISK_MAP = {
  CRITICAL: { bg: ACCENT.rose.bg,    text: ACCENT.rose.text,    border: ACCENT.rose.border,    icon: ACCENT.rose.icon },
  HIGH:     { bg: ACCENT.amber.bg,   text: ACCENT.amber.text,   border: ACCENT.amber.border,   icon: ACCENT.amber.icon },
  MEDIUM:   { bg: ACCENT.violet.bg,  text: ACCENT.violet.text,  border: ACCENT.violet.border,  icon: ACCENT.violet.icon },
  LOW:      { bg: ACCENT.emerald.bg, text: ACCENT.emerald.text, border: ACCENT.emerald.border, icon: ACCENT.emerald.icon },
};

const STATUS_MAP = {
  CONNECTED:  { label: 'Connected',  icon: CheckCircle, accent: ACCENT.emerald },
  PENDING:    { label: 'Pending',    icon: Clock,       accent: ACCENT.amber },
  ERROR:      { label: 'Error',      icon: AlertCircle, accent: ACCENT.rose },
  DISABLED:   { label: 'Disabled',   icon: Lock,        accent: ACCENT.slate },
};

const GEL_PIPELINE_STEPS = [
  { key: 'INITIATED', label: 'Initiated', order: 0 },
  { key: 'CLASSIFYING', label: 'Classifying', order: 1 },
  { key: 'RULES_GENERATED', label: 'Rules Generated', order: 2 },
  { key: 'BINDING', label: 'Binding', order: 3 },
  { key: 'TESTING', label: 'Testing', order: 4 },
  { key: 'ACTIVE', label: 'Active', order: 5 },
];

const ENFORCEMENT_MODES = [
  { key: 'MONITOR', label: 'Monitor', accent: ACCENT.sky },
  { key: 'WARN', label: 'Warn', accent: ACCENT.amber },
  { key: 'ENFORCE', label: 'Enforce', accent: ACCENT.rose },
  { key: 'BLOCK', label: 'Block', accent: ACCENT.rose },
];

/* ─── Polymorphic Connector Type Metadata ──────────────────────────── */
const CONNECTOR_TYPES = {
  TEMPORAL:       { label: 'Temporal OSS',            icon: '⚡', defaultPort: 7233, supportsNamespace: true,  supportsProxy: true,  supportsDomain: false, color: ACCENT.violet },
  CONDUCTOR:      { label: 'Netflix Conductor',   icon: '🎬', defaultPort: 8080, supportsNamespace: false, supportsProxy: true,  supportsDomain: true,  color: ACCENT.amber,
                    extraFields: ['apiVersion', 'domain', 'workflowPrefix'],
                    hostPlaceholder: 'conductor.internal.local or conductor.example.com',
                    hostLabel: 'Host / Conductor OSS cluster URL',
                    authOptions: ['None', 'API Key', 'OAuth2', 'Basic', 'Token (Conductor OSS)'] },
  ARGO:           { label: 'Argo Workflows',      icon: '🐙', defaultPort: 2746, supportsNamespace: true,  supportsProxy: false, supportsDomain: false, color: ACCENT.sky },
  STEP_FUNCTIONS: { label: 'AWS Step Functions',   icon: '☁️', defaultPort: 443,  supportsNamespace: false, supportsProxy: false, supportsDomain: false, color: ACCENT.emerald },
  AIRFLOW:        { label: 'Apache Airflow',       icon: '🌊', defaultPort: 8080, supportsNamespace: false, supportsProxy: false, supportsDomain: false, color: ACCENT.slate },
  PREFECT:        { label: 'Prefect',              icon: '🔷', defaultPort: 4200, supportsNamespace: false, supportsProxy: false, supportsDomain: false, color: ACCENT.sky },
  CUSTOM:         { label: 'Custom Connector',     icon: '🔧', defaultPort: 8080, supportsNamespace: false, supportsProxy: false, supportsDomain: false, color: ACCENT.slate },
};

function ConnectorTypeBadge({ type, size = 'default' }) {
  const ct = CONNECTOR_TYPES[type] || CONNECTOR_TYPES.CUSTOM;
  const isSmall = size === 'small';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: isSmall ? 4 : 5,
      fontSize: isSmall ? 10 : 11, fontWeight: 600,
      padding: isSmall ? '2px 8px' : '3px 10px',
      borderRadius: 6, background: ct.color.bg, color: ct.color.text,
      border: `1px solid ${ct.color.border}`,
      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      letterSpacing: '0.01em',
    }}>
      <span style={{ fontSize: isSmall ? 10 : 12 }}>{ct.icon}</span>
      {ct.label}
    </span>
  );
}

// ─── Tab Information Architecture (#72 + #76) ──────────────────────────────
// Collapsed from 7 tabs to 5 with the workflow as the unit of design.
// Visual Mapper stays as a top-level tab (#76) — operators want full
// pipeline visibility for Native conversion, not a drawer flow. The
// remaining row-level Govern + Audit context lives in the Workflows row
// drawer (#73). Three legacy keys still survive in LEGACY_TAB_KEYS for
// one release cycle so deep links from docs / bookmarks don't 404.
//
//   Old (7)                                     New (5)
//   ───────────────────────────────────────     ────────────────────────────
//   Discovery Dashboard           ─┐
//                                  ├─→          Overview        (read-only)
//   (recent discoveries widget)   ─┘
//   Workflow Connectors            ──→          Sources         (only Discover trigger lives here)
//   Discovered Workflows           ──→          Workflows       (single inventory)
//   Visual Mapper                  ──→          Visual Mapper   (full conversion pipeline; "Run Natively"
//                                                                 from a row deep-links here)
//   GEL Registration              ─┐
//                                  ├─→          Workflows row drawer (Govern + Audit sub-tabs, #73)
//   Governance Bindings           ─┘
//   GEL Audit                      ──→          Audit           (global, read-only)
const TABS = [
  { key: 'overview',   label: 'Overview',      icon: Activity,    desc: 'Discovery + governance KPIs, recent activity, alerts' },
  { key: 'sources',    label: 'Sources',       icon: Network,     desc: 'External orchestrator connectors (Temporal OSS, Conductor, Argo, …) — single entry point for triggering discovery scans' },
  { key: 'workflows',  label: 'Workflows',     icon: Workflow,    desc: 'Inventory of every discovered external workflow. Click a row to govern, execute, or audit. "Run Natively" jumps to Visual Mapper.' },
  { key: 'mapper',     label: 'Visual Mapper', icon: GitBranch,   desc: 'Full external-workflow → Canvas DAG conversion pipeline. Auto-opens here when you click "Run Natively" on a workflow row.' },
  { key: 'audit',      label: 'Audit',         icon: FileText,    desc: 'Cross-workflow GEL enforcement audit trail, hash-chain integrity, and compliance analytics' },
];

// Legacy tab keys still reachable via direct URL (?tab=gel-registration etc.)
// until Phase 4 (#75) deletes the render blocks and adds proper redirects.
// Keeping the keys here lets the activeTab state machine resolve them
// without complaining; the load* effects continue to fire.
const LEGACY_TAB_KEYS = new Set(['discovery', 'connections', 'gel-registration', 'bindings']);

// Map legacy keys onto their new equivalents so a stored ?tab=... query
// param from before the collapse still lands somewhere sensible. The
// dashboard collapses into Overview; connections is now Sources;
// gel-registration and bindings fold into the Workflows row drawer.
// (mapper isn't here anymore — it's a real tab again.)
const LEGACY_TAB_REDIRECTS = {
  'discovery':        'overview',
  'connections':      'sources',
  'gel-registration': 'workflows',
  'bindings':         'workflows',
};

/* ─── Shared Base Styles ───────────────────────────────────────────────── */
const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};
const inputBase = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: S[900],
  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
};

/* ─── CSS Keyframes ────────────────────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @keyframes wdpSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes wdpPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    @keyframes wdpSlideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes wdpShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes wdpFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .wdp-card:hover { border-color: ${BRAND.primary}30 !important; box-shadow: 0 4px 20px ${BRAND.glow}, 0 1px 3px rgba(0,0,0,0.04) !important; transform: translateY(-1px); }
    .wdp-card { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
    .wdp-btn-ghost:hover { background: ${S[100]} !important; }
    .wdp-btn-primary:hover { filter: brightness(1.08); box-shadow: 0 2px 8px rgba(226,74,58,0.25); }
    .wdp-stat-card:hover { border-color: ${S[300]} !important; box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
    .wdp-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
    .wdp-search:focus { border-color: ${BRAND.primary}60 !important; box-shadow: 0 0 0 3px ${BRAND.glow} !important; }
    .wdp-row:hover { background: ${S[25]} !important; }
    .wdp-action-btn:hover { background: ${S[150]} !important; color: ${S[800]} !important; }
    .wdp-card-wrapper:hover { border-color: ${S[300]} !important; box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important; transform: translateY(-1px); }
    .wdp-card-wrapper { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
  `}</style>
);

/* ──── Reusable Components ──────────────────────────────────────────── */

function ShimmerBlock({ width = '100%', height = 28 }) {
  return (
    <div style={{
      width, height, borderRadius: 6,
      background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`,
      backgroundSize: '200% 100%', animation: 'wdpShimmer 1.5s infinite',
    }} />
  );
}

function RiskBadge({ tier, size = 'default' }) {
  const r = RISK_MAP[tier] || RISK_MAP.MEDIUM;
  const isSmall = size === 'small';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: isSmall ? 4 : 5,
      fontSize: isSmall ? 10 : 11, fontWeight: 600,
      padding: isSmall ? '2px 8px' : '3px 10px',
      borderRadius: 6, background: r.bg, color: r.text,
      border: `1px solid ${r.border}`,
      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      letterSpacing: '0.01em',
    }}>
      <AlertTriangle size={isSmall ? 10 : 11} />
      {tier}
    </span>
  );
}

function StatusBadge({ status, size = 'default' }) {
  const s = STATUS_MAP[status] || STATUS_MAP.PENDING;
  const Icon = s.icon;
  const isSmall = size === 'small';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: isSmall ? 4 : 5,
      fontSize: isSmall ? 10 : 11, fontWeight: 600,
      padding: isSmall ? '2px 8px' : '3px 10px',
      borderRadius: 6, background: s.accent.bg, color: s.accent.text,
      border: `1px solid ${s.accent.border}`,
      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      letterSpacing: '0.01em',
    }}>
      <Icon size={isSmall ? 10 : 11} />
      {s.label}
    </span>
  );
}

function StatCard({ label, value, subtitle, icon: Icon, accent, loading: isLoading }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="wdp-stat-card" style={{
      flex: 1, minWidth: 180, background: '#fff', border: `1px solid ${S[200]}`,
      borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden',
      cursor: 'default', transition: 'all 0.2s ease',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.15 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: a.text, textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 8,
          }}>{label}</div>
          {isLoading ? (
            <ShimmerBlock width={48} height={28} />
          ) : (
            <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{value}</div>
          )}
          {subtitle && <div style={{ fontSize: 11, color: S[500], marginTop: 6, lineHeight: 1.3 }}>{subtitle}</div>}
        </div>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: a.bg, border: `1px solid ${a.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {Icon && <Icon size={17} color={a.icon} strokeWidth={2} />}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, accent, actions, children }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="wdp-card" style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.12 }} />
      <div style={{ padding: '18px 20px' }}>
        {(title || actions) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            {title && <div style={{ fontSize: 13, fontWeight: 700, color: S[800], letterSpacing: '-0.01em' }}>{title}</div>}
            {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function TabSkeleton() {
  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, animation: 'wdpFadeIn 0.3s ease' }}>
      <div style={{ display: 'flex', gap: 16 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            flex: 1, minWidth: 180, background: '#fff', border: `1px solid ${S[200]}`,
            borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: S[200] }} />
            <ShimmerBlock width={80} height={10} />
            <div style={{ marginTop: 12 }}><ShimmerBlock width={48} height={28} /></div>
            <div style={{ marginTop: 8 }}><ShimmerBlock width={120} height={10} /></div>
          </div>
        ))}
      </div>
      <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: 20 }}>
        <ShimmerBlock width={160} height={14} />
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3, 4, 5].map(i => <ShimmerBlock key={i} height={44} />)}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div style={{
      padding: 48, textAlign: 'center', background: S[50], borderRadius: 12,
      border: `1px dashed ${S[200]}`, margin: 24, animation: 'wdpFadeIn 0.3s ease',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 12, background: S[100], border: `1px solid ${S[200]}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
      }}>
        <Icon size={22} color={S[400]} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: S[800], marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: S[500], maxWidth: 360, margin: '0 auto' }}>{subtitle}</div>
    </div>
  );
}

function MetricPill({ value, label, icon: Icon, trend }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, color: S[500], background: S[50], padding: '3px 10px',
      borderRadius: 6, border: `1px solid ${S[200]}`,
    }}>
      {Icon && <Icon size={11} color={S[400]} />}
      <strong style={{ color: S[700], fontWeight: 600 }}>{value}</strong>
      {label && <span>{label}</span>}
      {trend && <TrendingUp size={10} color={ACCENT.emerald.icon} />}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAB PANELS
   ══════════════════════════════════════════════════════════════════════════════ */

const DiscoveryDashboardTab = ({ loading, data, connectors }) => {
  // #72 — Discover-Workflows button moved to Sources tab; the picker
  // state that used to drive its dropdown is gone with the widget that
  // hosted it. The `onDiscoverWorkflows` prop the caller passed in is
  // intentionally unused here now (kept as a no-op receiver in case
  // legacy parent code still passes it).
  if (loading) return <TabSkeleton />;

  const dashboard = data || {};
  const metrics = dashboard.metrics || {};
  const activeConnectors = (connectors || []).filter(c => c.status === 'CONNECTED');

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, animation: 'wdpFadeIn 0.3s ease' }}>
      {/* Hero Stat Row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard label="Workflow Connectors" value={metrics.totalConnections || 0} subtitle="Active connections" icon={Network} accent={ACCENT.sky} />
        <StatCard label="Workflows Discovered" value={metrics.totalWorkflows || 0} subtitle="External workflows" icon={Workflow} accent={ACCENT.violet} />
        <StatCard label="Protected Workflows" value={metrics.protectedWorkflows || 0} subtitle="With GEL bindings" icon={ShieldCheck} accent={ACCENT.emerald} />
        <StatCard label="Unprotected" value={metrics.unprotectedWorkflows || 0} subtitle="Require protection" icon={AlertTriangle} accent={ACCENT.amber} />
      </div>

      {/* #72 — Recent Discoveries widget removed. Workflows tab is the
          canonical inventory; surfacing the same rows here was the
          single biggest source of "where do I look?" confusion. The
          Discover-Workflows button used to live in this widget's
          header — it now exists only on the Sources tab where it
          belongs (one entry point for triggering scans). */}

      {/* #77 — Placeholder "GEL Registration Pipeline" widget removed.
          The full GELRegistrationTab content (Active Registration
          Pipelines + Completed Registrations) is now rendered inline
          under DiscoveryDashboardTab as a sibling, in embedded mode.
          See the parent's activeTab==='overview' block. The minimal
          dashboard.activePipelines summary it used to render duplicated
          gelRegistrationData.pipelines so the merge is a strict UX
          gain — same data, richer presentation, in one place. */}
      {false && (
        <SectionCard title="GEL Registration Pipeline" accent={ACCENT.rose}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(dashboard.activePipelines || []).length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: S[400], fontSize: 13 }}>No active pipelines</div>
          ) : (
            (dashboard.activePipelines || []).slice(0, 5).map((pipeline, i) => {
              const currentStep = GEL_PIPELINE_STEPS.find(s => s.key === pipeline.currentStep);
              const stepProgress = currentStep ? (currentStep.order + 1) / GEL_PIPELINE_STEPS.length : 0;
              return (
                <div key={i} style={{
                  padding: 14, background: S[50], border: `1px solid ${S[200]}`, borderRadius: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: S[800] }}>{pipeline.source}</div>
                      <div style={{ fontSize: 10, color: S[500], marginTop: 2 }}>{pipeline.sourceType}</div>
                    </div>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                      background: ACCENT.violet.bg, color: ACCENT.violet.text, border: `1px solid ${ACCENT.violet.border}`,
                    }}>
                      <Clock size={10} />
                      {pipeline.currentStep}
                    </span>
                  </div>
                  <div style={{ position: 'relative', height: 6, background: S[150], borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      position: 'absolute', height: '100%', background: BRAND.gradient,
                      width: `${stepProgress * 100}%`, transition: 'width 0.3s ease',
                    }} />
                  </div>
                  <div style={{ fontSize: 9, color: S[500], marginTop: 6, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {currentStep ? currentStep.order + 1 : 0} of {GEL_PIPELINE_STEPS.length} steps
                  </div>
                </div>
              );
            })
          )}
        </div>
      </SectionCard>
      )}

      {/* Discovery Activity Placeholder */}
      <SectionCard title="Discovery Scan Activity" accent={ACCENT.sky}>
        <div style={{
          padding: 48, textAlign: 'center', background: S[50], borderRadius: 10,
          border: `1px dashed ${S[200]}`, fontSize: 13, color: S[400],
        }}>
          Discovery scan activity over time
        </div>
      </SectionCard>
    </div>
  );
};

/* ─── Reverse map: backend auth enum → display label ─────────────────── */
const AUTH_TYPE_DISPLAY = {
  'NONE': 'None', 'API_KEY': 'API Key', 'MTLS': 'mTLS', 'OAUTH2': 'OAuth2',
  'BASIC': 'Basic', 'TOKEN': 'Token (Conductor OSS)', 'IAM_ROLE': 'IAM Role',
};
const AUTH_TYPE_TO_BACKEND = {
  'None': 'NONE', 'API Key': 'API_KEY', 'mTLS': 'MTLS', 'OAuth2': 'OAUTH2',
  'Basic': 'BASIC', 'Token (Conductor OSS)': 'TOKEN', 'IAM Role': 'IAM_ROLE',
};

/**
 * Inline config panel for an existing connector — loads saved values
 * including auth credentials, and supports editing + saving back.
 */
const ConnectorConfigPanel = ({ conn, onSaveConfig, onConfigure }) => {
  const authDisplay = AUTH_TYPE_DISPLAY[conn.authType] || conn.authType || 'None';
  const isTokenAuth = conn.authType === 'TOKEN' || conn.authType === 'API_KEY';
  const isOAuth2 = conn.authType === 'OAUTH2';
  const isMtls = conn.authType === 'MTLS';

  const [cfg, setCfg] = React.useState({
    host: conn.host || '',
    port: conn.port || 443,
    scanIntervalMinutes: conn.scanIntervalMinutes ?? 60,
    autoScanEnabled: conn.autoScanEnabled ?? true,
    domain: conn.connectorConfig?.domain || conn.domain || '',
    apiVersion: conn.connectorConfig?.apiVersion || 'v3',
    workflowPrefix: conn.connectorConfig?.workflowPrefix || '',
    namespace: conn.namespace || (CONNECTOR_TYPES[conn.connectorType]?.supportsNamespace ? 'default' : ''),
    tlsEnabled: conn.tlsEnabled ?? false,
    authType: authDisplay,
    // Credentials — load from saved authConfig (secrets show as masked placeholders)
    authKeyId: conn.authConfig?.keyId || conn.authConfig?.clientId || '',
    authKeySecret: '', // Never pre-fill secrets from server — show placeholder instead
    hasExistingSecret: !!(conn.authConfig?.keyId || conn.authConfig?.keySecret || conn.authConfig?.clientId || conn.authConfig?.clientSecret),
    tlsCertPath: conn.authConfig?.certPath || '',
    tlsKeyPath: conn.authConfig?.keyPath || '',
    tlsCaPath: conn.authConfig?.caPath || '',
  });

  const selectedAuthType = cfg.authType;
  const showCredentials = selectedAuthType !== 'None';

  return (
    <div style={{
      marginTop: 14, padding: 16, background: S[50], border: `1px solid ${S[200]}`,
      borderRadius: 10, animation: 'wdpSlideUp 0.2s ease',
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: S[700], marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Settings size={14} color={S[500]} />
        Configure: {conn.name}
      </div>
      {/* Host & Port */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Host</label>
          <input type="text" value={cfg.host}
            onChange={e => setCfg({ ...cfg, host: e.target.value })}
            placeholder="e.g., developer.conductor-oss.example.com"
            style={{ ...inputBase, fontSize: 12 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Port</label>
          <input type="number" value={cfg.port}
            onChange={e => setCfg({ ...cfg, port: parseInt(e.target.value) || 443 })}
            style={{ ...inputBase, fontSize: 12 }} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
            Scan Interval (minutes)
          </label>
          <input type="number" value={cfg.scanIntervalMinutes}
            onChange={e => setCfg({ ...cfg, scanIntervalMinutes: parseInt(e.target.value) || 60 })}
            style={{ ...inputBase, fontSize: 12 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
            Auto-Scan
          </label>
          <select value={cfg.autoScanEnabled ? 'true' : 'false'}
            onChange={e => setCfg({ ...cfg, autoScanEnabled: e.target.value === 'true' })}
            style={{ ...inputBase, fontSize: 12 }}>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        </div>
      </div>
      {conn.connectorType === 'CONDUCTOR' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Domain</label>
            <input type="text" value={cfg.domain}
              onChange={e => setCfg({ ...cfg, domain: e.target.value })}
              style={{ ...inputBase, fontSize: 12 }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>API Version</label>
            <select value={cfg.apiVersion}
              onChange={e => setCfg({ ...cfg, apiVersion: e.target.value })}
              style={{ ...inputBase, fontSize: 12 }}>
              <option value="v3">v3</option>
              <option value="v2">v2</option>
              <option value="v1">v1</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Workflow Prefix</label>
            <input type="text" placeholder="e.g., agentlets_" value={cfg.workflowPrefix}
              onChange={e => setCfg({ ...cfg, workflowPrefix: e.target.value })}
              style={{ ...inputBase, fontSize: 12 }} />
          </div>
        </div>
      )}
      {conn.connectorType === 'TEMPORAL' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Namespace</label>
            <input type="text" value={cfg.namespace}
              onChange={e => setCfg({ ...cfg, namespace: e.target.value })}
              style={{ ...inputBase, fontSize: 12 }} />
          </div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: showCredentials ? 10 : 12 }}>
        <div>
          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>TLS Enabled</label>
          <select value={cfg.tlsEnabled ? 'true' : 'false'}
            onChange={e => setCfg({ ...cfg, tlsEnabled: e.target.value === 'true' })}
            style={{ ...inputBase, fontSize: 12 }}>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Authentication</label>
          <select value={cfg.authType}
            onChange={e => setCfg({ ...cfg, authType: e.target.value, authKeyId: '', authKeySecret: '', hasExistingSecret: false })}
            style={{ ...inputBase, fontSize: 12 }}>
            <option value="None">None</option>
            <option value="API Key">API Key</option>
            <option value="OAuth2">OAuth2</option>
            <option value="mTLS">mTLS</option>
            {conn.connectorType === 'CONDUCTOR' && <option value="Token (Conductor OSS)">Token (Conductor OSS)</option>}
            {conn.connectorType === 'STEP_FUNCTIONS' && <option value="IAM Role">IAM Role</option>}
          </select>
        </div>
      </div>
      {/* Credential fields — shown when auth type requires them */}
      {showCredentials && (
        <div style={{
          padding: 12, background: '#fffbeb', border: '1px solid #fde68a',
          borderRadius: 8, marginBottom: 12,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Lock size={11} />
            {selectedAuthType === 'Token (Conductor OSS)' ? 'Conductor OSS Application Credentials' : `${selectedAuthType} Credentials`}
            {cfg.hasExistingSecret && <span style={{ fontWeight: 500, opacity: 0.7 }}> (saved — leave blank to keep)</span>}
          </div>
          {(selectedAuthType === 'API Key' || selectedAuthType === 'Token (Conductor OSS)') && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>
                  {selectedAuthType === 'Token (Conductor OSS)' ? 'Key ID' : 'API Key'}
                </label>
                <input type="text" value={cfg.authKeyId}
                  placeholder={cfg.hasExistingSecret ? '••••••• (saved)' : 'Enter key ID'}
                  onChange={e => setCfg({ ...cfg, authKeyId: e.target.value })}
                  style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>
                  {selectedAuthType === 'Token (Conductor OSS)' ? 'Key Secret' : 'Secret'}
                </label>
                <input type="password" value={cfg.authKeySecret}
                  placeholder={cfg.hasExistingSecret ? '••••••• (saved)' : 'Enter secret'}
                  onChange={e => setCfg({ ...cfg, authKeySecret: e.target.value })}
                  style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }} />
              </div>
            </div>
          )}
          {selectedAuthType === 'OAuth2' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Client ID</label>
                <input type="text" value={cfg.authKeyId}
                  placeholder={cfg.hasExistingSecret ? '••••••• (saved)' : 'OAuth2 Client ID'}
                  onChange={e => setCfg({ ...cfg, authKeyId: e.target.value })}
                  style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Client Secret</label>
                <input type="password" value={cfg.authKeySecret}
                  placeholder={cfg.hasExistingSecret ? '••••••• (saved)' : 'OAuth2 Client Secret'}
                  onChange={e => setCfg({ ...cfg, authKeySecret: e.target.value })}
                  style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }} />
              </div>
            </div>
          )}
          {selectedAuthType === 'mTLS' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Cert Path</label>
                <input type="text" value={cfg.tlsCertPath}
                  onChange={e => setCfg({ ...cfg, tlsCertPath: e.target.value })}
                  style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Key Path</label>
                <input type="text" value={cfg.tlsKeyPath}
                  onChange={e => setCfg({ ...cfg, tlsKeyPath: e.target.value })}
                  style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>CA Path</label>
                <input type="text" value={cfg.tlsCaPath}
                  onChange={e => setCfg({ ...cfg, tlsCaPath: e.target.value })}
                  style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }} />
              </div>
            </div>
          )}
          {selectedAuthType === 'Token (Conductor OSS)' && (
            <div style={{ fontSize: 9, color: '#92400e', marginTop: 6, opacity: 0.7 }}>
              Conductor OSS console → Access Control → Applications → Create Access Key
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => { if (onSaveConfig) onSaveConfig(conn.id, cfg); onConfigure(null); }}
          className="wdp-btn-primary"
          style={{ ...btnBase, padding: '8px 14px', background: BRAND.gradient, color: '#fff', fontSize: 11, fontWeight: 600 }}
        >
          <CheckCircle size={12} />
          Save Configuration
        </button>
        <button
          onClick={() => onConfigure(null)}
          className="wdp-btn-ghost"
          style={{ ...btnBase, padding: '8px 14px', background: S[100], color: S[600], fontSize: 11, fontWeight: 500 }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const WorkflowConnectorsTab = ({ loading, data, onAddConnection, onTestConnection, onDiscoverWorkflows, onRemoveConnection, onConfigure, configureConnId, onSaveConfig }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', connectorType: 'TEMPORAL', host: '', port: 7233, namespace: 'default', authType: 'None' });

  if (loading) return <TabSkeleton />;

  const connections = data || [];

  const handleConnectorTypeChange = (type) => {
    const ct = CONNECTOR_TYPES[type] || CONNECTOR_TYPES.TEMPORAL;
    setFormData({
      ...formData,
      connectorType: type,
      port: ct.defaultPort,
      namespace: ct.supportsNamespace ? 'default' : '',
      // Conductor-specific fields
      domain: ct.supportsDomain ? 'default' : '',
      apiVersion: type === 'CONDUCTOR' ? 'v3' : '',
      workflowPrefix: '',
    });
  };

  const handleAddConnection = async () => {
    await onAddConnection(formData);
    setFormData({ name: '', connectorType: 'TEMPORAL', host: '', port: 7233, namespace: 'default', authType: 'None', domain: '', apiVersion: '', workflowPrefix: '', authKeyId: '', authKeySecret: '', tlsCertPath: '', tlsKeyPath: '', tlsCaPath: '' });
    setShowAddModal(false);
  };

  const selectedType = CONNECTOR_TYPES[formData.connectorType] || CONNECTOR_TYPES.TEMPORAL;

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, animation: 'wdpFadeIn 0.3s ease' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: S[800], marginBottom: 2 }}>External Workflow Connectors</div>
          <div style={{ fontSize: 12, color: S[500] }}>Connect to Temporal OSS, Conductor, Argo, Step Functions, Airflow, Prefect and more</div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="wdp-btn-primary"
          style={{ ...btnBase, padding: '10px 16px', background: BRAND.gradient, color: '#fff', fontSize: 12, fontWeight: 600 }}
        >
          <Plus size={15} />
          Add Connector
        </button>
      </div>

      {/* Add Connection Modal */}
      {showAddModal && (
        <div style={{
          padding: 20, background: S[50], border: `1px solid ${S[200]}`, borderRadius: 12,
          animation: 'wdpSlideUp 0.2s ease',
        }}>
          {/* Connector Type Selector */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Connector Type
            </label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Object.entries(CONNECTOR_TYPES).map(([key, ct]) => (
                <button
                  key={key}
                  onClick={() => handleConnectorTypeChange(key)}
                  style={{
                    ...btnBase, padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    background: formData.connectorType === key ? ct.color.bg : '#fff',
                    color: formData.connectorType === key ? ct.color.text : S[500],
                    border: `1.5px solid ${formData.connectorType === key ? ct.color.border : S[200]}`,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span style={{ fontSize: 14 }}>{ct.icon}</span>
                  {ct.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Connection Name
            </label>
            <input
              type="text"
              placeholder={`e.g., Production ${selectedType.label}`}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{ ...inputBase, fontSize: 12 }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: selectedType.supportsNamespace ? '1fr 1fr 1fr' : '2fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                {selectedType.hostLabel || (formData.connectorType === 'STEP_FUNCTIONS' ? 'Region / ARN' : 'Host')}
              </label>
              <input
                type="text"
                placeholder={selectedType.hostPlaceholder || (formData.connectorType === 'STEP_FUNCTIONS' ? 'us-east-1' : 'localhost')}
                value={formData.host}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                style={{ ...inputBase, fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                Port
              </label>
              <input
                type="number"
                value={formData.port}
                onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                style={{ ...inputBase, fontSize: 12 }}
              />
            </div>
            {selectedType.supportsNamespace && (
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                  Namespace
                </label>
                <input
                  type="text"
                  placeholder="default"
                  value={formData.namespace}
                  onChange={(e) => setFormData({ ...formData, namespace: e.target.value })}
                  style={{ ...inputBase, fontSize: 12 }}
                />
              </div>
            )}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Authentication Type
            </label>
            <select
              value={formData.authType}
              onChange={(e) => setFormData({ ...formData, authType: e.target.value })}
              style={{ ...inputBase, fontSize: 12 }}
            >
              {selectedType.authOptions ? (
                selectedType.authOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))
              ) : (
                <>
                  <option value="None">None</option>
                  <option value="API Key">API Key</option>
                  <option value="mTLS">mTLS</option>
                  <option value="OAuth2">OAuth2</option>
                  {formData.connectorType === 'STEP_FUNCTIONS' && <option value="IAM Role">IAM Role</option>}
                </>
              )}
            </select>
          </div>
          {/* Credential fields — shown when auth type requires credentials */}
          {formData.authType && formData.authType !== 'None' && (
            <div style={{
              padding: 14, background: '#fffbeb', border: '1px solid #fde68a',
              borderRadius: 10, marginBottom: 14,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Lock size={12} />
                {formData.authType === 'Token' || formData.authType === 'Token (Conductor OSS)' ? 'Conductor OSS Application Credentials' : `${formData.authType} Credentials`}
              </div>
              {(formData.authType === 'API Key' || formData.authType === 'Token' || formData.authType === 'Token (Conductor OSS)') && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                      {formData.authType === 'Token' || formData.authType === 'Token (Conductor OSS)' ? 'Key ID' : 'API Key'}
                    </label>
                    <input
                      type="text"
                      placeholder={formData.authType === 'Token' || formData.authType === 'Token (Conductor OSS)' ? 'Your Conductor OSS Application Key ID' : 'Enter API key'}
                      value={formData.authKeyId || ''}
                      onChange={(e) => setFormData({ ...formData, authKeyId: e.target.value })}
                      style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                      {formData.authType === 'Token' || formData.authType === 'Token (Conductor OSS)' ? 'Key Secret' : 'Secret'}
                    </label>
                    <input
                      type="password"
                      placeholder={formData.authType === 'Token' || formData.authType === 'Token (Conductor OSS)' ? 'Your Conductor OSS Application Key Secret' : 'Enter secret'}
                      value={formData.authKeySecret || ''}
                      onChange={(e) => setFormData({ ...formData, authKeySecret: e.target.value })}
                      style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }}
                    />
                  </div>
                </div>
              )}
              {formData.authType === 'OAuth2' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                      Client ID
                    </label>
                    <input
                      type="text"
                      placeholder="OAuth2 Client ID"
                      value={formData.authKeyId || ''}
                      onChange={(e) => setFormData({ ...formData, authKeyId: e.target.value })}
                      style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                      Client Secret
                    </label>
                    <input
                      type="password"
                      placeholder="OAuth2 Client Secret"
                      value={formData.authKeySecret || ''}
                      onChange={(e) => setFormData({ ...formData, authKeySecret: e.target.value })}
                      style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }}
                    />
                  </div>
                </div>
              )}
              {formData.authType === 'mTLS' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                      Cert Path
                    </label>
                    <input
                      type="text"
                      placeholder="/path/to/cert.pem"
                      value={formData.tlsCertPath || ''}
                      onChange={(e) => setFormData({ ...formData, tlsCertPath: e.target.value })}
                      style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                      Key Path
                    </label>
                    <input
                      type="text"
                      placeholder="/path/to/key.pem"
                      value={formData.tlsKeyPath || ''}
                      onChange={(e) => setFormData({ ...formData, tlsKeyPath: e.target.value })}
                      style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                      CA Path
                    </label>
                    <input
                      type="text"
                      placeholder="/path/to/ca.pem"
                      value={formData.tlsCaPath || ''}
                      onChange={(e) => setFormData({ ...formData, tlsCaPath: e.target.value })}
                      style={{ ...inputBase, fontSize: 12, borderColor: '#fde68a' }}
                    />
                  </div>
                </div>
              )}
              {(formData.authType === 'Token' || formData.authType === 'Token (Conductor OSS)') && (
                <div style={{ fontSize: 10, color: '#92400e', marginTop: 8, opacity: 0.8 }}>
                  Get your Key ID and Key Secret from Conductor OSS console → Access Control → Applications → Create Access Key
                </div>
              )}
            </div>
          )}

          {/* Conductor OSS-specific fields */}
          {formData.connectorType === 'CONDUCTOR' && (
            <div style={{
              padding: 14, background: ACCENT.amber.bg, border: `1px solid ${ACCENT.amber.border}`,
              borderRadius: 10, marginBottom: 14,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT.amber.text, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14 }}>🎬</span>
                Conductor OSS Configuration
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: ACCENT.amber.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                    Domain
                  </label>
                  <input
                    type="text"
                    placeholder="default"
                    value={formData.domain || ''}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    style={{ ...inputBase, fontSize: 12, borderColor: ACCENT.amber.border }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: ACCENT.amber.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                    API Version
                  </label>
                  <select
                    value={formData.apiVersion || 'v3'}
                    onChange={(e) => setFormData({ ...formData, apiVersion: e.target.value })}
                    style={{ ...inputBase, fontSize: 12, borderColor: ACCENT.amber.border }}
                  >
                    <option value="v3">v3 (Recommended)</option>
                    <option value="v2">v2 (Legacy)</option>
                    <option value="v1">v1 (Deprecated)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: ACCENT.amber.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                    Workflow Prefix
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., agentlets_"
                    value={formData.workflowPrefix || ''}
                    onChange={(e) => setFormData({ ...formData, workflowPrefix: e.target.value })}
                    style={{ ...inputBase, fontSize: 12, borderColor: ACCENT.amber.border }}
                  />
                </div>
              </div>
              <div style={{ fontSize: 10, color: ACCENT.amber.text, marginTop: 8, opacity: 0.8 }}>
                For Conductor OSS cluster: use your cluster URL (e.g., conductor.example.com) and Token authentication.
                Open-source Conductor typically runs on port 8080 with no auth.
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleAddConnection}
              className="wdp-btn-primary"
              style={{ ...btnBase, padding: '10px 16px', background: BRAND.gradient, color: '#fff', fontSize: 12, fontWeight: 600 }}
            >
              <Plus size={14} />
              Save Connector
            </button>
            <button
              onClick={() => setShowAddModal(false)}
              className="wdp-btn-ghost"
              style={{ ...btnBase, padding: '10px 16px', background: S[100], color: S[600], fontSize: 12, fontWeight: 500 }}
            >
              Cancel
            </button>
          </div>
          <div style={{ fontSize: 10, color: S[400], marginTop: 4 }}>
            After saving, use the <strong>Test</strong> button on the connector card to verify connectivity.
          </div>
        </div>
      )}

      {/* Connection Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 }}>
        {connections.length === 0 ? (
          <EmptyState icon={Network} title="No connectors configured" subtitle="Add your first external workflow connector to begin auto-discovery" />
        ) : (
          connections.map((conn) => {
            const ct = CONNECTOR_TYPES[conn.connectorType] || CONNECTOR_TYPES.CUSTOM;
            return (
            <div key={conn.id} className="wdp-card-wrapper" style={{
              background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: 18,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: (STATUS_MAP[conn.status] || STATUS_MAP.PENDING).accent.icon, opacity: 0.15 }} />
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16 }}>{ct.icon}</span>
                    <div style={{ fontSize: 13, fontWeight: 700, color: S[800] }}>{conn.name}</div>
                  </div>
                  <div style={{ fontSize: 11, color: S[500], fontFamily: "'IBM Plex Mono', monospace" }}>
                    {conn.host}{conn.port && conn.port !== 443 ? `:${conn.port}` : ''}{conn.connectorType !== 'CONDUCTOR' && conn.namespace ? `/${conn.namespace}` : ''}
                  </div>
                </div>
                <StatusBadge status={conn.status} size="small" />
              </div>
              <div style={{ marginBottom: 10, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <ConnectorTypeBadge type={conn.connectorType} size="small" />
                {conn.connectorType === 'CONDUCTOR' && conn.domain && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: ACCENT.amber.text, background: ACCENT.amber.bg,
                    padding: '2px 8px', borderRadius: 4, border: `1px solid ${ACCENT.amber.border}`,
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}>
                    domain: {conn.domain}
                  </span>
                )}
                {conn.connectorType === 'CONDUCTOR' && conn.isOrkes && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: ACCENT.violet.text, background: ACCENT.violet.bg,
                    padding: '2px 8px', borderRadius: 4, border: `1px solid ${ACCENT.violet.border}`,
                  }}>
                    Conductor OSS cluster
                  </span>
                )}
                {conn.supportsProxy && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: ACCENT.emerald.text, background: ACCENT.emerald.bg,
                    padding: '2px 8px', borderRadius: 4, border: `1px solid ${ACCENT.emerald.border}`,
                  }}>
                    Proxy
                  </span>
                )}
              </div>

              {conn.status === 'CONNECTED' && (
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', background: ACCENT.emerald.icon,
                  animation: 'wdpPulse 2s infinite', marginBottom: 12,
                }}></div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, marginBottom: 12, color: S[600] }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Workflows Discovered:</span>
                  <span style={{ fontWeight: 600, color: S[800] }}>{conn.workflowsDiscovered || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Last Connected:</span>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{conn.lastConnected || 'Never'}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  onClick={() => onTestConnection(conn.id)}
                  className="wdp-action-btn"
                  style={{
                    ...btnBase, padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
                    background: '#fff', color: S[500], fontSize: 11, flex: 1, justifyContent: 'center',
                  }}
                >
                  <Zap size={12} />
                  Test
                </button>
                <button
                  onClick={() => onDiscoverWorkflows(conn.id)}
                  className="wdp-btn-primary"
                  style={{
                    ...btnBase, padding: '6px 10px', borderRadius: 6, border: 'none',
                    background: BRAND.primary, color: '#fff', fontSize: 11, flex: 1, justifyContent: 'center',
                  }}
                >
                  <Play size={12} />
                  Discover
                </button>
                <button
                  onClick={() => onConfigure(conn.id)}
                  className="wdp-action-btn"
                  style={{
                    ...btnBase, padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
                    background: '#fff', color: S[500], fontSize: 11,
                  }}
                >
                  <Settings size={12} />
                </button>
                <button
                  onClick={() => onRemoveConnection(conn.id)}
                  className="wdp-action-btn"
                  style={{
                    ...btnBase, padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
                    background: '#fff', color: S[500], fontSize: 11,
                  }}
                >
                  <Trash2 size={12} />
                </button>
              </div>

              {/* ── Inline Configuration Panel ── */}
              {configureConnId === conn.id && (
                <ConnectorConfigPanel conn={conn} onSaveConfig={onSaveConfig} onConfigure={onConfigure} />
              )}
            </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const DiscoveredWorkflowsTab = ({ loading, data, onProtect, onConvertNative, onViewCanvas, onTriggerExecution, onExecuteExternally }) => {
  const [filter, setFilter] = useState({ status: 'All', risk: 'All', search: '' });
  const [expandedId, setExpandedId] = useState(null);

  if (loading) return <TabSkeleton />;

  const workflows = data || [];

  let filtered = workflows.filter((w) => {
    if (filter.status !== 'All' && w.status !== filter.status) return false;
    if (filter.risk !== 'All' && w.riskTier !== filter.risk) return false;
    if (filter.search && !w.name.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16, animation: 'wdpFadeIn 0.3s ease' }}>
      {/* Filter Bar */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        padding: '12px 16px', background: '#fff', borderRadius: 10, border: `1px solid ${S[200]}`,
      }}>
        <Filter size={15} color={S[400]} />

        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          style={{ ...inputBase, width: 'auto', padding: '7px 12px', fontSize: 12 }}
        >
          <option value="All">Status: All</option>
          <option value="Discovered">Discovered</option>
          <option value="Analyzing">Analyzing</option>
          <option value="Mapped">Mapped</option>
          <option value="Protected">Protected</option>
          <option value="Ignored">Ignored</option>
        </select>

        <select
          value={filter.risk}
          onChange={(e) => setFilter({ ...filter, risk: e.target.value })}
          style={{ ...inputBase, width: 'auto', padding: '7px 12px', fontSize: 12 }}
        >
          <option value="All">Risk: All</option>
          {Object.keys(RISK_MAP).map((rt) => (
            <option key={rt} value={rt}>{rt}</option>
          ))}
        </select>

        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 220,
          padding: '7px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
          background: '#fff', transition: 'border-color 0.15s, box-shadow 0.15s',
        }}>
          <Search size={14} color={S[400]} />
          <input
            className="wdp-search"
            placeholder="Search workflows..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, background: 'transparent', color: S[900] }}
          />
        </div>

        <MetricPill value={filtered.length} label={`of ${workflows.length}`} icon={Workflow} />
      </div>

      {/* Workflow Cards */}
      {filtered.length === 0 ? (
        <EmptyState icon={Workflow} title="No workflows discovered" subtitle="Connect external orchestrators and run discovery to find workflows" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((workflow) => {
            const isExpanded = expandedId === workflow.id;
            return (
              <div key={workflow.id} className="wdp-card" style={{
                background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: (RISK_MAP[workflow.riskTier] || RISK_MAP.MEDIUM).icon, opacity: 0.12 }} />

                {/* Header */}
                <div style={{ padding: '18px 20px', borderBottom: isExpanded ? `1px solid ${S[150]}` : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : workflow.id)}
                      style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        color: S[800], fontWeight: 600, display: 'flex',
                        alignItems: 'center', gap: 8, padding: 0, fontSize: 13,
                      }}
                    >
                      {isExpanded ? <ChevronUp size={16} color={S[400]} /> : <ChevronDown size={16} color={S[400]} />}
                      <span>{workflow.name}</span>
                    </button>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {workflow.connectorType && <ConnectorTypeBadge type={workflow.connectorType} size="small" />}
                      {workflow.namespace && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: ACCENT.sky.text, background: ACCENT.sky.bg,
                          padding: '2px 8px', borderRadius: 4, border: `1px solid ${ACCENT.sky.border}`,
                        }}>
                          {workflow.namespace}
                        </span>
                      )}
                      {workflow.taskQueue && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: ACCENT.violet.text, background: ACCENT.violet.bg,
                          padding: '2px 8px', borderRadius: 4, border: `1px solid ${ACCENT.violet.border}`,
                        }}>
                          {workflow.taskQueue}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 600,
                      color: workflow.status === 'ORPHANED' ? ACCENT.rose.text
                           : workflow.status === 'PROTECTED' ? ACCENT.emerald.text
                           : workflow.status === 'REGISTERING' ? ACCENT.amber.text
                           : workflow.status === 'MAPPED' ? ACCENT.violet.text
                           : ACCENT.slate.text,
                      background: workflow.status === 'ORPHANED' ? ACCENT.rose.bg
                               : workflow.status === 'PROTECTED' ? ACCENT.emerald.bg
                               : workflow.status === 'REGISTERING' ? ACCENT.amber.bg
                               : workflow.status === 'MAPPED' ? ACCENT.violet.bg
                               : ACCENT.slate.bg,
                      padding: '3px 10px', borderRadius: 6,
                      border: `1px solid ${
                        workflow.status === 'ORPHANED' ? ACCENT.rose.border
                        : workflow.status === 'PROTECTED' ? ACCENT.emerald.border
                        : workflow.status === 'REGISTERING' ? ACCENT.amber.border
                        : workflow.status === 'MAPPED' ? ACCENT.violet.border
                        : ACCENT.slate.border
                      }`,
                    }}>
                      {workflow.status === 'REGISTERING' && <Loader size={9} style={{ marginRight: 4, animation: 'wdpSpin 1s linear infinite' }} />}
                      {workflow.status}
                    </span>
                    <RiskBadge tier={workflow.riskTier} size="small" />
                    {workflow.dataClassification && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: ACCENT.rose.text, background: ACCENT.rose.bg,
                        padding: '3px 10px', borderRadius: 6, border: `1px solid ${ACCENT.rose.border}`,
                      }}>
                        {workflow.dataClassification}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
                    <MetricPill value={workflow.executionCount} label="executions (30d)" icon={Play} />
                    <MetricPill value={workflow.avgDuration} label="avg duration" icon={Clock} />
                    <MetricPill value={workflow.failureRate} label="failure rate" icon={AlertTriangle} />
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    {/* #68 — ORPHANED rows: source workflow no longer
                        exists at the connector. Hide the Protect /
                        Run Natively actions (they have no source to
                        bind to) and surface the tombstone state with
                        a grace-period countdown + Canvas-only button
                        when a Native conversion already exists. */}
                    {workflow.status === 'ORPHANED' ? (
                      <>
                        <div style={{
                          padding: '10px 16px', background: ACCENT.rose.bg,
                          border: `1px solid ${ACCENT.rose.border}`, borderRadius: 8,
                          display: 'flex', alignItems: 'center', gap: 8, flex: 1,
                          color: ACCENT.rose.text, fontSize: 12, fontWeight: 600,
                        }}>
                          <AlertTriangle size={14} />
                          {workflow.nativeWorkflowId
                            ? 'Source deleted — Native copy preserved'
                            : (() => {
                                if (!workflow.tombstonedAt) return 'Source deleted';
                                const ageMs = Date.now() - new Date(workflow.tombstonedAt).getTime();
                                const remaining = Math.max(0, 7 - Math.floor(ageMs / 86400000));
                                return remaining > 0
                                  ? `Source deleted — purges in ${remaining}d`
                                  : 'Source deleted — pending purge';
                              })()}
                        </div>
                        {workflow.nativeWorkflowId && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onViewCanvas(workflow.nativeWorkflowId); }}
                            className="wdp-btn-primary"
                            title="Open the preserved Native canvas workflow (lineage to the deleted source remains as audit)"
                            style={{
                              ...btnBase, padding: '10px 16px',
                              background: 'linear-gradient(135deg, #0891b2, #0e7490)',
                              color: '#fff', fontSize: 12, fontWeight: 600, flex: 0, whiteSpace: 'nowrap',
                              border: 'none', borderRadius: 8,
                            }}
                          >
                            <ArrowUpRight size={14} />
                            Canvas
                          </button>
                        )}
                      </>
                    ) : workflow.status === 'REGISTERING' ? (
                      <button
                        onClick={() => onProtect(workflow.id, true)}
                        className="wdp-btn-primary"
                        title="A GEL registration pipeline is in flight for this workflow. Click to view its current step in the Active Registration Pipelines panel on Overview."
                        style={{
                          ...btnBase, padding: '10px 16px',
                          background: '#fffbeb', color: '#d97706',
                          border: '1.5px solid #d97706',
                          fontSize: 12, fontWeight: 600, flex: 1, justifyContent: 'center',
                          borderRadius: 8,
                        }}
                      >
                        <Loader size={14} style={{ animation: 'wdpSpin 1s linear infinite' }} />
                        Registering… View Pipeline
                      </button>
                    ) : (
                      // #79 — Unified three-button action cluster for any
                      // live workflow (DISCOVERED / ANALYZING / MAPPED /
                      // PROTECTED). The previous design had four buttons
                      // ("Protect with GEL" → "Execute on Conductor OSS" + "Run
                      // Natively" + "Canvas") with state-dependent
                      // visibility, which was confusing. New model:
                      //
                      //   1. Execute Externally          (green primary)
                      //   2. Execute Externally in Canvas (cyan secondary)
                      //   3. Run Natively                (purple tertiary)
                      //
                      // Both "Execute Externally" variants auto-protect
                      // first if the workflow is not yet PROTECTED — see
                      // handleExecuteExternally in the parent. "Run
                      // Natively" opens Visual Mapper for full pipeline
                      // conversion (#76).
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); onExecuteExternally(workflow, 'direct'); }}
                          className="wdp-btn-primary"
                          title="Protect with GEL and trigger external workflow invocation. For unprotected rows the click first kicks off the GEL registration pipeline; once it reaches ACTIVE, subsequent clicks invoke execution at the source."
                          style={{
                            ...btnBase, padding: '10px 14px',
                            background: 'linear-gradient(135deg, #059669, #047857)',
                            color: '#fff', fontSize: 12, fontWeight: 600,
                            flex: 1, justifyContent: 'center', whiteSpace: 'nowrap',
                            border: 'none', borderRadius: 8,
                          }}
                        >
                          <Play size={14} />
                          Execute Externally
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onExecuteExternally(workflow, 'canvas'); }}
                          className="wdp-btn-primary"
                          title="Execute Externally with the creation of a visual workflow in Prove7 Canvas. Routes execution through a Canvas DAG so the GEL enforcement chain is visible while the workflow runs at the source."
                          style={{
                            ...btnBase, padding: '10px 14px',
                            background: 'linear-gradient(135deg, #0891b2, #0e7490)',
                            color: '#fff', fontSize: 12, fontWeight: 600,
                            flex: 1, justifyContent: 'center', whiteSpace: 'nowrap',
                            border: 'none', borderRadius: 8,
                          }}
                        >
                          <GitBranch size={14} />
                          Execute Externally in Canvas
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onConvertNative(workflow.id, workflow.name); }}
                          className="wdp-btn-primary"
                          title="Convert the workflow to Agentlets Native to protect and run locally. Opens Visual Mapper for the Analyze → Map → Review JSON → Commit pipeline."
                          style={{
                            ...btnBase, padding: '10px 14px',
                            background: '#f5f0ff', color: '#7c3aed',
                            border: '1.5px solid #7c3aed',
                            fontSize: 12, fontWeight: 600,
                            flex: 1, justifyContent: 'center', whiteSpace: 'nowrap',
                            borderRadius: 8,
                          }}
                        >
                          <Sparkles size={14} />
                          Run Natively
                        </button>
                        {/* COMMITTED workflows (Visual Mapper has run and
                            produced a Canvas-native copy) keep a small
                            secondary affordance to open the existing
                            converted workflow in Canvas. Tertiary by
                            design — the three primary buttons stay
                            stable; this is just a fast-path link. */}
                        {workflow.nativeWorkflowId && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onViewCanvas(workflow.nativeWorkflowId); }}
                            className="wdp-btn-primary"
                            title="Open the existing Native canvas conversion of this workflow in the Canvas editor."
                            style={{
                              ...btnBase, padding: '10px 12px',
                              background: '#fff', color: S[700],
                              border: `1px solid ${S[300]}`,
                              fontSize: 12, fontWeight: 600,
                              flex: 0, whiteSpace: 'nowrap',
                              borderRadius: 8,
                            }}
                          >
                            <ArrowUpRight size={14} />
                            Canvas
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div style={{ padding: '18px 20px', background: S[50], animation: 'wdpSlideUp 0.2s ease' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                          Activity Types
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {(workflow.activityTypes || []).slice(0, 5).map((act, i) => (
                            <span key={i} style={{
                              fontSize: 11, color: S[700], padding: '2px 8px', background: '#fff',
                              border: `1px solid ${S[200]}`, borderRadius: 4,
                            }}>
                              {typeof act === 'object' ? JSON.stringify(act) : String(act)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                          Detected Data Flows
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {(workflow.detectedDataFlows || []).slice(0, 5).map((flow, i) => (
                            <span key={i} style={{
                              fontSize: 11, color: S[700], padding: '2px 8px', background: '#fff',
                              border: `1px solid ${S[200]}`, borderRadius: 4,
                            }}>
                              {typeof flow === 'object' ? JSON.stringify(flow) : String(flow)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                          Protection Score
                        </div>
                        <div style={{ position: 'relative', height: 20, background: S[150], borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                          <div style={{
                            position: 'absolute', height: '100%', background: BRAND.gradient,
                            width: `${workflow.governanceScore || 0}%`, transition: 'width 0.3s ease',
                          }} />
                          <div style={{
                            position: 'absolute', height: '100%', width: '100%', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 700, color: S[800], textShadow: '0 0 2px rgba(255,255,255,0.5)',
                          }}>
                            {workflow.governanceScore || 0}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// `embedded={true}` lets the Overview tab render this component inline
// without the standalone-tab chrome: the outer 24px padding (the Overview
// already pads its layout), the "Register Workflow" action button (the
// Workflows tab is the canonical entry point for registration via
// "Protect with GEL"), and the per-row Execute button (Execute lives
// only on the Workflows row now). #76 follow-on.
const GELRegistrationTab = ({ loading, data, onAdvancePipeline, onActivatePipeline, onCancelPipeline, onTriggerExecution, embedded = false }) => {
  const [expandedPipelineId, setExpandedPipelineId] = useState(null);

  // Pagination state for Completed Registrations. Page size 10/25/50,
  // initial value seeded from ?gelPageSize=N so reloads + shared links
  // preserve the chosen density. Persists back to the URL on change.
  const [completedPageSize, setCompletedPageSize] = useState(() => {
    const raw = parseInt(new URLSearchParams(window.location.search).get('gelPageSize') || '10', 10);
    return [10, 25, 50].includes(raw) ? raw : 10;
  });
  const [completedPage, setCompletedPage] = useState(0);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (completedPageSize === 10) params.delete('gelPageSize');
    else params.set('gelPageSize', String(completedPageSize));
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }, [completedPageSize]);

  if (loading) return <TabSkeleton />;

  const pipelines = data?.pipelines || [];
  const completed = data?.completed || [];
  const effectiveExec = embedded ? null : onTriggerExecution;

  // Clamp page when data shrinks (e.g. after cascade-purge) or page-size
  // grows past the current page's offset.
  const completedTotal = completed.length;
  const completedTotalPages = Math.max(1, Math.ceil(completedTotal / completedPageSize));
  const safePage = Math.min(completedPage, completedTotalPages - 1);
  const completedSlice = completed.slice(safePage * completedPageSize, (safePage + 1) * completedPageSize);

  // Outer container — embedded mode drops the padding + animation since
  // the Overview parent already supplies them.
  const wrapStyle = embedded
    ? { display: 'flex', flexDirection: 'column', gap: 20 }
    : { padding: 24, display: 'flex', flexDirection: 'column', gap: 20, animation: 'wdpFadeIn 0.3s ease' };

  return (
    <div style={wrapStyle}>
      {/* #77 follow-up — "Register Workflow" button removed entirely.
          It was an unwired placeholder (no onClick handler). The two
          real entry points are: (1) "Protect with GEL" on a workflow
          row in the Workflows tab → kicks off the registration
          pipeline; (2) adding a new Sources connector + running
          Discover, which surfaces newly-found workflows for protect.
          A manual "register an arbitrary workflow" path doesn't
          exist by design — every governed workflow must originate
          from a discovered source so its lineage is auditable. */}

      {/* Active Pipelines */}
      <SectionCard title="Active Registration Pipelines" accent={ACCENT.violet}>
        {pipelines.length === 0 ? (
          <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 12.5, color: S[600], lineHeight: 1.55 }}>
              When you click <strong>Protect with GEL</strong> on a discovered workflow, a six-step
              registration pipeline kicks off and appears here. The pipeline classifies the workflow,
              generates protection rules from that classification, binds the rules into an enforcement
              policy, and tests the binding before going live. Two of the six steps wait for operator
              input — the rest auto-advance.
            </div>
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 0,
              padding: 14, background: S[25], border: `1px solid ${S[150]}`, borderRadius: 8,
              overflowX: 'auto',
            }}>
              {[
                { k: 'INITIATED',       label: 'Initiate',  desc: 'Pipeline created.',                                                auto: true  },
                { k: 'CLASSIFYING',     label: 'Classify',  desc: 'Auto: read risk-tier, data classification, autonomy level.',       auto: true  },
                { k: 'RULES_GENERATED', label: 'Rules',     desc: 'Stops here. Operator reviews + selects auto-generated rules.',     auto: false },
                { k: 'BINDING',         label: 'Bind',      desc: 'Auto: writes rules into the enforcement binding.',                 auto: true  },
                { k: 'TESTING',         label: 'Test',      desc: 'Stops here. Operator confirms before GEL enforcement goes live.',  auto: false },
                { k: 'ACTIVE',          label: 'Active',    desc: 'GEL 6-phase chain runs on every execution of this workflow.',      auto: true  },
              ].map((s, idx, arr) => (
                <React.Fragment key={s.k}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110, padding: '0 4px' }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 11, fontWeight: 700,
                      background: '#fff', color: ACCENT.violet.text,
                      border: `2px solid ${ACCENT.violet.border}`,
                    }}>{idx + 1}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: S[800], marginTop: 6 }}>{s.label}</div>
                    <div style={{
                      fontSize: 9, fontWeight: 600, marginTop: 2, padding: '1px 6px', borderRadius: 4,
                      background: s.auto ? ACCENT.emerald.bg : ACCENT.amber.bg,
                      color:      s.auto ? ACCENT.emerald.text : ACCENT.amber.text,
                      border:    `1px solid ${s.auto ? ACCENT.emerald.border : ACCENT.amber.border}`,
                    }}>{s.auto ? 'AUTO' : 'WAITS'}</div>
                    <div style={{ fontSize: 10, color: S[500], marginTop: 6, lineHeight: 1.35, textAlign: 'center' }}>
                      {s.desc}
                    </div>
                  </div>
                  {idx < arr.length - 1 && (
                    <div style={{ width: 18, height: 2, background: S[200], marginTop: 14, flexShrink: 0 }} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div style={{ fontSize: 11, color: S[500] }}>
              Pipelines that auto-advance (no operator review needed) skip straight from CLASSIFYING to ACTIVE
              and never appear in this list — they show up directly in <strong>Completed Registrations</strong> below.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pipelines.map((pipeline) => {
              const isExpanded = expandedPipelineId === pipeline.id;
              const currentStepObj = GEL_PIPELINE_STEPS.find(s => s.key === pipeline.currentStep);
              const stepProgress = currentStepObj ? (currentStepObj.order + 1) / GEL_PIPELINE_STEPS.length : 0;

              return (
                <div key={pipeline.id} className="wdp-card" style={{
                  background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
                  position: 'relative', overflow: 'hidden',
                }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: BRAND.primary, opacity: 0.12 }} />

                  <div style={{ padding: '18px 20px', borderBottom: isExpanded ? `1px solid ${S[150]}` : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <button
                        onClick={() => setExpandedPipelineId(isExpanded ? null : pipeline.id)}
                        style={{
                          border: 'none', background: 'transparent', cursor: 'pointer',
                          color: S[800], fontWeight: 600, display: 'flex',
                          alignItems: 'center', gap: 8, padding: 0, fontSize: 13,
                        }}
                      >
                        {isExpanded ? <ChevronUp size={16} color={S[400]} /> : <ChevronDown size={16} color={S[400]} />}
                        <span>{pipeline.source}</span>
                      </button>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: ACCENT.amber.text, background: ACCENT.amber.bg,
                          padding: '3px 10px', borderRadius: 6, border: `1px solid ${ACCENT.amber.border}`,
                        }}>
                          {pipeline.sourceType}
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: ACCENT.violet.text, background: ACCENT.violet.bg,
                          padding: '3px 10px', borderRadius: 6, border: `1px solid ${ACCENT.violet.border}`,
                        }}>
                          {pipeline.status}
                        </span>
                      </div>
                    </div>

                    {/* Step Progress Indicator with Labels */}
                    <div style={{ marginBottom: 12, display: 'flex', gap: 0, alignItems: 'flex-start' }}>
                      {GEL_PIPELINE_STEPS.map((step, idx) => {
                        const currentIdx = GEL_PIPELINE_STEPS.findIndex(s => s.key === pipeline.currentStep);
                        const isCompleted = idx <= currentIdx;
                        const isCurrent = step.key === pipeline.currentStep;
                        const SHORT_LABELS = ['Initiate', 'Classify', 'Rules', 'Bind', 'Test', 'Active'];
                        return (
                          <div key={step.key} style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 48 }}>
                              <div style={{
                                width: 28, height: 28, borderRadius: '50%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', fontSize: 10,
                                fontWeight: 700, border: `2px solid ${isCompleted ? BRAND.primary : S[200]}`,
                                background: isCompleted && !isCurrent ? BRAND.primary : (isCurrent ? BRAND.light : '#fff'),
                                color: isCompleted && !isCurrent ? '#fff' : (isCurrent ? BRAND.primary : S[400]),
                              }}>
                                {isCompleted && !isCurrent ? <CheckCircle size={14} /> : idx + 1}
                              </div>
                              <span style={{
                                fontSize: 9, fontWeight: isCurrent ? 700 : 500, marginTop: 4,
                                color: isCompleted ? BRAND.primary : S[400],
                                textAlign: 'center', lineHeight: 1.1,
                              }}>
                                {SHORT_LABELS[idx] || step.label}
                              </span>
                            </div>
                            {idx < GEL_PIPELINE_STEPS.length - 1 && (
                              <div style={{
                                width: 20, height: 2, background: idx < currentIdx ? BRAND.primary : S[200],
                                transition: 'background 0.3s ease', marginTop: 13, flexShrink: 0,
                              }} />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Current Step Underline */}
                    <div style={{
                      position: 'relative', height: 4, background: S[150], borderRadius: 2, overflow: 'hidden', marginBottom: 12,
                    }}>
                      <div style={{
                        position: 'absolute', height: '100%', background: BRAND.gradient,
                        width: `${stepProgress * 100}%`, transition: 'width 0.3s ease',
                      }} />
                    </div>

                    {/* Current Step Description */}
                    {currentStepObj && (
                      <div style={{
                        padding: '8px 12px', marginBottom: 10, borderRadius: 6,
                        background: ACCENT.sky.bg, border: `1px solid ${ACCENT.sky.border}`,
                        fontSize: 11, color: ACCENT.sky.text, display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <Activity size={13} />
                        {pipeline.currentStep === 'CLASSIFYING' && 'Classifying workflow — reviewing data sensitivity, autonomy level, and risk tier.'}
                        {pipeline.currentStep === 'RULES_GENERATED' && `${pipeline.rules?.length || pipeline.classification?.generatedRules || 0} governance rules generated from classification. Review and select rules to apply.`}
                        {pipeline.currentStep === 'BINDING' && 'Binding governance rules to the workflow. This creates the enforcement policy.'}
                        {pipeline.currentStep === 'TESTING' && 'Governance binding ready. Advance to activate 6-phase GEL enforcement on this workflow.'}
                        {pipeline.currentStep === 'INITIATED' && 'Pipeline initiated — ready to begin classification.'}
                        {pipeline.currentStep === 'ACTIVE' && 'Enforcement active — GEL 6-phase chain runs on every execution.'}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {currentStepObj && currentStepObj.order < GEL_PIPELINE_STEPS.length - 1 && (
                        <button
                          onClick={() => {
                            if (pipeline.currentStep === 'TESTING') {
                              onActivatePipeline(pipeline.id);
                            } else {
                              onAdvancePipeline(pipeline.id);
                            }
                          }}
                          className="wdp-btn-primary"
                          style={{
                            ...btnBase, padding: '8px 14px', background: BRAND.gradient, color: '#fff',
                            fontSize: 11, fontWeight: 600, borderRadius: 6,
                          }}
                        >
                          {pipeline.currentStep === 'TESTING' ? (
                            <><Play size={12} /> Activate Enforcement</>
                          ) : pipeline.currentStep === 'CLASSIFYING' ? (
                            <><ArrowUpRight size={12} /> Approve Classification &amp; Generate Rules</>
                          ) : pipeline.currentStep === 'RULES_GENERATED' ? (
                            <><ArrowUpRight size={12} /> Accept Rules &amp; Create Binding</>
                          ) : pipeline.currentStep === 'BINDING' ? (
                            <><ArrowUpRight size={12} /> Confirm Binding &amp; Begin Testing</>
                          ) : (
                            <><ArrowUpRight size={12} /> Advance to Next Step</>
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => onCancelPipeline(pipeline.id)}
                        className="wdp-btn-ghost"
                        style={{
                          ...btnBase, padding: '8px 14px', background: S[100], color: S[600],
                          fontSize: 11, fontWeight: 500, borderRadius: 6,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div style={{ padding: '18px 20px', background: S[50], animation: 'wdpSlideUp 0.2s ease' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                            Classification
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                            <div><span style={{ color: S[500] }}>Data Classification:</span> <span style={{ fontWeight: 600, color: S[800] }}>{pipeline.classification?.dataLevel || 'N/A'}</span></div>
                            <div><span style={{ color: S[500] }}>Autonomy Level:</span> <span style={{ fontWeight: 600, color: S[800] }}>{pipeline.classification?.autonomyLevel || 'N/A'}</span></div>
                            <div><span style={{ color: S[500] }}>Risk Tier:</span> <RiskBadge tier={pipeline.classification?.riskTier || 'MEDIUM'} size="small" /></div>
                            <div><span style={{ color: S[500] }}>Confidence:</span> <span style={{ fontWeight: 600, color: S[800] }}>{pipeline.classification?.confidence || 0}%</span></div>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                            Generated Rules
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                            <div><span style={{ color: S[500] }}>Generated Rules:</span> <span style={{ fontWeight: 600, color: S[800] }}>{pipeline.generatedRulesCount || 0}</span></div>
                            <div><span style={{ color: S[500] }}>Custom Rules:</span> <span style={{ fontWeight: 600, color: S[800] }}>{pipeline.customRulesCount || 0}</span></div>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                            Bound Frameworks
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {(pipeline.boundFrameworks || []).map((fw, i) => (
                              <span key={i} style={{
                                fontSize: 10, fontWeight: 600, color: ACCENT.sky.text, background: ACCENT.sky.bg,
                                padding: '2px 8px', borderRadius: 4, border: `1px solid ${ACCENT.sky.border}`, display: 'inline-block',
                              }}>
                                {fw}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* Completed Registrations */}
      <SectionCard
        title="Completed Registrations"
        accent={ACCENT.emerald}
        actions={completedTotal > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: S[500] }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>Rows per page</span>
              <select
                value={completedPageSize}
                onChange={(e) => { setCompletedPageSize(parseInt(e.target.value, 10)); setCompletedPage(0); }}
                style={{
                  padding: '4px 6px', border: `1px solid ${S[200]}`, borderRadius: 6,
                  background: '#fff', fontSize: 11, color: S[700], cursor: 'pointer',
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              {completedTotal === 0 ? '0' : `${safePage * completedPageSize + 1}-${Math.min((safePage + 1) * completedPageSize, completedTotal)}`} of {completedTotal}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => setCompletedPage(p => Math.max(0, p - 1))}
                disabled={safePage === 0}
                style={{
                  ...btnBase, padding: '4px 8px', fontSize: 11, borderRadius: 6,
                  background: '#fff', color: safePage === 0 ? S[300] : S[700],
                  border: `1px solid ${S[200]}`,
                  cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                }}
              >Prev</button>
              <button
                onClick={() => setCompletedPage(p => Math.min(completedTotalPages - 1, p + 1))}
                disabled={safePage >= completedTotalPages - 1}
                style={{
                  ...btnBase, padding: '4px 8px', fontSize: 11, borderRadius: 6,
                  background: '#fff', color: safePage >= completedTotalPages - 1 ? S[300] : S[700],
                  border: `1px solid ${S[200]}`,
                  cursor: safePage >= completedTotalPages - 1 ? 'not-allowed' : 'pointer',
                }}
              >Next</button>
            </div>
          </div>
        ) : null}
      >
        {completed.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: S[400], fontSize: 13 }}>No completed registrations yet</div>
        ) : (
          <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${S[150]}` }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead style={{ background: S[50] }}>
                <tr>
                  {['Source', 'Type', 'Status', 'Activated', 'Time to Activate', 'Actions'].map(col => (
                    <th key={col} style={{
                      padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: S[500],
                      fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em',
                      borderBottom: `1px solid ${S[200]}`,
                    }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {completedSlice.map((item, i) => (
                  <tr key={i} className="wdp-row" style={{ borderBottom: i < completedSlice.length - 1 ? `1px solid ${S[150]}` : 'none', transition: 'background 0.15s' }}>
                    <td style={{ padding: '9px 12px', color: S[800], fontWeight: 600 }}>{item.source}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 500, color: ACCENT.violet.text, background: ACCENT.violet.bg,
                        padding: '2px 7px', borderRadius: 4, border: `1px solid ${ACCENT.violet.border}`,
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}>{item.type}</span>
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                        background: ACCENT.emerald.bg, color: ACCENT.emerald.text, border: `1px solid ${ACCENT.emerald.border}`,
                      }}>
                        <CheckCircle size={10} />
                        ACTIVE
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px', color: S[700], fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{item.activatedDate}</td>
                    <td style={{ padding: '9px 12px', color: S[600], fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>{item.timeToActivate}</td>
                    <td style={{ padding: '9px 12px' }}>
                      {item.sourceId && effectiveExec && (
                        <button
                          onClick={() => effectiveExec(item.sourceId, item.source)}
                          style={{
                            ...btnBase, padding: '5px 10px',
                            background: 'linear-gradient(135deg, #059669, #047857)',
                            color: '#fff', fontSize: 10, fontWeight: 600,
                            border: 'none', borderRadius: 5, whiteSpace: 'nowrap',
                          }}
                        >
                          <Play size={10} /> Execute
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

const GovernanceBindingsTab = ({ loading, data, onChangeEnforcementMode, onViewAuditTrail, onTriggerExecution, onRematerializeDags }) => {
  const [enforcementMenuOpen, setEnforcementMenuOpen] = useState({});

  if (loading) return <TabSkeleton />;

  const bindings = data || [];

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, animation: 'wdpFadeIn 0.3s ease' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: S[800], marginBottom: 2 }}>Active Protection Bindings</div>
          <div style={{ fontSize: 12, color: S[500] }}>Manage enforcement modes, Canvas DAG routing, and audit trails for protected workflows</div>
        </div>
        {onRematerializeDags && (
          <button
            onClick={onRematerializeDags}
            style={{
              ...btnBase, padding: '6px 14px', fontSize: 10, fontWeight: 600,
              background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd',
              borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <RefreshCw size={12} />
            Rebuild Canvas DAGs
          </button>
        )}
      </div>

      {bindings.length === 0 ? (
        <EmptyState icon={Shield} title="No protection bindings" subtitle="Protect discovered workflows with GEL to create active protection bindings" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {bindings.map((binding) => {
            const enforcementAccent = ENFORCEMENT_MODES.find(m => m.key === binding.enforcementMode)?.accent || ACCENT.slate;
            const isMenuOpen = enforcementMenuOpen[binding.id];

            return (
              <div key={binding.id} className="wdp-card-wrapper" style={{
                background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 14,
                position: 'relative',
              }}>
                {/* Top accent bar */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: enforcementAccent.icon, borderRadius: '14px 14px 0 0' }} />

                {/* Header row */}
                <div style={{
                  padding: '16px 20px 12px', display: 'flex', alignItems: 'flex-start',
                  justifyContent: 'space-between', gap: 16,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <ShieldCheck size={18} color={binding.bindingStatus === 'ACTIVE' ? '#059669' : S[400]} />
                      <span style={{ fontSize: 15, fontWeight: 700, color: S[900] }}>{binding.workflowName}</span>
                    </div>
                    <div style={{ fontSize: 11, color: S[500], marginBottom: 8 }}>{binding.connectionName}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: enforcementAccent.text, background: enforcementAccent.bg,
                        padding: '3px 10px', borderRadius: 20, border: `1px solid ${enforcementAccent.border}`,
                      }}>
                        {binding.enforcementMode}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: '#059669', background: '#ecfdf5',
                        padding: '3px 10px', borderRadius: 20, border: '1px solid #a7f3d0',
                      }}>
                        {binding.bindingStatus}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: '#7c3aed', background: '#f5f3ff',
                        padding: '3px 10px', borderRadius: 20, border: '1px solid #ddd6fe',
                      }}>
                        {binding.dataClassification}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: '#0369a1', background: '#f0f9ff',
                        padding: '3px 10px', borderRadius: 20, border: '1px solid #bae6fd',
                      }}>
                        {binding.autonomyLevel}
                      </span>
                    </div>
                  </div>

                  {/* Enforcement mode dropdown (top-right) */}
                  <div style={{ position: 'relative', flex: '0 0 auto' }}>
                    <button
                      onClick={() => setEnforcementMenuOpen({ ...enforcementMenuOpen, [binding.id]: !isMenuOpen })}
                      style={{
                        ...btnBase, padding: '8px 14px', background: enforcementAccent.bg,
                        color: enforcementAccent.text, border: `1px solid ${enforcementAccent.border}`,
                        fontSize: 11, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      <Settings size={12} />
                      <span>{binding.enforcementMode}</span>
                      <ChevronDown size={12} style={{ transform: isMenuOpen ? 'rotate(180deg)' : '', transition: 'transform 0.15s' }} />
                    </button>
                    {isMenuOpen && (
                      <div style={{
                        position: 'absolute', top: '100%', right: 0, marginTop: 4, minWidth: 180,
                        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden',
                      }}>
                        {ENFORCEMENT_MODES.map((mode) => (
                          <button
                            key={mode.key}
                            onClick={() => { onChangeEnforcementMode(binding.id, mode.key); setEnforcementMenuOpen({}); }}
                            className="wdp-row"
                            style={{
                              display: 'flex', width: '100%', alignItems: 'center', gap: 8,
                              textAlign: 'left', padding: '9px 14px', border: 'none',
                              background: mode.key === binding.enforcementMode ? S[50] : 'transparent',
                              cursor: 'pointer', fontSize: 12, color: S[700], transition: 'background 0.15s',
                            }}
                          >
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: mode.accent.icon }} />
                            {mode.label}
                            {mode.key === binding.enforcementMode && <CheckCircle size={12} color={mode.accent.icon} style={{ marginLeft: 'auto' }} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Stats row */}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0,
                  borderTop: `1px solid ${S[100]}`, borderBottom: `1px solid ${S[100]}`,
                }}>
                  {[
                    { label: 'Executions', value: binding.totalExecutions || 0, color: S[800] },
                    { label: 'Violations', value: binding.violationsDetected || 0, color: '#d97706' },
                    { label: 'Blocks', value: binding.blocks || 0, color: '#dc2626' },
                    { label: 'Last Enforced', value: binding.lastEnforcement || 'Never', color: S[600], isText: true },
                  ].map((stat, i) => (
                    <div key={stat.label} style={{
                      padding: '10px 16px', textAlign: 'center',
                      borderRight: i < 3 ? `1px solid ${S[100]}` : 'none',
                    }}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: S[400], textTransform: 'uppercase', letterSpacing: 0.5 }}>{stat.label}</div>
                      <div style={{
                        fontSize: stat.isText ? 10 : 18, fontWeight: 700, color: stat.color, marginTop: 2,
                        fontFamily: stat.isText ? "'IBM Plex Mono', monospace" : 'inherit',
                      }}>{stat.value}</div>
                    </div>
                  ))}
                </div>

                {/* Actions row */}
                <div style={{ padding: '12px 20px', display: 'flex', gap: 8, alignItems: 'center' }}>
                  {/* Canvas DAG status (bottom-left) */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
                    background: binding.hasEnforcementDag ? '#ecfdf5' : '#fffbeb',
                    border: `1px solid ${binding.hasEnforcementDag ? '#a7f3d0' : '#fde68a'}`,
                  }}>
                    {binding.hasEnforcementDag ? (
                      <CheckCircle size={12} color="#059669" />
                    ) : (
                      <AlertTriangle size={12} color="#d97706" />
                    )}
                    <span style={{
                      fontSize: 10, fontWeight: 600,
                      color: binding.hasEnforcementDag ? '#059669' : '#d97706',
                    }}>
                      Canvas DAG: {binding.hasEnforcementDag ? 'Active' : 'Pending'}
                    </span>
                    {binding.hasEnforcementDag && (
                      <a
                        href={`/canvas?workflowId=${binding.canvasWorkflowId}`}
                        style={{ fontSize: 9, color: '#059669', textDecoration: 'underline', marginLeft: 4 }}
                      >
                        View →
                      </a>
                    )}
                  </div>

                  <div style={{ flex: 1 }} />

                  {/* Execute with GEL (direct inline enforcement — always available on active bindings) */}
                  {binding.bindingStatus === 'ACTIVE' && binding.discoveredWorkflowId && onTriggerExecution && (
                    <button
                      onClick={() => onTriggerExecution(binding.discoveredWorkflowId, binding.workflowName, 'direct')}
                      style={{
                        ...btnBase, padding: '8px 16px',
                        background: BRAND.light, color: BRAND.primary,
                        border: `1.5px solid ${BRAND.primary}`,
                        fontSize: 11, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      <Play size={12} />
                      Execute with GEL
                    </button>
                  )}
                  {/* Execute via Canvas (visual enforcement DAG — only when Canvas DAG exists) */}
                  {binding.bindingStatus === 'ACTIVE' && binding.discoveredWorkflowId && binding.hasEnforcementDag && onTriggerExecution && (
                    <button
                      onClick={() => onTriggerExecution(binding.discoveredWorkflowId, binding.workflowName, 'canvas')}
                      style={{
                        ...btnBase, padding: '8px 16px',
                        background: '#f0f9ff', color: '#0369a1',
                        border: '1.5px solid #0369a1',
                        fontSize: 11, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      <Network size={12} />
                      Execute via Canvas
                    </button>
                  )}

                  {/* View Audit Trail */}
                  <button
                    onClick={() => onViewAuditTrail(binding.id)}
                    style={{
                      ...btnBase, padding: '8px 16px',
                      background: '#f5f3ff', color: '#7c3aed',
                      border: '1.5px solid #7c3aed',
                      fontSize: 11, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
                    }}
                  >
                    <FileText size={12} />
                    Audit Trail
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   VISUAL WORKFLOW MAPPER TAB
   Three-step pipeline: Analyze (IR) → Map (Manifest) → Materialize (Canvas)
   ══════════════════════════════════════════════════════════════════════════════ */

const VisualWorkflowMapperTab = ({ loading: parentLoading, workflows, onViewCanvas, addToast, autoSelectWorkflowId, onAutoSelectConsumed }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [pipelineStep, setPipelineStep] = useState(0); // 0=select, 1=analyze, 2=map/commit
  const [pipelineData, setPipelineData] = useState(null);
  const [stepLoading, setStepLoading] = useState(false);
  const [expandedNode, setExpandedNode] = useState(null);
  // Track whether workflows have been received at least once (shared loading is unreliable)
  const [workflowsReady, setWorkflowsReady] = useState(false);
  React.useEffect(() => {
    if (workflows && workflows.length > 0) setWorkflowsReady(true);
  }, [workflows]);
  const isLoadingWorkflows = !workflowsReady && (!workflows || workflows.length === 0);

  const STEP_LABELS = ['Select Workflow', 'Analyze Source', 'Review JSON', 'Commit'];
  const STEP_COLORS = ['#6b7280', '#3b82f6', '#8b5cf6', '#16a34a'];

  // ─── Review-JSON step state ──────────────────────────────────────
  const [sourceJsonText, setSourceJsonText] = useState('');
  const [mappedJsonText, setMappedJsonText] = useState('');
  const [mappedJsonDirty, setMappedJsonDirty] = useState(false);
  const [mappedJsonError, setMappedJsonError] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  // Collapsible "Mapping Decisions" panel (node mappings + rewrites + warnings).
  // Each step has its own default:
  //   Step 2 (Review JSON) — default-collapsed; the two-panel JSON editor is the focus.
  //   Step 3 (Commit)      — default-expanded; the commit screen is otherwise sparse
  //                          and the user is about to commit — show the decisions
  //                          recap prominently as a final sanity check.
  const [mappingDetailsExpandedStep2, setMappingDetailsExpandedStep2] = useState(false);
  const [mappingDetailsExpandedStep3, setMappingDetailsExpandedStep3] = useState(true);

  // ─── Mapper Progress Helpers ─────────────────────────────────────
  const getMapperProgress = (wf) => wf?.mapperProgress || 'NOT_STARTED';
  const isCommitted = (wf) => getMapperProgress(wf) === 'COMMITTED';
  const isAnalyzed = (wf) => getMapperProgress(wf) === 'ANALYZED';
  const isReviewReady = (wf) => getMapperProgress(wf) === 'REVIEW_READY';
  const hasProgress = (wf) => getMapperProgress(wf) !== 'NOT_STARTED';

  const PROGRESS_BADGE = {
    ANALYZED:     { label: 'Analyzed',  bg: '#dbeafe', color: '#1e40af', icon: '🔍' },
    REVIEW_READY: { label: 'Review Ready', bg: '#fef3c7', color: '#92400e', icon: '📋' },
    COMMITTED:    { label: 'Converted', bg: '#dcfce7', color: '#166534', icon: '✓' },
  };

  const confidenceColor = (conf) => {
    if (conf === 'HIGH') return '#16a34a';
    if (conf === 'MEDIUM') return '#d97706';
    if (conf === 'LOW') return '#dc2626';
    return '#6b7280';
  };

  const severityColor = (sev) => {
    if (sev === 'ERROR') return '#dc2626';
    if (sev === 'WARNING') return '#d97706';
    return '#6b7280';
  };

  const canonicalTypeIcon = (type) => {
    const icons = {
      HTTP_CALL: '\u{1F310}', INLINE_SCRIPT: '\u{1F4DD}', DATA_TRANSFORM: '\u{1F504}',
      PARALLEL_FORK: '\u{1F500}', PARALLEL_JOIN: '\u{1F500}', CONDITIONAL: '\u{2753}',
      LOOP: '\u{1F501}', SUB_WORKFLOW: '\u{1F4E6}', HUMAN_TASK: '\u{1F464}',
      WAIT_SIGNAL: '\u{23F3}', MESSAGE_PUBLISH: '\u{1F4E8}', TERMINATE: '\u{1F6D1}',
      EVENT_PUBLISHER: '\u{1F4E2}', EVENT_LISTENER: '\u{1F4E5}', EXTERNAL_TASK: '\u{2699}',
      SET_VARIABLE: '\u{1F4CB}', CUSTOM_TASK: '\u{2754}', DYNAMIC_FORK: '\u{26A1}',
      ASYNC_SUB_WORKFLOW: '\u{1F680}',
    };
    return icons[type] || '\u{2699}';
  };

  // ─── Smart workflow selection — resumes from saved progress ──────
  const handleSelectWorkflow = async (wf) => {
    const progress = getMapperProgress(wf);

    // Already committed — show committed state (no re-analysis needed)
    if (progress === 'COMMITTED') {
      setSelectedWorkflow(wf);
      setPipelineStep(4); // special "committed" view (after Commit step)
      setPipelineData(null);
      return;
    }

    // Has previous progress — resume from the Review step
    if (progress === 'REVIEW_READY') {
      setSelectedWorkflow(wf);
      setStepLoading(true);
      setReviewLoading(true);
      try {
        // Re-run mapping + materialize + fetch source JSON in parallel
        // so the Review step is fully populated (same code path as fresh flow)
        const [mapRes, materializeRes, sourceRes] = await Promise.all([
          api.discoveryMapperMap(wf.id),
          api.discoveryMapperMaterialize(wf.id),
          api.discoveryMapperSourceJson(wf.id),
        ]);
        const mappingData = mapRes?.data || mapRes;
        const materializedData = materializeRes?.data || materializeRes;
        const sourceData = sourceRes?.data || sourceRes;
        setPipelineData({ mapping: mappingData, materialized: materializedData });
        const canvasDef = materializedData?.canvasDefinition || materializedData?.canvas || materializedData;
        setMappedJsonText(JSON.stringify(canvasDef, null, 2));
        setSourceJsonText(JSON.stringify(sourceData, null, 2));
        setMappedJsonDirty(false);
        setMappedJsonError(null);
        setPipelineStep(2);
      } catch (err) {
        addToast('Resume failed \u2014 re-analyzing: ' + (err.message || err), 'error');
        await handleAnalyze(wf);
      }
      setReviewLoading(false);
      setStepLoading(false);
      return;
    }

    if (progress === 'ANALYZED') {
      // Re-run analysis to populate IR viewer
      setSelectedWorkflow(wf);
      setStepLoading(true);
      try {
        const res = await api.discoveryMapperAnalyze(wf.id);
        setPipelineData({ analysis: res?.data || res });
        setPipelineStep(1);
      } catch (err) {
        addToast('Resume failed: ' + (err.message || err), 'error');
      }
      setStepLoading(false);
      return;
    }

    // Fresh start
    await handleAnalyze(wf);
  };

  // #76 — Auto-select hand-off from "Run Natively" on the Workflows tab.
  // The parent page sets autoSelectWorkflowId; this effect waits until
  // the workflows array contains a row with that id (the page may have
  // just navigated tabs and the data load is racing the mount), then
  // calls handleSelectWorkflow once and tells the parent the value has
  // been consumed so navigating away and back doesn't re-trigger.
  // Intentionally NOT depending on handleSelectWorkflow / onAutoSelect-
  // Consumed — both are recreated on every render, and including them
  // would cause an infinite restart loop. The (autoSelectWorkflowId,
  // workflows) tuple is the real trigger.
  React.useEffect(() => {
    if (!autoSelectWorkflowId) return;
    if (!workflows || workflows.length === 0) return; // wait for data
    const target = workflows.find(w => w.id === autoSelectWorkflowId);
    if (!target) return; // wait until the row appears (rare race)
    handleSelectWorkflow(target);
    if (typeof onAutoSelectConsumed === 'function') onAutoSelectConsumed();
  }, [autoSelectWorkflowId, workflows]);

  // ─── Step 1: Analyze ─────────────────────────────────────────────
  const handleAnalyze = async (wf) => {
    setSelectedWorkflow(wf);
    setStepLoading(true);
    try {
      const res = await api.discoveryMapperAnalyze(wf.id);
      setPipelineData(prev => ({ ...prev, analysis: res?.data || res }));
      setPipelineStep(1);
    } catch (err) {
      addToast('Analysis failed: ' + (err.message || err), 'error');
    }
    setStepLoading(false);
  };

  // ─── Step 2: Map (manifest only — kept for programmatic resume) ──
  const handleMap = async () => {
    setStepLoading(true);
    try {
      const res = await api.discoveryMapperMap(selectedWorkflow.id);
      setPipelineData(prev => ({ ...prev, mapping: res?.data || res }));
      setPipelineStep(2);
    } catch (err) {
      addToast('Mapping failed: ' + (err.message || err), 'error');
    }
    setStepLoading(false);
  };

  // ─── Step 1 → Step 2: Proceed to Review JSON ─────────────────────
  // Runs mapping + materialization + fetches raw source JSON in parallel
  // so the user sees both sides of the diff when reviewing.
  const handleProceedToReview = async () => {
    setStepLoading(true);
    setReviewLoading(true);
    try {
      const [mapRes, materializeRes, sourceRes] = await Promise.all([
        api.discoveryMapperMap(selectedWorkflow.id),
        api.discoveryMapperMaterialize(selectedWorkflow.id),
        api.discoveryMapperSourceJson(selectedWorkflow.id),
      ]);
      const mappingData = mapRes?.data || mapRes;
      const materializedData = materializeRes?.data || materializeRes;
      const sourceData = sourceRes?.data || sourceRes;

      setPipelineData(prev => ({
        ...prev,
        mapping: mappingData,
        materialized: materializedData,
      }));

      // Pretty-print both JSON payloads for the textarea editors
      const canvasDef = materializedData?.canvasDefinition || materializedData?.canvas || materializedData;
      setMappedJsonText(JSON.stringify(canvasDef, null, 2));
      setSourceJsonText(JSON.stringify(sourceData, null, 2));
      setMappedJsonDirty(false);
      setMappedJsonError(null);
      setPipelineStep(2);
    } catch (err) {
      addToast('Failed to load Review step: ' + (err.message || err), 'error');
    }
    setReviewLoading(false);
    setStepLoading(false);
  };

  // ─── Step 2 → Step 3: Validate + advance to Commit ───────────────
  const handleProceedToCommit = () => {
    // Validate JSON parse before advancing if user has edited
    if (mappedJsonDirty) {
      try {
        JSON.parse(mappedJsonText);
        setMappedJsonError(null);
      } catch (err) {
        setMappedJsonError('Invalid JSON: ' + err.message);
        addToast('Mapped JSON is not valid — fix before proceeding', 'error');
        return;
      }
    }
    setPipelineStep(3);
  };

  // ─── Reset mapped JSON back to the generated version ─────────────
  const handleResetMappedJson = () => {
    const canvasDef = pipelineData?.materialized?.canvasDefinition
      || pipelineData?.materialized?.canvas
      || pipelineData?.materialized;
    if (canvasDef) {
      setMappedJsonText(JSON.stringify(canvasDef, null, 2));
      setMappedJsonDirty(false);
      setMappedJsonError(null);
    }
  };

  // ─── Materialize + Commit (single action — skips useless preview) ─
  const handleCommitToCanvas = async () => {
    setStepLoading(true);
    try {
      // Build commit body. If user edited the mapped JSON in Step 2,
      // send the override so the backend persists their edits instead
      // of the auto-generated canvas definition.
      let body = null;
      if (mappedJsonDirty) {
        try {
          const parsed = JSON.parse(mappedJsonText);
          body = { canvasDefinition: parsed };
        } catch (err) {
          setMappedJsonError('Invalid JSON: ' + err.message);
          addToast('Cannot commit — mapped JSON is not valid', 'error');
          setStepLoading(false);
          return;
        }
      }
      await api.discoveryMapperMaterialize(selectedWorkflow.id);
      const res = await api.discoveryMapperCommit(selectedWorkflow.id, body);
      const result = res?.data || res;
      console.log('[Mapper] Commit result:', JSON.stringify(result));
      if (result.success || result.alreadyConverted) {
        const nativeId = result.nativeWorkflowId;
        addToast(result.message || 'Workflow committed — opening in Canvas', 'success');
        // Update local workflow object to reflect committed state
        if (selectedWorkflow) {
          selectedWorkflow.mapperProgress = 'COMMITTED';
          selectedWorkflow.nativeWorkflowId = nativeId;
        }
        if (nativeId) {
          console.log('[Mapper] Navigating to Canvas with workflowId:', nativeId);
          // Use direct navigation — onViewCanvas may not work if component unmounts
          window.location.href = `/canvas?workflowId=${nativeId}`;
        } else {
          // Fallback: if nativeWorkflowId is missing, update UI to committed state
          console.warn('[Mapper] No nativeWorkflowId in commit response — showing committed state');
          setPipelineStep(4);
        }
      } else {
        addToast('Commit failed: ' + (result.message || result.error || 'Unknown error'), 'error');
      }
    } catch (err) {
      addToast('Commit failed: ' + (err.message || err), 'error');
    }
    setStepLoading(false);
  };

  // ─── Reset (local UI only) ──────────────────────────────────────
  const handleReset = () => {
    setSelectedWorkflow(null);
    setPipelineStep(0);
    setPipelineData(null);
    setExpandedNode(null);
  };

  // ─── Full Reset (clears backend progress so user can re-run) ────
  const handleFullReset = async (wf) => {
    const target = wf || selectedWorkflow;
    if (!target) return;
    setStepLoading(true);
    try {
      await api.discoveryMapperReset(target.id);
      // Update local workflow object
      target.mapperProgress = 'NOT_STARTED';
      target.nativeWorkflowId = null;
      addToast('Pipeline reset — you can re-run the conversion', 'success');
      handleReset();
    } catch (err) {
      addToast('Reset failed: ' + (err.message || err), 'error');
    }
    setStepLoading(false);
  };

  // ─── Auto Pipeline — executes via System Workflow DAG ────────────
  const handleAutoConvert = async (wf, e) => {
    if (e) e.stopPropagation(); // Don't trigger card selection
    setSelectedWorkflow(wf);
    setStepLoading(true);
    try {
      const res = await api.discoveryMapperExecuteWorkflow(wf.id);
      const result = res?.data || res;
      if (result.success) {
        addToast(
          `System workflow started (Run ${result.runId?.toString()?.substring(0, 8)}…). ` +
          'The full pipeline will run automatically — Analyze → Map → Commit → Audit.',
          'success'
        );
        // Poll for completion (simple approach — WebSocket would be better)
        setPipelineStep(0);
        setTimeout(async () => {
          try {
            const status = await api.discoveryMapperStatus(wf.id);
            const s = status?.data || status;
            if (s.mapperProgress === 'COMMITTED') {
              wf.mapperProgress = 'COMMITTED';
              wf.nativeWorkflowId = s.nativeWorkflowId;
              addToast('Auto-conversion completed — workflow is now in Canvas!', 'success');
              setPipelineStep(4);
            }
          } catch (ignored) {}
        }, 8000); // Check after 8 seconds
      } else {
        addToast('Failed to start system workflow: ' + (result.error || result.message), 'error');
      }
    } catch (err) {
      addToast('Auto-convert failed: ' + (err.message || err), 'error');
    }
    setStepLoading(false);
  };

  const ir = pipelineData?.analysis?.ir || pipelineData?.mapping?.ir;
  const manifest = pipelineData?.mapping?.manifest || pipelineData?.materialized?.manifest;
  // canvasDef removed — preview step eliminated; user goes straight to real Canvas

  // Collapsible "Mapping Decisions" panel — identical JSX is needed in both
  // Step 2 (Review JSON — reviewers see mapping decisions that produced the
  // Canvas JSON before inspecting it; default-collapsed) and Step 3 (Commit —
  // shown as a reference recap so the commit screen isn't just summary counts;
  // default-expanded). Extracted to avoid ~160 lines of duplicated JSX.
  // Caller passes its own (expanded, setExpanded) pair so each step retains
  // independent open/close state.
  const renderMappingDecisions = (expanded, setExpanded) => {
    if (!manifest) return null;
    return (
      <div style={{
        marginBottom: 12,
        border: '1px solid #e9d5ff', borderRadius: 10, background: '#fff',
        overflow: 'hidden',
      }}>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          style={{
            width: '100%', padding: '10px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            border: 'none', background: expanded ? '#faf5ff' : '#fafafa',
            cursor: 'pointer',
            borderBottom: expanded ? '1px solid #e9d5ff' : 'none',
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#6b21a8' }}>Mapping Decisions</span>
            <span style={{ fontSize: 11, color: '#475569' }}>
              {(manifest.nodeMappings || []).length} nodes mapped
              {(() => {
                const n = (manifest.nodeMappings || []).filter(m => m.rebuildRequired).length;
                return n > 0 ? ` \u00B7 ${n} need rebuild` : '';
              })()}
              {(manifest.warnings && manifest.warnings.length > 0)
                ? ` \u00B7 ${manifest.warnings.length} warning${manifest.warnings.length === 1 ? '' : 's'}`
                : ''}
              {(manifest.dataFlowRewrites && manifest.dataFlowRewrites.length > 0)
                ? ` \u00B7 ${manifest.dataFlowRewrites.length} data-flow rewrite${manifest.dataFlowRewrites.length === 1 ? '' : 's'}`
                : ''}
            </span>
          </div>
          <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
            {expanded ? `Hide ${'\u25B2'}` : `Show ${'\u25BC'}`}
          </span>
        </button>
        {expanded && (
          <div style={{ padding: 12 }}>
            {/* Node Mapping Table */}
            <SectionCard title="Node Mappings" accent="#8b5cf6">
              <div style={{ maxHeight: 380, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Source Node</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>Type</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>{'\u2192'}</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Canvas Node</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>Confidence</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(manifest.nodeMappings || []).map((m, i) => (
                      <tr
                        key={i}
                        onClick={() => setExpandedNode(expandedNode === m.sourceNodeRef ? null : m.sourceNodeRef)}
                        style={{
                          borderTop: '1px solid #f1f5f9', cursor: 'pointer',
                          background: expandedNode === m.sourceNodeRef ? '#faf5ff' : m.rebuildRequired ? '#fef2f2' : 'transparent',
                        }}
                      >
                        <td style={{ padding: '8px 10px' }}>
                          <div style={{ fontWeight: 500, color: '#1e293b' }}>{m.sourceLabel}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>{m.sourceNodeRef}</div>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, background: '#e0e7ff', color: '#3730a3' }}>
                            {m.sourceType}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', color: '#94a3b8' }}>{'\u2192'}</td>
                        <td style={{ padding: '8px 10px', color: '#1e40af', fontWeight: 500 }}>{m.canvasNodeType}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                            background: confidenceColor(m.confidence) + '15',
                            color: confidenceColor(m.confidence),
                          }}>{m.confidence}</span>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          {m.rebuildRequired ? (
                            <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: '#fee2e2', color: '#dc2626' }}>
                              REBUILD
                            </span>
                          ) : (
                            <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: '#dcfce7', color: '#16a34a' }}>
                              READY
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {/* Expanded Mapping Detail */}
            {expandedNode && manifest.nodeMappings?.find(m => m.sourceNodeRef === expandedNode) && (() => {
              const m = manifest.nodeMappings.find(m => m.sourceNodeRef === expandedNode);
              return (
                <div style={{ marginTop: 12, padding: 16, background: '#faf5ff', borderRadius: 10, border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>
                    Mapping Detail: {m.sourceLabel} {'\u2192'} {m.canvasNodeType}
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>{m.rationale}</div>
                  {m.rebuildRequired && (
                    <div style={{ padding: 8, background: '#fef2f2', borderRadius: 6, fontSize: 11, color: '#dc2626', marginBottom: 8 }}>
                      <strong>Rebuild Required:</strong> {m.rebuildReason}
                    </div>
                  )}
                  {Object.keys(m.translatedConfig || {}).length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Translated Config:</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2px 12px', fontSize: 10, fontFamily: 'monospace' }}>
                        {Object.entries(m.translatedConfig).map(([k, v]) => (
                          <React.Fragment key={k}>
                            <span style={{ color: '#6b7280' }}>{k}</span>
                            <span style={{ color: '#1e293b' }}>{String(v).substring(0, 80)}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                  {m.untranslatedConfigKeys && m.untranslatedConfigKeys.length > 0 && (
                    <div style={{ fontSize: 10, color: '#d97706' }}>
                      Untranslated: {m.untranslatedConfigKeys.join(', ')}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Data Flow Rewrites */}
            {manifest.dataFlowRewrites && manifest.dataFlowRewrites.length > 0 && (
              <SectionCard title={`Data Flow Rewrites (${manifest.dataFlowRewrites.length})`} accent="#0891b2" style={{ marginTop: 12 }}>
                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                  {manifest.dataFlowRewrites.map((r, i) => (
                    <div key={i} style={{
                      padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 11,
                      display: 'flex', gap: 12, alignItems: 'center',
                      background: r.resolved ? 'transparent' : '#fef2f2',
                    }}>
                      <span style={{ fontFamily: 'monospace', color: '#6b7280', flex: 1 }}>{r.originalExpression}</span>
                      <span style={{ color: '#94a3b8' }}>{'\u2192'}</span>
                      <span style={{ fontFamily: 'monospace', color: r.resolved ? '#16a34a' : '#dc2626', flex: 1 }}>
                        {r.resolved ? r.canvasExpression : r.unresolvedReason}
                      </span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Warnings */}
            {manifest.warnings && manifest.warnings.length > 0 && (
              <SectionCard title={`Warnings (${manifest.warnings.length})`} accent="#d97706" style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 0' }}>
                  {manifest.warnings.map((w, i) => (
                    <div key={i} style={{
                      padding: '8px 12px', borderRadius: 6, fontSize: 12,
                      background: severityColor(w.severity) + '08',
                      border: `1px solid ${severityColor(w.severity)}30`,
                    }}>
                      <span style={{ fontWeight: 600, color: severityColor(w.severity) }}>[{w.severity}]</span>{' '}
                      <span style={{ color: '#334155' }}>{w.message}</span>
                      {w.nodeRef && <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 10 }}>({w.nodeRef})</span>}
                      {w.suggestion && (
                        <div style={{ marginTop: 3, fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>{w.suggestion}</div>
                      )}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '0 24px 24px' }}>
      {/* Parent loading indicator */}
      {isLoadingWorkflows && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
          <Loader size={28} style={{ animation: 'wdpSpin 1s linear infinite' }} color="#7c3aed" />
          <span style={{ marginTop: 14, fontSize: 14, fontWeight: 600, color: '#7c3aed' }}>Loading discovered workflows…</span>
          <span style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Fetching external workflow inventory from connected orchestrators</span>
        </div>
      )}
      {/* Pipeline Step Indicator (hidden for committed state) */}
      {!isLoadingWorkflows && pipelineStep < 4 && <div style={{
        display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24, padding: '16px 20px',
        background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0',
      }}>
        {STEP_LABELS.map((label, i) => (
          <React.Fragment key={i}>
            <div
              onClick={() => { if (i === 0) handleReset(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                cursor: i === 0 ? 'pointer' : 'default',
                opacity: i <= pipelineStep ? 1 : 0.4,
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                background: i < pipelineStep ? STEP_COLORS[i] : i === pipelineStep ? STEP_COLORS[i] + '20' : '#e2e8f0',
                color: i < pipelineStep ? '#fff' : i === pipelineStep ? STEP_COLORS[i] : '#94a3b8',
                border: i === pipelineStep ? `2px solid ${STEP_COLORS[i]}` : '2px solid transparent',
              }}>
                {i < pipelineStep ? '\u2713' : i + 1}
              </div>
              <span style={{
                fontSize: 12, fontWeight: i === pipelineStep ? 700 : 500,
                color: i <= pipelineStep ? '#1e293b' : '#94a3b8',
              }}>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{
                flex: 1, height: 2, margin: '0 12px',
                background: i < pipelineStep ? STEP_COLORS[i] : '#e2e8f0',
              }} />
            )}
          </React.Fragment>
        ))}
      </div>}

      {/* STEP 0: Workflow Selection */}
      {!isLoadingWorkflows && pipelineStep === 0 && !stepLoading && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>
            Select a workflow to convert
          </h3>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            Choose an external workflow to analyze, map, and convert to a native Canvas DAG.
            {(workflows || []).some(wf => hasProgress(wf)) && (
              <span style={{ marginLeft: 6, color: '#7c3aed' }}>Workflows with saved progress will resume from where you left off.</span>
            )}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
            {/* #68 — exclude tombstoned (ORPHANED) rows from the
                Visual Mapper candidate grid. These workflows no
                longer exist at the source so converting them would
                produce a Canvas with no live counterpart. They stay
                visible on the Discovered Workflows tab where they
                render with an explicit "Source deleted" badge. */}
            {(workflows || []).filter(wf => !wf.tombstonedAt).map(wf => {
              const progress = getMapperProgress(wf);
              const badge = PROGRESS_BADGE[progress];
              const committed = isCommitted(wf);
              return (
                <div
                  key={wf.id}
                  onClick={() => handleSelectWorkflow(wf)}
                  style={{
                    padding: 16, borderRadius: 10,
                    border: committed ? '1px solid #86efac' : badge ? '1px solid #c4b5fd' : '1px solid #e2e8f0',
                    background: committed ? '#f0fdf4' : '#fff',
                    cursor: 'pointer', transition: 'all 0.15s',
                    position: 'relative',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = committed ? '#22c55e' : '#7c3aed'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = committed ? '#86efac' : badge ? '#c4b5fd' : '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', flex: 1 }}>
                      {wf.workflowType || wf.name}
                    </span>
                    {badge && (
                      <span style={{
                        padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                        background: badge.bg, color: badge.color,
                      }}>
                        {badge.icon} {badge.label}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
                    <span>{wf.connectorType || 'External'}</span>
                    {wf.connectionName && <span>\u00B7 {wf.connectionName}</span>}
                    {committed && wf.nativeWorkflowId && (
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: '#16a34a' }}>
                        Click to view \u2192
                      </span>
                    )}
                    {!committed && badge && (
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: '#7c3aed' }}>
                        Click to resume \u2192
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                    {wf.mapperLastActivityAt ? (
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>
                        Last activity: {new Date(wf.mapperLastActivityAt).toLocaleDateString()}
                      </span>
                    ) : !committed && !badge ? (
                      <span style={{ fontSize: 10, color: '#64748b' }}>
                        Click to analyze {'\u2192'}
                      </span>
                    ) : <span />}
                    {/* Auto-convert link — small secondary action for NOT_STARTED workflows */}
                    {!committed && !badge && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleAutoConvert(wf, e)}
                        style={{
                          fontSize: 10, color: '#7c3aed', cursor: 'pointer',
                          fontWeight: 600, opacity: 0.7, transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                      >
                        {'\u26A1'} Auto
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {(!workflows || workflows.length === 0) && (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                No discovered workflows available. Connect to an external orchestrator first.
              </div>
            )}
          </div>
        </div>
      )}

      {/* COMMITTED STATE — Workflow already converted */}
      {pipelineStep === 4 && selectedWorkflow && (
        <div style={{
          padding: 32, borderRadius: 16, textAlign: 'center',
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          border: '1px solid #86efac',
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#166534', marginBottom: 8 }}>
            {selectedWorkflow.workflowType || selectedWorkflow.name}
          </h3>
          <p style={{ fontSize: 14, color: '#15803d', marginBottom: 24 }}>
            This workflow has already been converted to a native Canvas DAG.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            {selectedWorkflow.nativeWorkflowId && onViewCanvas && (
              <button
                onClick={() => onViewCanvas(selectedWorkflow.nativeWorkflowId)}
                style={{
                  padding: '12px 28px', borderRadius: 8, border: 'none',
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(22,163,74,0.3)',
                }}
              >
                Open in Canvas \u2192
              </button>
            )}
            <button
              onClick={() => handleFullReset(selectedWorkflow)}
              disabled={stepLoading}
              style={{
                padding: '12px 28px', borderRadius: 8,
                border: '1px solid #d6d3d1', background: '#fff',
                color: '#57534e', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {stepLoading ? 'Resetting\u2026' : 'Reset & Re-convert'}
            </button>
            <button
              onClick={handleReset}
              style={{
                padding: '12px 28px', borderRadius: 8,
                border: '1px solid #e2e8f0', background: '#f8fafc',
                color: '#64748b', fontSize: 14, fontWeight: 500, cursor: 'pointer',
              }}
            >
              {'\u2190'} Back to List
            </button>
          </div>
        </div>
      )}

      {/* STEP 1: Source Workflow Analysis (IR Viewer) */}
      {pipelineStep === 1 && ir && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>
                Source Workflow: {ir.sourceWorkflowName}
              </h3>
              <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>
                Engine: {ir.sourceEngine} · v{ir.sourceWorkflowVersion} · Analyzed in {ir.analysisMs}ms
              </p>
            </div>
            <button onClick={handleProceedToReview} disabled={stepLoading} style={{
              padding: '10px 24px', borderRadius: 8, border: 'none',
              background: stepLoading ? '#94a3b8' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              color: '#fff', fontSize: 13, fontWeight: 600, cursor: stepLoading ? 'wait' : 'pointer',
            }}>
              {stepLoading ? 'Mapping\u2026' : `Proceed to Review ${'\u2192'}`}
            </button>
          </div>

          {/* IR Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Nodes', value: ir.nodes?.length || 0, color: '#3b82f6' },
              { label: 'Edges', value: ir.edges?.length || 0, color: '#8b5cf6' },
              { label: 'Control Blocks', value: ir.controlBlocks?.length || 0, color: '#0891b2' },
              { label: 'Max Parallelism', value: ir.maxParallelism || 1, color: '#d97706' },
              { label: 'Diagnostics', value: ir.diagnostics?.length || 0, color: ir.diagnostics?.length > 0 ? '#dc2626' : '#16a34a' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* IR Node List (Source DAG) */}
          <SectionCard title="Source Workflow Nodes" accent="#3b82f6">
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              {(ir.nodes || []).map((node, i) => (
                <div
                  key={node.nodeRef}
                  onClick={() => setExpandedNode(expandedNode === node.nodeRef ? null : node.nodeRef)}
                  style={{
                    padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer', background: expandedNode === node.nodeRef ? '#f0f9ff' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{canonicalTypeIcon(node.canonicalType)}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', flex: 1 }}>{node.label}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                      background: '#e0e7ff', color: '#3730a3',
                    }}>{node.canonicalType}</span>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>{node.sourceType}</span>
                  </div>
                  {expandedNode === node.nodeRef && (
                    <div style={{ marginTop: 8, padding: 10, background: '#f8fafc', borderRadius: 6, fontSize: 11 }}>
                      <div style={{ color: '#475569', marginBottom: 4 }}><strong>Ref:</strong> {node.nodeRef}</div>
                      <div style={{ color: '#475569', marginBottom: 4 }}><strong>Layer:</strong> {node.layer} · <strong>Lane:</strong> {node.laneIndex}</div>
                      {node.dependsOn && node.dependsOn.length > 0 && (
                        <div style={{ color: '#475569', marginBottom: 4 }}><strong>Depends on:</strong> {(Array.isArray(node.dependsOn) ? node.dependsOn : Object.keys(node.dependsOn || {})).join(', ')}</div>
                      )}
                      {node.dataBindings && node.dataBindings.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <strong style={{ color: '#475569' }}>Data Bindings:</strong>
                          {node.dataBindings.map((b, j) => (
                            <div key={j} style={{ marginTop: 2, paddingLeft: 8, color: '#64748b', fontFamily: 'monospace', fontSize: 10 }}>
                              {b.targetInputKey} {'\u2190'} {b.isWorkflowInput ? 'workflow.input.' + b.sourceField : b.sourceNodeRef + '.' + b.sourceField}
                            </div>
                          ))}
                        </div>
                      )}
                      {Object.keys(node.config || {}).length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <strong style={{ color: '#475569' }}>Config:</strong>
                          {Object.entries(node.config || {}).map(([k, v]) => (
                            <div key={k} style={{ marginTop: 1, paddingLeft: 8, color: '#64748b', fontSize: 10 }}>
                              <span style={{ color: '#334155' }}>{k}:</span> {String(v).substring(0, 80)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Control Blocks */}
          {ir.controlBlocks && ir.controlBlocks.length > 0 && (
            <SectionCard title="Control Flow Blocks" accent="#0891b2" style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 0' }}>
                {ir.controlBlocks.map((block, i) => (
                  <div key={i} style={{
                    padding: '8px 14px', borderRadius: 8, fontSize: 11,
                    background: block.blockType === 'FORK' ? '#dbeafe' : block.blockType === 'SWITCH' ? '#fef3c7' : '#e0e7ff',
                    border: `1px solid ${block.blockType === 'FORK' ? '#93c5fd' : block.blockType === 'SWITCH' ? '#fcd34d' : '#a5b4fc'}`,
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{block.blockType}: {block.entryNodeRef}</div>
                    {Object.entries(block.branches || {}).map(([name, refs]) => (
                      <div key={name} style={{ color: '#475569', fontSize: 10 }}>
                        {name}: {(refs || []).join(' \u2192 ')}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Analyzer Diagnostics — surfaces issues the adapter/analyzer flagged
              (unsupported features, ambiguous refs, dangling edges, etc.).
              Each entry may reference a sourceNodeRef so reviewers can jump
              back to the offending node in the Source DAG above. */}
          {ir.diagnostics && ir.diagnostics.length > 0 && (() => {
            const sevColor = (s) => s === 'ERROR' ? '#dc2626' : s === 'WARNING' ? '#d97706' : '#0891b2';
            const sevBg = (s) => s === 'ERROR' ? '#fef2f2' : s === 'WARNING' ? '#fffbeb' : '#ecfeff';
            const sevBorder = (s) => s === 'ERROR' ? '#fecaca' : s === 'WARNING' ? '#fde68a' : '#a5f3fc';
            return (
              <SectionCard
                title={`Analyzer Diagnostics (${ir.diagnostics.length})`}
                accent="#dc2626"
                style={{ marginTop: 12 }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 0' }}>
                  {ir.diagnostics.map((d, i) => (
                    <div
                      key={i}
                      onClick={() => d.nodeRef && setExpandedNode(d.nodeRef)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 6,
                        fontSize: 12,
                        background: sevBg(d.severity),
                        border: `1px solid ${sevBorder(d.severity)}`,
                        cursor: d.nodeRef ? 'pointer' : 'default',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <span style={{
                          padding: '1px 7px', borderRadius: 3,
                          fontSize: 9, fontWeight: 700,
                          background: sevColor(d.severity), color: '#fff',
                          letterSpacing: 0.3,
                        }}>
                          {d.severity || 'INFO'}
                        </span>
                        {d.category && (
                          <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>
                            {d.category}
                          </span>
                        )}
                        {d.nodeRef && (
                          <span style={{ fontSize: 10, color: '#2563eb', marginLeft: 'auto', fontFamily: 'monospace' }}>
                            @ {d.nodeRef}
                          </span>
                        )}
                      </div>
                      <div style={{ color: '#1e293b', lineHeight: 1.4 }}>{d.message}</div>
                      {d.suggestion && (
                        <div style={{ marginTop: 4, fontSize: 11, color: '#475569', fontStyle: 'italic' }}>
                          {'\u2192'} {d.suggestion}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </SectionCard>
            );
          })()}
        </div>
      )}

      {/* STEP 2: Review JSON (two-panel editor) */}
      {pipelineStep === 2 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>
                Review JSON{manifest ? `: ${manifest.sourceWorkflowName}` : ''}
              </h3>
              <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>
                Inspect the raw source JSON on the left; edit the mapped Canvas JSON on the right.
                Your edits will be persisted when you commit.
                {manifest && (
                  <span style={{ marginLeft: 6 }}>
                    {manifest.nodeMappings?.length || 0} nodes {'\u00B7'} {manifest.connectionMappings?.length || 0} connections
                  </span>
                )}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleResetMappedJson}
                disabled={!mappedJsonDirty || reviewLoading}
                style={{
                  padding: '10px 18px', borderRadius: 8,
                  border: '1px solid #e2e8f0', background: mappedJsonDirty ? '#fff' : '#f8fafc',
                  color: mappedJsonDirty ? '#64748b' : '#cbd5e1',
                  fontSize: 12, fontWeight: 600,
                  cursor: mappedJsonDirty ? 'pointer' : 'not-allowed',
                }}
              >
                Reset to generated
              </button>
              <button
                onClick={handleProceedToCommit}
                disabled={reviewLoading || !!mappedJsonError}
                style={{
                  padding: '10px 24px', borderRadius: 8, border: 'none',
                  background: mappedJsonError ? '#94a3b8' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  color: '#fff', fontSize: 13, fontWeight: 700,
                  cursor: mappedJsonError ? 'not-allowed' : 'pointer',
                }}
              >
                {`Proceed to Commit ${'\u2192'}`}
              </button>
            </div>
          </div>

          {/* Dirty / error banner */}
          {mappedJsonDirty && !mappedJsonError && (
            <div style={{
              padding: '8px 12px', marginBottom: 10, borderRadius: 6, fontSize: 12,
              background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e',
            }}>
              {'\u270F'} You have unsaved edits to the mapped Canvas JSON. These will be used at commit time.
            </div>
          )}
          {mappedJsonError && (
            <div style={{
              padding: '8px 12px', marginBottom: 10, borderRadius: 6, fontSize: 12,
              background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
            }}>
              {'\u26A0'} {mappedJsonError}
            </div>
          )}

          {/* Mapping Decisions (collapsible) — default-collapsed in Step 2 so
              the JSON editor stays the focal point. See renderMappingDecisions. */}
          {renderMappingDecisions(mappingDetailsExpandedStep2, setMappingDetailsExpandedStep2)}

          {/* Two-panel JSON editor */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            marginBottom: 12,
          }}>
            {/* Source JSON (read-only) */}
            <div style={{
              display: 'flex', flexDirection: 'column',
              border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
                  External Source JSON (read-only)
                </span>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>
                  {sourceJsonText ? `${sourceJsonText.length.toLocaleString()} chars` : ''}
                </span>
              </div>
              <textarea
                readOnly
                value={sourceJsonText}
                spellCheck={false}
                style={{
                  width: '100%', minHeight: 520, resize: 'vertical',
                  padding: 12, fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
                  fontSize: 11, lineHeight: 1.5, color: '#0f172a',
                  background: '#fafafa', border: 'none', outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Mapped Canvas JSON (editable) */}
            <div style={{
              display: 'flex', flexDirection: 'column',
              border: `1px solid ${mappedJsonError ? '#fca5a5' : mappedJsonDirty ? '#fcd34d' : '#c4b5fd'}`,
              borderRadius: 10,
              background: '#fff',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '8px 12px', background: '#faf5ff', borderBottom: '1px solid #e9d5ff',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#6b21a8' }}>
                  Mapped Canvas JSON (editable)
                </span>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>
                  {mappedJsonText ? `${mappedJsonText.length.toLocaleString()} chars` : ''}
                  {mappedJsonDirty && <span style={{ marginLeft: 8, color: '#d97706', fontWeight: 600 }}>{'\u2022'} edited</span>}
                </span>
              </div>
              <textarea
                value={mappedJsonText}
                spellCheck={false}
                onChange={(e) => {
                  const v = e.target.value;
                  setMappedJsonText(v);
                  setMappedJsonDirty(true);
                  // Validate on each keystroke so user sees parse errors immediately
                  try {
                    JSON.parse(v);
                    setMappedJsonError(null);
                  } catch (err) {
                    setMappedJsonError(err.message);
                  }
                }}
                style={{
                  width: '100%', minHeight: 520, resize: 'vertical',
                  padding: 12, fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
                  fontSize: 11, lineHeight: 1.5, color: '#0f172a',
                  background: '#fff', border: 'none', outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: Commit (summary + big action) */}
      {pipelineStep === 3 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>
                Commit to Canvas{manifest ? `: ${manifest.sourceWorkflowName}` : ''}
              </h3>
              <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>
                Review the summary below and commit to generate the native DAG.
                {mappedJsonDirty && (
                  <span style={{ marginLeft: 6, color: '#d97706', fontWeight: 600 }}>
                    Your edited JSON will be persisted instead of the auto-generated version.
                  </span>
                )}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setPipelineStep(2)}
                disabled={stepLoading}
                style={{
                  padding: '10px 18px', borderRadius: 8,
                  border: '1px solid #e2e8f0', background: '#fff',
                  color: '#64748b', fontSize: 12, fontWeight: 600,
                  cursor: stepLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {'\u2190'} Back to Review
              </button>
              <button
                onClick={handleCommitToCanvas}
                disabled={stepLoading || !!mappedJsonError}
                style={{
                  padding: '10px 28px', borderRadius: 8, border: 'none',
                  background: (stepLoading || mappedJsonError) ? '#94a3b8' : 'linear-gradient(135deg, #16a34a, #15803d)',
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: (stepLoading || mappedJsonError) ? 'wait' : 'pointer',
                  boxShadow: (stepLoading || mappedJsonError) ? 'none' : '0 4px 12px rgba(22,163,74,0.3)',
                }}
              >
                {stepLoading ? 'Committing\u2026' : `Commit & Open in Canvas ${'\u2192'}`}
              </button>
            </div>
          </div>

          {/* Commit Summary (compact manifest cards) */}
          {manifest && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Source Nodes', value: manifest.nodeMappings?.length || 0, color: '#3b82f6' },
                { label: 'Canvas Nodes', value: (manifest.nodeMappings?.filter(m => m.canvasNodeType)?.length || 0) + 2, color: '#16a34a' },
                { label: 'Connections', value: manifest.connectionMappings?.length || 0, color: '#8b5cf6' },
                { label: 'High Confidence', value: manifest.nodeMappings?.filter(m => m.confidence === 'HIGH')?.length || 0, color: '#16a34a' },
                { label: 'Rebuild Required', value: manifest.nodeMappings?.filter(m => m.rebuildRequired)?.length || 0, color: '#dc2626' },
                { label: 'Warnings', value: manifest.warnings?.length || 0, color: manifest.warnings?.length > 0 ? '#d97706' : '#16a34a' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Governance chips */}
          {manifest && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              {manifest.suggestedDataClassification && (
                <span style={{ padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#dbeafe', color: '#1e40af' }}>
                  Data: {manifest.suggestedDataClassification}
                </span>
              )}
              {manifest.suggestedRiskTier && (
                <span style={{ padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
                  Risk: {manifest.suggestedRiskTier}
                </span>
              )}
              {mappedJsonDirty && (
                <span style={{ padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
                  {'\u270F'} User-edited JSON
                </span>
              )}
            </div>
          )}

          {/* Mapping Decisions — shown on the Commit screen as well (default-expanded)
              so reviewers have the full decision recap in front of them before
              hitting "Commit & Open in Canvas". Same panel as Step 2, independent
              collapsible state. */}
          {renderMappingDecisions(mappingDetailsExpandedStep3, setMappingDetailsExpandedStep3)}
        </div>
      )}

      {/* Step 4 (Preview) removed — commit goes straight to real Canvas */}

      {/* Loading overlay — visible at any pipeline step */}
      {stepLoading && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: 60, margin: '24px 0', borderRadius: 12, background: '#faf5ff',
          border: '1px dashed #c4b5fd',
        }}>
          <Loader size={32} style={{ animation: 'wdpSpin 1s linear infinite' }} color="#7c3aed" />
          <span style={{ marginTop: 16, fontSize: 15, fontWeight: 600, color: '#7c3aed' }}>
            {pipelineStep === 0 || pipelineStep === 1 ? 'Analyzing workflow\u2026' :
             pipelineStep === 2 ? 'Preparing JSON review\u2026' :
             pipelineStep === 3 ? 'Committing to Canvas\u2026' : 'Processing\u2026'}
          </span>
          <span style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>
            {pipelineStep <= 1 ? 'Fetching definitions and building intermediate representation' :
             pipelineStep === 2 ? 'Materializing Canvas JSON and loading source workflow' :
             pipelineStep === 3 ? 'Materializing Canvas JSON, committing, and opening in Canvas' : ''}
          </span>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   EXECUTION INPUT MODAL
   ══════════════════════════════════════════════════════════════════════════════ */

// ═══════════════════════════════════════════════════════════════════
// NATIVE CONVERSION PREVIEW MODAL
// ═══════════════════════════════════════════════════════════════════

const NativeConversionPreviewModal = ({ preview, onCommit, onClose, loading }) => {
  if (!preview) return null;
  const { workflowName, report } = preview;

  const confidenceColor = (conf) => {
    if (conf === 'HIGH') return '#16a34a';
    if (conf === 'MEDIUM') return '#d97706';
    if (conf === 'LOW') return '#dc2626';
    return '#6b7280';
  };

  const severityColor = (sev) => {
    if (sev === 'ERROR') return '#dc2626';
    if (sev === 'WARNING') return '#d97706';
    return '#6b7280';
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, maxWidth: 720, width: '100%',
        maxHeight: '85vh', overflow: 'auto', padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ marginBottom: 24, borderBottom: '1px solid #e5e7eb', paddingBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1e293b' }}>
            Native Conversion Preview
          </h2>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>
            Converting <strong>{workflowName}</strong> from Conductor to Canvas DAG
          </p>
        </div>

        {report.status === 'FAILED' ? (
          <div style={{ padding: 16, background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca', color: '#dc2626' }}>
            <strong>Conversion Failed:</strong> {report.errorMessage}
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Source Tasks', value: report.totalSourceTasks, color: '#3b82f6' },
                { label: 'Canvas Nodes', value: report.totalCanvasNodes, color: '#16a34a' },
                { label: 'Connections', value: report.totalConnections, color: '#8b5cf6' },
                { label: 'Duration', value: `${report.conversionDurationMs}ms`, color: '#0891b2' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Dependency Analysis */}
            {report.dependencyAnalysis && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0369a1', marginBottom: 6 }}>Dependency Analysis</div>
                <div style={{ fontSize: 12, color: '#334155', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                  <span>Depth: <strong>{report.dependencyAnalysis.executionDepth}</strong> layers</span>
                  <span>Parallelism: <strong>{report.dependencyAnalysis.maxParallelism}</strong></span>
                  <span>Forks: <strong>{report.dependencyAnalysis.forkJoinPairs}</strong></span>
                  <span>Conditions: <strong>{report.dependencyAnalysis.conditionalBranches}</strong></span>
                  <span>Loops: <strong>{report.dependencyAnalysis.loops}</strong></span>
                  <span>Sub-workflows: <strong>{report.dependencyAnalysis.subWorkflows}</strong></span>
                </div>
              </div>
            )}

            {/* Task Mappings */}
            {report.taskMappings && report.taskMappings.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>
                  Task Mappings ({report.taskMappings.length})
                </div>
                <div style={{ maxHeight: 180, overflow: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}>Conductor Task</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}>Canvas Node</th>
                        <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600 }}>Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.taskMappings.map((mapping, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={{ color: '#475569' }}>{mapping.conductorTaskType}</span>
                            <span style={{ color: '#94a3b8', marginLeft: 4, fontSize: 11 }}>({mapping.conductorTaskRefName})</span>
                          </td>
                          <td style={{ padding: '6px 10px', color: '#1e40af', fontWeight: 500 }}>{mapping.canvasNodeType}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                              background: confidenceColor(mapping.confidence) + '15',
                              color: confidenceColor(mapping.confidence),
                            }}>{mapping.confidence}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Warnings */}
            {report.warnings && report.warnings.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>
                  Warnings ({report.warnings.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {report.warnings.map((w, i) => (
                    <div key={i} style={{
                      padding: '8px 12px', borderRadius: 6, fontSize: 12,
                      background: severityColor(w.severity) + '08',
                      border: `1px solid ${severityColor(w.severity)}30`,
                      color: '#334155',
                    }}>
                      <span style={{ fontWeight: 600, color: severityColor(w.severity) }}>[{w.severity}]</span>{' '}
                      {w.message}
                      {w.suggestion && (
                        <div style={{ marginTop: 4, fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
                          Suggestion: {w.suggestion}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Governance */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              {report.suggestedDataClassification && (
                <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#dbeafe', color: '#1e40af' }}>
                  {report.suggestedDataClassification}
                </span>
              )}
              {report.suggestedRiskTier && (
                <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
                  Risk: {report.suggestedRiskTier}
                </span>
              )}
            </div>
          </>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px', borderRadius: 8, border: '1px solid #d1d5db',
              background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          {report.status !== 'FAILED' && (
            <button
              onClick={onCommit}
              disabled={loading}
              style={{
                padding: '10px 24px', borderRadius: 8, border: 'none',
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                color: '#fff', fontSize: 13, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
              }}
            >
              {loading ? 'Converting…' : 'Convert & Open in Canvas'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   ReconciliationConfirmModal — task #64
   ──────────────────────────────────────────────────────────────────────────
   When a discovery scan returns rows that disappeared at the source, the
   backend NEVER mutates them automatically (see OrphanReconciler). This
   modal surfaces the diff and asks the operator to explicitly confirm
   tombstoning. Two cohorts are rendered with different copy:

     • Converted-to-Native — the Canvas workflow already exists and is
       graduated. Tombstoning preserves it forever; the discovered_workflow
       row is just marked ORPHANED as audit. Never auto-purges.
     • Non-converted — no Canvas copy exists. Tombstoning starts a 7-day
       grace clock; the auto-purge job sweeps afterward unless the user
       clicks Restore in the meantime (or the source workflow comes back
       and gets auto-restored on the next scan).

   Cancel leaves the rows untouched — they remain LIVE in the DB and will
   re-surface in the modal on the next scan.
   ────────────────────────────────────────────────────────────────────── */
const ReconciliationConfirmModal = ({ reconciliation, connectorName, onConfirm, onCancel, loading }) => {
  if (!reconciliation || !reconciliation.candidates) return null;
  const { candidates, totalCandidates, convertedCount, nonConvertedCount } = reconciliation;
  const converted = candidates.filter(c => c.convertedToNative);
  const nonConverted = candidates.filter(c => !c.convertedToNative);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={loading ? undefined : onCancel}>
      <div style={{
        background: '#fff', borderRadius: 16, maxWidth: 720, width: '100%',
        maxHeight: '85vh', overflow: 'auto', padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ marginBottom: 20, borderBottom: '1px solid #e5e7eb', paddingBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <AlertTriangle size={22} color={ACCENT.amber.icon} />
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: S[800] }}>
              Reconcile discovered workflows
            </h2>
          </div>
          <p style={{ margin: '6px 0 0', color: S[600], fontSize: 13.5, lineHeight: 1.5 }}>
            <strong>{totalCandidates}</strong> workflow{totalCandidates === 1 ? '' : 's'}{' '}
            previously discovered from <strong>{connectorName || 'this connector'}</strong>{' '}
            no longer exist at the source. Choose what happens next:
          </p>
        </div>

        {/* What confirm does — non-collapsing explanation block */}
        <div style={{
          background: ACCENT.sky.bg, border: `1px solid ${ACCENT.sky.border}`,
          borderRadius: 8, padding: 14, marginBottom: 18,
          color: ACCENT.sky.text, fontSize: 12.5, lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>What happens when you confirm</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Each row is marked <code>ORPHANED</code> with a tombstone timestamp; the row is preserved in the database.</li>
            <li><strong>Native-converted</strong> rows ({convertedCount}): the Canvas workflow stays untouched. The discovered row remains as a permanent audit trail.</li>
            <li><strong>Non-converted</strong> rows ({nonConvertedCount}): a 7-day grace clock starts. The row is hard-deleted automatically afterward, cascading conversion reports + governance bindings.</li>
            <li>If the source workflow reappears in a future scan, the row is auto-restored — no further action needed.</li>
            <li>You can click <strong>Restore</strong> on any tombstoned row during the grace period to undo this.</li>
          </ul>
        </div>

        {/* Converted cohort */}
        {converted.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: S[700], marginBottom: 8,
                          display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={15} color={ACCENT.emerald.icon} />
              Native-preserved ({converted.length}) — Canvas workflow stays
            </div>
            <div style={{ borderRadius: 8, border: `1px solid ${S[200]}`, overflow: 'hidden' }}>
              {converted.map(c => (
                <div key={c.id} style={{
                  padding: '10px 12px', borderBottom: `1px solid ${S[150]}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  fontSize: 12.5,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: S[800], fontWeight: 500, overflow: 'hidden',
                                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.workflowType}
                    </div>
                    {c.lastSeenAt && (
                      <div style={{ color: S[500], fontSize: 11, marginTop: 2 }}>
                        Last seen at source: {new Date(c.lastSeenAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <span style={{
                    fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
                    background: ACCENT.emerald.bg, color: ACCENT.emerald.text,
                    border: `1px solid ${ACCENT.emerald.border}`,
                  }}>NATIVE</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Non-converted cohort */}
        {nonConverted.length > 0 && (
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: S[700], marginBottom: 8,
                          display: 'flex', alignItems: 'center', gap: 8 }}>
              <Trash2 size={15} color={ACCENT.amber.icon} />
              Will hard-delete after 7 days ({nonConverted.length}) — no Canvas conversion
            </div>
            <div style={{ borderRadius: 8, border: `1px solid ${ACCENT.amber.border}`, overflow: 'hidden' }}>
              {nonConverted.map(c => (
                <div key={c.id} style={{
                  padding: '10px 12px', borderBottom: `1px solid ${S[150]}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  fontSize: 12.5,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: S[800], fontWeight: 500, overflow: 'hidden',
                                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.workflowType}
                    </div>
                    <div style={{ color: S[500], fontSize: 11, marginTop: 2 }}>
                      Status: {c.status} {c.lastSeenAt && (
                        <>· Last seen: {new Date(c.lastSeenAt).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
                    background: ACCENT.amber.bg, color: ACCENT.amber.text,
                    border: `1px solid ${ACCENT.amber.border}`,
                  }}>GRACE: 7d</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10,
                      paddingTop: 16, borderTop: `1px solid ${S[200]}` }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: '#fff', color: S[700], border: `1px solid ${S[300]}`,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >Cancel — keep all rows</button>
          <button
            onClick={() => onConfirm(candidates.map(c => c.id))}
            disabled={loading}
            style={{
              padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: BRAND.primary, color: '#fff', border: 'none',
              cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
            }}
          >{loading ? 'Reconciling…' : `Confirm — tombstone ${totalCandidates}`}</button>
        </div>
      </div>
    </div>
  );
};

const ExecutionInputModal = ({ workflow, onExecute, onClose }) => {
  const [inputRows, setInputRows] = useState(() => {
    // Pre-populate from inputSchema if available
    const schema = workflow?.inputSchema;
    if (schema && typeof schema === 'object') {
      // Handle array format: ["firstName", "lastName"] (raw Conductor inputParameters)
      if (Array.isArray(schema)) {
        const names = schema.filter(v => typeof v === 'string' && v.trim());
        if (names.length > 0) {
          return names.map(name => ({
            key: name,
            value: '',
            required: true,
            type: 'string',
          }));
        }
      }
      // Handle JSON Schema format: { type: "object", properties: {...}, required: [...] }
      if (schema.properties && typeof schema.properties === 'object') {
        const required = new Set(schema.required || []);
        const keys = Object.keys(schema.properties);
        if (keys.length > 0) {
          return keys.map(k => ({
            key: k,
            value: schema.properties[k]?.default != null ? String(schema.properties[k].default) : '',
            required: required.has(k),
            type: schema.properties[k]?.type || 'string',
          }));
        }
      }
      // Handle flat object format: { "firstName": "...", "lastName": "..." }
      const keys = Object.keys(schema).filter(k => !['required', 'type', 'properties', '$schema'].includes(k));
      if (keys.length > 0) {
        return keys.map(k => ({
          key: k,
          value: '',
          required: false,
          type: 'string',
        }));
      }
    }
    // Default: one empty row
    return [{ key: '', value: '', required: false, type: 'string' }];
  });
  const [executing, setExecuting] = useState(false);

  const addRow = () => setInputRows(prev => [...prev, { key: '', value: '', required: false, type: 'string' }]);
  const removeRow = (idx) => setInputRows(prev => prev.filter((_, i) => i !== idx));
  const updateRow = (idx, field, val) => setInputRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));

  const handleExecute = async () => {
    const input = {};
    inputRows.forEach(r => {
      if (r.key.trim()) {
        // Auto-cast numbers and booleans
        let val = r.value;
        if (r.type === 'integer' || r.type === 'number') val = Number(val) || 0;
        else if (r.type === 'boolean') val = val === 'true';
        else if (val === 'true') val = true;
        else if (val === 'false') val = false;
        else if (/^\d+$/.test(val) && val.length < 16) val = Number(val);
        input[r.key.trim()] = val;
      }
    });
    setExecuting(true);
    try {
      await onExecute(input);
    } finally {
      setExecuting(false);
    }
  };

  const hasRequired = inputRows.some(r => r.required);
  const requiredMissing = inputRows.some(r => r.required && !r.value.trim());

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.45)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'wdpFadeIn 0.15s ease',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          animation: 'wdpSlideUp 0.2s ease', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${S[150]}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: S[900] }}>
              {workflow?.executionMode === 'canvas' ? 'Execute via Canvas' : 'Execute with GEL'}
            </div>
            <div style={{ fontSize: 12, color: S[500], marginTop: 2, fontFamily: "'IBM Plex Mono', monospace" }}>
              {workflow?.name || workflow?.workflowType || 'Unknown'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={18} color={S[400]} />
          </button>
        </div>

        {/* Input Fields */}
        <div style={{ padding: '20px 24px', maxHeight: 400, overflowY: 'auto' }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 12,
          }}>
            Workflow Input Parameters
          </div>

          {hasRequired && (
            <div style={{
              fontSize: 11, color: ACCENT.amber.text, background: ACCENT.amber.bg,
              border: `1px solid ${ACCENT.amber.border}`, borderRadius: 6,
              padding: '6px 10px', marginBottom: 12,
            }}>
              Fields marked with * are required by the workflow schema
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {inputRows.map((row, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Key"
                    value={row.key}
                    onChange={e => updateRow(idx, 'key', e.target.value)}
                    readOnly={row.required}
                    style={{
                      width: '100%', padding: '8px 10px', fontSize: 12,
                      border: `1px solid ${row.required && !row.value.trim() ? '#fca5a5' : S[200]}`,
                      borderRadius: 6, background: row.required ? S[50] : '#fff',
                      color: S[800], fontFamily: "'IBM Plex Mono', monospace",
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                  {row.required && (
                    <span style={{ position: 'absolute', right: 6, top: 6, color: '#ef4444', fontSize: 14, fontWeight: 700 }}>*</span>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Value"
                  value={row.value}
                  onChange={e => updateRow(idx, 'value', e.target.value)}
                  style={{
                    flex: 1.5, padding: '8px 10px', fontSize: 12,
                    border: `1px solid ${row.required && !row.value.trim() ? '#fca5a5' : S[200]}`,
                    borderRadius: 6, background: '#fff', color: S[800],
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
                {row.type && row.type !== 'string' && (
                  <span style={{
                    fontSize: 9, fontWeight: 600, color: ACCENT.violet.text,
                    background: ACCENT.violet.bg, padding: '2px 6px', borderRadius: 4,
                    whiteSpace: 'nowrap',
                  }}>
                    {row.type}
                  </span>
                )}
                {!row.required && (
                  <button
                    onClick={() => removeRow(idx)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                  >
                    <X size={14} color={S[400]} />
                  </button>
                )}
                {row.required && <div style={{ width: 22 }} />}
              </div>
            ))}
          </div>

          <button
            onClick={addRow}
            style={{
              ...btnBase, marginTop: 10, padding: '6px 12px',
              fontSize: 11, fontWeight: 600, color: BRAND.primary,
              background: BRAND.light, border: `1px solid ${BRAND.soft}`,
              borderRadius: 6, cursor: 'pointer',
            }}
          >
            <Plus size={12} />
            Add Parameter
          </button>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${S[150]}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button
            onClick={onClose}
            style={{
              ...btnBase, padding: '10px 20px', fontSize: 12, fontWeight: 600,
              background: S[100], color: S[600], border: `1px solid ${S[200]}`, borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleExecute}
            disabled={executing || requiredMissing}
            style={{
              ...btnBase, padding: '10px 20px', fontSize: 12, fontWeight: 600,
              background: executing || requiredMissing ? S[200] : 'linear-gradient(135deg, #059669, #047857)',
              color: executing || requiredMissing ? S[400] : '#fff',
              border: 'none', borderRadius: 8,
              cursor: executing || requiredMissing ? 'not-allowed' : 'pointer',
              minWidth: 140,
            }}
          >
            {executing ? (
              <><Loader size={14} style={{ animation: 'wdpSpin 1s linear infinite' }} /> Executing…</>
            ) : workflow?.executionMode === 'canvas' ? (
              <><Network size={14} /> Execute via Canvas</>
            ) : (
              <><Play size={14} /> Execute with GEL</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   AUDIT & ENFORCEMENT TAB — Workflow GEL Dashboard
   ══════════════════════════════════════════════════════════════════════════════ */

const DECISION_COLORS = {
  ALLOW: { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0', icon: CheckCircle },
  BLOCK: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca', icon: ShieldAlert },
  WARN: { bg: '#fffbeb', text: '#d97706', border: '#fde68a', icon: AlertTriangle },
  REQUIRE_APPROVAL: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe', icon: Clock },
};

const PHASE_LABELS = {
  PRE_GATE: 'Pre-Gate',
  DPG_SCAN: 'DPG Scan',
  POLICY_EVAL: 'Policy Eval',
  DECISION: 'Decision',
  EXECUTION: 'Execution',
  AUDIT: 'Audit',
  POST_RECORD: 'Post-Record',
  ENFORCEMENT_CHAIN: 'Full Chain',
};

const PHASE_ORDER = ['PRE_GATE', 'DPG_SCAN', 'POLICY_EVAL', 'DECISION', 'EXECUTION', 'AUDIT', 'POST_RECORD'];

const EnforcementChainTimeline = ({ envelope }) => {
  const detail = envelope.detailPayload || {};
  const isBlocked = envelope.decision === 'BLOCK';
  const phases = PHASE_ORDER.map((phase, i) => {
    const isDecisionPhase = phase === 'DECISION';
    const isExecutionPhase = phase === 'EXECUTION';
    // If decision was BLOCK, execution and post-execution phases are skipped
    const isActive = isBlocked ? (i <= PHASE_ORDER.indexOf('DECISION')) : true;
    return { key: phase, label: PHASE_LABELS[phase], index: i, isActive, isDecisionPhase, isExecutionPhase };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, margin: '8px 0' }}>
      {phases.map((phase, i) => {
        const dc = phase.isDecisionPhase ? DECISION_COLORS[envelope.decision] || DECISION_COLORS.ALLOW : null;
        // Execution phase gets distinct teal/blue styling
        const execStyle = phase.isExecutionPhase ? {
          bg: phase.isActive ? '#f0f9ff' : S[50],
          border: phase.isActive ? '#0ea5e9' : S[300],
          color: phase.isActive ? '#0284c7' : S[400],
        } : null;
        return (
          <React.Fragment key={phase.key}>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 70,
              opacity: phase.isActive ? 1 : 0.4,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: phase.isDecisionPhase ? (dc?.bg || '#ecfdf5')
                  : phase.isExecutionPhase ? execStyle.bg
                  : (phase.isActive ? BRAND.light : S[100]),
                border: `2px solid ${phase.isDecisionPhase ? (dc?.border || '#a7f3d0')
                  : phase.isExecutionPhase ? execStyle.border
                  : (phase.isActive ? BRAND.primary : S[300])}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
                color: phase.isDecisionPhase ? (dc?.text || '#059669')
                  : phase.isExecutionPhase ? execStyle.color
                  : (phase.isActive ? BRAND.primary : S[400]),
              }}>
                {phase.isDecisionPhase ? '\u2713' : phase.isExecutionPhase ? '\u25B6' : (i + 1)}
              </div>
              <span style={{
                fontSize: 9, fontWeight: 600, color: phase.isActive ? S[700] : S[400],
                textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center',
              }}>{phase.label}</span>
            </div>
            {i < phases.length - 1 && (
              <div style={{
                flex: 1, height: 2, background: phase.isActive ? BRAND.primary : S[200],
                minWidth: 12, marginBottom: 16,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const AuditStatCard = ({ label, value, color, icon: Icon, subtitle }) => (
  <div style={{
    flex: 1, minWidth: 140, padding: '16px 18px', borderRadius: 12,
    background: '#fff', border: `1px solid ${S[150]}`,
    display: 'flex', flexDirection: 'column', gap: 4,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      {Icon && <Icon size={14} color={color || S[400]} />}
    </div>
    <span style={{ fontSize: 28, fontWeight: 700, color: color || S[900], lineHeight: 1.1 }}>{value}</span>
    {subtitle && <span style={{ fontSize: 11, color: S[400] }}>{subtitle}</span>}
  </div>
);

const ChainIntegrityBadge = ({ valid, chainLength, brokenLinks }) => {
  if (chainLength === 0) return (
    <span style={{ fontSize: 11, color: S[400], fontStyle: 'italic' }}>No envelopes</span>
  );
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px',
      borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: valid ? '#ecfdf5' : '#fef2f2',
      color: valid ? '#059669' : '#dc2626',
      border: `1px solid ${valid ? '#a7f3d0' : '#fecaca'}`,
    }}>
      {valid ? <Link2 size={12} /> : <AlertCircle size={12} />}
      {valid ? `Verified · ${chainLength} records` : `Broken · ${(brokenLinks || []).length} link(s)`}
    </div>
  );
};

const AuditEnforcementTab = ({ loading, dashboard, onRefresh, onRepairAll, onRepairChain }) => {
  const [expandedBinding, setExpandedBinding] = useState(null);
  const [expandedEnvelope, setExpandedEnvelope] = useState(null);
  const [expandedWorkflow, setExpandedWorkflow] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);
  const [expandedRunEnvelope, setExpandedRunEnvelope] = useState(null);
  const [filterDecision, setFilterDecision] = useState('ALL');
  const [repairing, setRepairing] = useState(false);
  const [repairingBinding, setRepairingBinding] = useState(null);

  if (loading && !dashboard) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
      <Loader size={20} style={{ animation: 'wdpSpin 1s linear infinite' }} color={S[400]} />
      <span style={{ marginLeft: 10, color: S[500], fontSize: 13 }}>Loading audit data…</span>
    </div>
  );

  if (!dashboard) return (
    <div style={{
      textAlign: 'center', padding: '48px 24px', color: S[500],
    }}>
      <FileText size={40} color={S[300]} style={{ marginBottom: 12 }} />
      <div style={{ fontSize: 15, fontWeight: 600, color: S[700], marginBottom: 6 }}>No Audit Data Yet</div>
      <div style={{ fontSize: 13, maxWidth: 400, margin: '0 auto' }}>
        Execute GEL-protected workflows to generate enforcement audit records.
        Each execution creates a tamper-evident, hash-chained audit envelope.
      </div>
    </div>
  );

  const { totalEnvelopes = 0, totalBindings = 0, decisionDistribution = {},
    connectorDistribution = {}, bindingSummaries = [], recentEnvelopes = [],
    blockedCount = 0, allowedCount = 0, warnCount = 0 } = dashboard;

  const blockRate = totalEnvelopes > 0 ? ((blockedCount / totalEnvelopes) * 100).toFixed(1) : '0.0';

  const filteredEnvelopes = filterDecision === 'ALL'
    ? recentEnvelopes
    : recentEnvelopes.filter(e => e.decision === filterDecision);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ─── Summary Stats Row ─── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <AuditStatCard label="Total Envelopes" value={totalEnvelopes} icon={FileText} color={BRAND.primary}
          subtitle="Hash-chained audit records" />
        <AuditStatCard label="Allowed" value={allowedCount} icon={CheckCircle} color="#059669"
          subtitle={`${totalEnvelopes > 0 ? ((allowedCount / totalEnvelopes) * 100).toFixed(0) : 0}% of executions`} />
        <AuditStatCard label="Blocked" value={blockedCount} icon={ShieldAlert} color="#dc2626"
          subtitle={`${blockRate}% block rate`} />
        <AuditStatCard label="Warnings" value={warnCount} icon={AlertTriangle} color="#d97706"
          subtitle="Monitored, not blocked" />
        <AuditStatCard label="Bindings" value={totalBindings} icon={ShieldCheck} color="#7c3aed"
          subtitle="Active protection bindings" />
      </div>

      {/* ─── Connector & Decision Breakdown ─── */}
      <div style={{ display: 'flex', gap: 12 }}>
        {/* Decision distribution */}
        <div style={{
          flex: 1, padding: '16px 20px', borderRadius: 12, background: '#fff',
          border: `1px solid ${S[150]}`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: S[700], marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Decision Distribution
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries(decisionDistribution).map(([decision, count]) => {
              const dc = DECISION_COLORS[decision] || { bg: S[100], text: S[600], border: S[200] };
              return (
                <div key={decision} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                  borderRadius: 10, background: dc.bg, border: `1px solid ${dc.border}`,
                }}>
                  <span style={{ fontSize: 20, fontWeight: 700, color: dc.text }}>{count}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: dc.text }}>{decision}</span>
                </div>
              );
            })}
            {Object.keys(decisionDistribution).length === 0 && (
              <span style={{ fontSize: 12, color: S[400], fontStyle: 'italic' }}>No decisions recorded yet</span>
            )}
          </div>
        </div>

        {/* Connector distribution */}
        <div style={{
          flex: 1, padding: '16px 20px', borderRadius: 12, background: '#fff',
          border: `1px solid ${S[150]}`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: S[700], marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            By Connector Type
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries(connectorDistribution).map(([type, count]) => (
              <div key={type} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                borderRadius: 10, background: '#f5f3ff', border: '1px solid #ddd6fe',
              }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed' }}>{count}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed' }}>{type}</span>
              </div>
            ))}
            {Object.keys(connectorDistribution).length === 0 && (
              <span style={{ fontSize: 12, color: S[400], fontStyle: 'italic' }}>No connector data yet</span>
            )}
          </div>
        </div>
      </div>

      {/* ─── Per-Binding Hash Chain Integrity ─── */}
      <div style={{
        padding: '16px 20px', borderRadius: 12, background: '#fff',
        border: `1px solid ${S[150]}`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Hash size={14} color={BRAND.primary} />
            <span style={{ fontSize: 13, fontWeight: 700, color: S[800] }}>Audit Hash Chain Integrity</span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onRefresh} style={{
              ...btnBase, padding: '5px 12px', background: S[50], border: `1px solid ${S[200]}`,
              fontSize: 11, fontWeight: 600, color: S[600], borderRadius: 8, cursor: 'pointer',
            }}>
              <RefreshCw size={12} /> Refresh
            </button>
            <button
              onClick={async () => {
                setRepairing(true);
                try { await onRepairAll(); } finally { setRepairing(false); }
              }}
              disabled={repairing}
              style={{
                ...btnBase, padding: '5px 12px',
                background: repairing ? S[100] : '#ecfdf5',
                border: `1px solid ${repairing ? S[200] : '#a7f3d0'}`,
                fontSize: 11, fontWeight: 600,
                color: repairing ? S[400] : '#059669',
                borderRadius: 8, cursor: repairing ? 'not-allowed' : 'pointer',
              }}
            >
              {repairing ? <><Loader size={12} style={{ animation: 'wdpSpin 1s linear infinite' }} /> Repairing…</> : <><ShieldCheck size={12} /> Verify &amp; Repair All</>}
            </button>
          </div>
        </div>

        {bindingSummaries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: S[400], fontSize: 13 }}>
            No protection bindings with audit records
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bindingSummaries.map(binding => {
              const isExpanded = expandedBinding === binding.bindingId;
              const stats = binding.stats || {};
              return (
                <div key={binding.bindingId} style={{
                  border: `1px solid ${isExpanded ? BRAND.primary : S[150]}`,
                  borderRadius: 10, overflow: 'hidden',
                  transition: 'border-color 0.15s ease',
                }}>
                  {/* Binding header */}
                  <div
                    onClick={() => setExpandedBinding(isExpanded ? null : binding.bindingId)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 16px', cursor: 'pointer', background: isExpanded ? BRAND.glow : '#fff',
                      transition: 'background 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                      <ShieldCheck size={16} color={binding.chainValid ? '#059669' : '#dc2626'} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: S[900] }}>
                          {binding.workflowType || binding.bindingId?.slice(0, 8)}
                        </div>
                        <div style={{ fontSize: 11, color: S[500] }}>
                          {binding.connectorType} · {binding.enforcementMode || 'ENFORCE'} · {binding.dataClassification || 'INTERNAL'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <ChainIntegrityBadge
                        valid={binding.chainValid}
                        chainLength={binding.chainLength}
                        brokenLinks={binding.brokenLinks}
                      />
                      <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                        <span style={{ color: '#059669', fontWeight: 600 }}>{stats.allowedCount || 0} ✓</span>
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>{stats.blockedCount || 0} ✕</span>
                        <span style={{ color: '#d97706', fontWeight: 600 }}>{stats.warnCount || 0} ⚠</span>
                      </div>
                      {isExpanded ? <ChevronUp size={16} color={S[400]} /> : <ChevronDown size={16} color={S[400]} />}
                    </div>
                  </div>

                  {/* Expanded: enforcement stats + chain detail */}
                  {isExpanded && (
                    <div style={{ padding: '14px 16px', borderTop: `1px solid ${S[150]}`, background: '#fafafa' }}>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                        <div style={{
                          padding: '10px 14px', borderRadius: 8, background: '#fff',
                          border: `1px solid ${S[150]}`, flex: '1 1 120px',
                        }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: S[400], textTransform: 'uppercase' }}>Total Records</div>
                          <div style={{ fontSize: 22, fontWeight: 700, color: BRAND.primary }}>{stats.totalEnvelopes || 0}</div>
                        </div>
                        <div style={{
                          padding: '10px 14px', borderRadius: 8, background: '#fff',
                          border: `1px solid ${S[150]}`, flex: '1 1 120px',
                        }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: S[400], textTransform: 'uppercase' }}>Autonomy Level</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#7c3aed' }}>{binding.autonomyLevel || 'N/A'}</div>
                        </div>
                        <div style={{
                          padding: '10px 14px', borderRadius: 8, background: '#fff',
                          border: `1px solid ${S[150]}`, flex: '1 1 120px',
                        }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: S[400], textTransform: 'uppercase' }}>Activated</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: S[700] }}>
                            {binding.activatedAt ? new Date(binding.activatedAt).toLocaleDateString() : 'Pending'}
                          </div>
                        </div>
                        <div style={{
                          padding: '10px 14px', borderRadius: 8, background: '#fff',
                          border: `1px solid ${S[150]}`, flex: '1 1 120px',
                        }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: S[400], textTransform: 'uppercase' }}>Chain Hash</div>
                          {binding.brokenLinks?.length === 0 && binding.chainLength > 0 ? (
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#059669' }}>✓ Valid</div>
                          ) : binding.chainLength === 0 ? (
                            <div style={{ fontSize: 11, fontWeight: 600, color: S[400] }}>—</div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: '#dc2626' }}>✕ Broken</span>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setRepairingBinding(binding.bindingId);
                                  try { await onRepairChain(binding.bindingId); } finally { setRepairingBinding(null); }
                                }}
                                disabled={repairingBinding === binding.bindingId}
                                style={{
                                  ...btnBase, padding: '2px 8px', fontSize: 9, fontWeight: 600,
                                  background: repairingBinding === binding.bindingId ? S[100] : '#ecfdf5',
                                  color: repairingBinding === binding.bindingId ? S[400] : '#059669',
                                  border: `1px solid ${repairingBinding === binding.bindingId ? S[200] : '#a7f3d0'}`,
                                  borderRadius: 6, cursor: repairingBinding === binding.bindingId ? 'not-allowed' : 'pointer',
                                }}
                              >
                                {repairingBinding === binding.bindingId ? 'Repairing…' : 'Repair'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Decision distribution bar */}
                      {stats.decisionDistribution && Object.keys(stats.decisionDistribution).length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: S[400], marginBottom: 4, textTransform: 'uppercase' }}>Decision Breakdown</div>
                          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', height: 8, background: S[100] }}>
                            {Object.entries(stats.decisionDistribution).map(([d, c]) => {
                              const dc = DECISION_COLORS[d];
                              const pct = stats.totalEnvelopes > 0 ? (c / stats.totalEnvelopes) * 100 : 0;
                              return pct > 0 ? (
                                <div key={d} title={`${d}: ${c}`} style={{
                                  width: `${pct}%`, background: dc?.text || S[400], height: '100%',
                                }} />
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Enforcement Records by Workflow ─── */}
      <div style={{
        padding: '16px 20px', borderRadius: 12, background: '#fff',
        border: `1px solid ${S[150]}`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Activity size={14} color={BRAND.primary} />
            <span style={{ fontSize: 13, fontWeight: 700, color: S[800] }}>Enforcement Records by Workflow</span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {['ALL', 'ALLOW', 'BLOCK', 'WARN'].map(d => (
              <button
                key={d}
                onClick={() => setFilterDecision(d)}
                style={{
                  ...btnBase, padding: '4px 10px', fontSize: 10, fontWeight: 600, borderRadius: 6,
                  background: filterDecision === d ? (d === 'ALL' ? S[800] : (DECISION_COLORS[d]?.bg || S[100])) : S[50],
                  color: filterDecision === d ? (d === 'ALL' ? '#fff' : (DECISION_COLORS[d]?.text || S[600])) : S[500],
                  border: `1px solid ${filterDecision === d ? (d === 'ALL' ? S[800] : (DECISION_COLORS[d]?.border || S[200])) : S[200]}`,
                  cursor: 'pointer',
                }}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {(() => {
          const wfExecs = dashboard.workflowExecutions || [];
          if (wfExecs.length === 0 && totalEnvelopes === 0) {
            return (
              <div style={{ textAlign: 'center', padding: 24, color: S[400], fontSize: 13 }}>
                No enforcement records yet — execute a GEL-protected workflow to generate records
              </div>
            );
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {wfExecs.map((wf) => {
                const isWfExpanded = expandedWorkflow === wf.workflowId;
                // Filter runs by decision if filter active
                const filteredRuns = filterDecision === 'ALL'
                  ? (wf.runs || [])
                  : (wf.runs || []).filter(r => r.decision === filterDecision);

                if (filteredRuns.length === 0 && filterDecision !== 'ALL') return null;

                return (
                  <div key={wf.workflowId} style={{
                    border: `1px solid ${isWfExpanded ? BRAND.primary : S[150]}`,
                    borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.15s',
                  }}>
                    {/* Workflow header */}
                    <div
                      onClick={() => setExpandedWorkflow(isWfExpanded ? null : wf.workflowId)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                        cursor: 'pointer', background: isWfExpanded ? BRAND.glow : '#fff',
                      }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        background: BRAND.light, border: `1.5px solid ${BRAND.soft}`,
                      }}>
                        <Workflow size={16} color={BRAND.primary} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: S[900] }}>{wf.workflowName || wf.workflowId}</div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                          {wf.connectorType && (
                            <span style={{
                              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                              background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe',
                            }}>{wf.connectorType}</span>
                          )}
                          <span style={{
                            fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 10,
                            background: S[100], color: S[600],
                          }}>{wf.totalRuns} run{wf.totalRuns !== 1 ? 's' : ''}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 10,
                            background: S[100], color: S[600],
                          }}>{wf.totalEnvelopes} envelope{wf.totalEnvelopes !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      {isWfExpanded ? <ChevronUp size={16} color={S[400]} /> : <ChevronDown size={16} color={S[400]} />}
                    </div>

                    {/* Expanded: individual runs */}
                    {isWfExpanded && (
                      <div style={{ borderTop: `1px solid ${S[150]}`, background: '#fafafa' }}>
                        {filteredRuns.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: 16, color: S[400], fontSize: 12 }}>
                            No runs match the selected filter
                          </div>
                        ) : filteredRuns.map((run, runIdx) => {
                          const runDc = DECISION_COLORS[run.decision] || DECISION_COLORS.ALLOW;
                          const RunDecIcon = runDc.icon;
                          const isRunExpanded = expandedRun === `${wf.workflowId}-run-${runIdx}`;
                          const runEnvelopes = run.envelopes || [];
                          const isInlineRun = run.runId?.startsWith('run-');

                          return (
                            <div key={run.runId || runIdx} style={{
                              borderBottom: runIdx < filteredRuns.length - 1 ? `1px solid ${S[100]}` : 'none',
                            }}>
                              {/* Run header */}
                              <div
                                onClick={() => setExpandedRun(isRunExpanded ? null : `${wf.workflowId}-run-${runIdx}`)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px 10px 28px',
                                  cursor: 'pointer', background: isRunExpanded ? runDc.bg : 'transparent',
                                  transition: 'background 0.15s',
                                }}
                              >
                                <div style={{
                                  width: 26, height: 26, borderRadius: '50%', display: 'flex',
                                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                  background: runDc.bg, border: `1.5px solid ${runDc.border}`,
                                }}>
                                  <RunDecIcon size={12} color={runDc.text} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: S[800] }}>
                                      {isInlineRun ? `Execution #${runIdx + 1}` : `Run ${run.runId?.slice(0, 12)}…`}
                                    </span>
                                    <span style={{
                                      fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                                      background: runDc.bg, color: runDc.text, border: `1px solid ${runDc.border}`,
                                    }}>{run.decision}</span>
                                    <span style={{
                                      fontSize: 9, fontWeight: 500, padding: '2px 7px', borderRadius: 10,
                                      background: S[100], color: S[500],
                                    }}>{run.envelopeCount} envelope{run.envelopeCount !== 1 ? 's' : ''}</span>
                                  </div>
                                  <div style={{ fontSize: 10, color: S[400], marginTop: 2 }}>
                                    {run.timestamp ? new Date(run.timestamp).toLocaleString() : '—'}
                                  </div>
                                </div>
                                {isRunExpanded ? <ChevronUp size={14} color={S[400]} /> : <ChevronDown size={14} color={S[400]} />}
                              </div>

                              {/* Expanded run: show all envelopes for this run */}
                              {isRunExpanded && (
                                <div style={{ padding: '8px 16px 12px 44px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {runEnvelopes.map((env, envIdx) => {
                                    const envDc = DECISION_COLORS[env.decision] || DECISION_COLORS.ALLOW;
                                    const detail = env.detailPayload || {};
                                    const isEnvExpanded = expandedRunEnvelope === `env-${env.id || envIdx}`;
                                    return (
                                      <div key={env.id || envIdx} style={{
                                        border: `1px solid ${isEnvExpanded ? envDc.border : S[150]}`,
                                        borderRadius: 8, overflow: 'hidden', background: '#fff',
                                      }}>
                                        <div
                                          onClick={(e) => { e.stopPropagation(); setExpandedRunEnvelope(isEnvExpanded ? null : `env-${env.id || envIdx}`); }}
                                          style={{
                                            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                                            cursor: 'pointer', background: isEnvExpanded ? envDc.bg : '#fff',
                                          }}
                                        >
                                          <span style={{
                                            fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 6,
                                            background: envDc.bg, color: envDc.text, border: `1px solid ${envDc.border}`,
                                          }}>{env.decision}</span>
                                          <span style={{ fontSize: 11, color: S[700], flex: 1 }}>
                                            {PHASE_LABELS[env.enforcementPhase] || env.enforcementPhase}
                                            <span style={{ color: S[400] }}> · Seq #{env.sequenceNumber}</span>
                                          </span>
                                          <span style={{ fontSize: 9, fontFamily: 'monospace', color: S[400] }}>
                                            {env.envelopeHash?.slice(0, 10)}…
                                          </span>
                                          {isEnvExpanded ? <ChevronUp size={12} color={S[400]} /> : <ChevronDown size={12} color={S[400]} />}
                                        </div>

                                        {isEnvExpanded && (
                                          <div style={{ padding: '10px 12px', borderTop: `1px solid ${S[150]}`, background: '#fafafa' }}>
                                            {/* 6-phase timeline */}
                                            <div style={{ marginBottom: 10 }}>
                                              <div style={{ fontSize: 9, fontWeight: 600, color: S[500], marginBottom: 4, textTransform: 'uppercase' }}>
                                                Enforcement Chain Timeline
                                              </div>
                                              <EnforcementChainTimeline envelope={env} />
                                            </div>

                                            {/* Hash chain */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                                              <div style={{ padding: '6px 10px', borderRadius: 6, background: '#fff', border: `1px solid ${S[150]}` }}>
                                                <div style={{ fontSize: 8, fontWeight: 600, color: S[400], textTransform: 'uppercase', marginBottom: 2 }}>Current Hash</div>
                                                <div style={{ fontSize: 9, fontFamily: 'monospace', color: S[700], wordBreak: 'break-all' }}>{env.envelopeHash || '—'}</div>
                                              </div>
                                              <div style={{ padding: '6px 10px', borderRadius: 6, background: '#fff', border: `1px solid ${S[150]}` }}>
                                                <div style={{ fontSize: 8, fontWeight: 600, color: S[400], textTransform: 'uppercase', marginBottom: 2 }}>Previous Hash</div>
                                                <div style={{ fontSize: 9, fontFamily: 'monospace', color: S[700], wordBreak: 'break-all' }}>{env.previousHash || '— (genesis)'}</div>
                                              </div>
                                            </div>

                                            {/* Detail payload */}
                                            {detail && Object.keys(detail).length > 0 && (
                                              <div style={{ padding: '8px 10px', borderRadius: 6, background: '#fff', border: `1px solid ${S[150]}` }}>
                                                <div style={{ fontSize: 8, fontWeight: 600, color: S[400], textTransform: 'uppercase', marginBottom: 4 }}>
                                                  Enforcement Detail
                                                </div>
                                                {detail.reason && (
                                                  <div style={{ fontSize: 11, color: S[700], marginBottom: 2 }}>
                                                    <strong style={{ color: S[500] }}>Reason:</strong> {detail.reason}
                                                  </div>
                                                )}
                                                {detail.enforcementMode && (
                                                  <div style={{ fontSize: 11, color: S[700], marginBottom: 2 }}>
                                                    <strong style={{ color: S[500] }}>Mode:</strong> {detail.enforcementMode}
                                                  </div>
                                                )}
                                                {detail.dpgScan && (
                                                  <div style={{ fontSize: 11, color: S[700], marginBottom: 2 }}>
                                                    <strong style={{ color: S[500] }}>DPG Scan:</strong> {typeof detail.dpgScan === 'object' ? JSON.stringify(detail.dpgScan) : detail.dpgScan}
                                                  </div>
                                                )}
                                                {detail.dataClassification && (
                                                  <div style={{ fontSize: 11, color: S[700] }}>
                                                    <strong style={{ color: S[500] }}>Classification:</strong> {detail.dataClassification}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Fallback: show flat list for envelopes without workflow grouping */}
              {wfExecs.length === 0 && recentEnvelopes.length > 0 && (
                <div style={{ padding: 12, color: S[500], fontSize: 12, fontStyle: 'italic' }}>
                  {recentEnvelopes.length} envelope(s) without workflow grouping — run executions to generate grouped records
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   PAGE ROOT
   ══════════════════════════════════════════════════════════════════════════════ */

export default function WorkflowDiscoveryPage() {
  const { addToast } = useToast();
  const { clearContext } = useNavigation();
  // #72 — Hydrate activeTab from the URL query (?tab=<key>) so deep
  // links (including legacy ones from before the IA collapse) land on
  // the right place. Legacy keys redirect via LEGACY_TAB_REDIRECTS.
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('tab');
    if (!requested) return 'overview';
    if (LEGACY_TAB_REDIRECTS[requested]) return LEGACY_TAB_REDIRECTS[requested];
    if (LEGACY_TAB_KEYS.has(requested)) return requested; // explicit legacy access during migration
    if (TABS.some(t => t.key === requested)) return requested;
    return 'overview';
  });
  const [loading, setLoading] = useState(true);
  const [discoveryData, setDiscoveryData] = useState(null);
  const [connectionsData, setConnectionsData] = useState([]);
  const [workflowsData, setWorkflowsData] = useState([]);
  const [gelRegistrationData, setGelRegistrationData] = useState(null);
  const [bindingsData, setBindingsData] = useState([]);
  const [auditDashboard, setAuditDashboard] = useState(null);
  // #76 — when set, the Visual Mapper tab will auto-select this workflow
  // on its next mount (or when the prop changes while it's already
  // mounted) and clear the value. Lets "Run Natively" from a workflow
  // row deep-link into the mapper without piping state through context.
  const [autoSelectMapperWorkflowId, setAutoSelectMapperWorkflowId] = useState(null);

  useEffect(() => {
    clearContext();
  }, [clearContext]);

  const loadDiscoveryDashboard = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch summary + recent discoveries + active pipelines in parallel
      const [summaryRes, gelSummaryRes] = await Promise.all([
        api.discoveryGetWorkflowSummary(),
        api.discoveryGelGetSummary(),
      ]);
      const summary = summaryRes?.data || summaryRes || {};
      const gelSummary = gelSummaryRes?.data || gelSummaryRes || {};
      setDiscoveryData({
        metrics: {
          totalConnections: summary.totalConnections || 0,
          totalWorkflows: summary.totalWorkflows || 0,
          protectedWorkflows: summary.protectedWorkflows || 0,
          unprotectedWorkflows: summary.unprotectedWorkflows || 0,
        },
        recentDiscoveries: (summary.recentDiscoveries || []).map(w => {
          const cleanStr = (val) => (val && val !== 'null' && val !== 'undefined') ? val : null;
          return {
            id: w.id,
            name: w.workflowType || w.name,
            connectorType: cleanStr(w.connectorType),
            namespace: cleanStr(w.temporalNamespace) || cleanStr(w.namespace),
            taskQueue: cleanStr(w.taskQueue),
            riskTier: w.riskTier || 'UNKNOWN',
            discoveredDate: w.firstSeenAt ? new Date(w.firstSeenAt).toLocaleString() : 'Unknown',
          };
        }),
        activePipelines: (gelSummary.activePipelines || gelSummary.pipelines || []).map(p => ({
          id: p.id,
          source: p.sourceName || p.source,
          sourceType: p.sourceType || 'WORKFLOW',
          currentStep: p.pipelineStatus || p.currentStep,
          status: p.pipelineStatus || p.status,
        })),
      });
    } catch (err) {
      addToast('Failed to load discovery dashboard: ' + (err.message || err), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.discoveryListConnectorConfigs();
      const configs = res?.data || res || [];
      setConnectionsData(configs.map(c => ({
        id: c.id,
        name: c.name,
        connectorType: c.connectorType,
        host: c.host,
        port: c.port,
        namespace: c.namespace,
        domain: c.connectorConfig?.domain,
        // RC-17 — flag a managed (cloud-hosted) Conductor OSS cluster vs
        // self-hosted. Accepts either the new `managedCluster` config key or
        // the legacy `orkesCluster` key for pre-RC-17 persisted connectors.
        isManagedCluster: c.connectorConfig?.managedCluster
          || c.connectorConfig?.orkesCluster
          || (c.host && c.host.includes('conductor-oss.example.com')),
        supportsProxy: c.connectorType === 'TEMPORAL' || c.connectorType === 'CONDUCTOR',
        status: c.status || 'PENDING',
        workflowsDiscovered: c.totalWorkflowsDiscovered || 0,
        lastConnected: c.lastConnectedAt ? new Date(c.lastConnectedAt).toLocaleString() : 'Never',
        // Preserve raw config for inline editor
        authType: c.authType,
        authConfig: c.authConfig,
        tlsEnabled: c.tlsEnabled,
        scanIntervalMinutes: c.scanIntervalMinutes,
        autoScanEnabled: c.autoScanEnabled,
        connectorConfig: c.connectorConfig,
      })));
    } catch (err) {
      addToast('Failed to load connections: ' + (err.message || err), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.discoveryListWorkflows();
      const workflows = res?.data?.content || res?.data || res || [];
      setWorkflowsData(workflows.map(w => {
        // Safely flatten JsonNode arrays to string arrays (backend sends JSONB which may contain objects)
        const flattenArray = (arr) => {
          if (!arr || !Array.isArray(arr)) return [];
          return arr.map(item => typeof item === 'object' && item !== null ? (item.name || item.type || JSON.stringify(item)) : String(item));
        };
        // Filter out Java "null" string serialization
        const cleanStr = (val) => (val && val !== 'null' && val !== 'undefined') ? val : null;
        return {
          id: w.id,
          name: w.workflowType || w.name,
          connectorType: cleanStr(w.connectorType),
          namespace: cleanStr(w.temporalNamespace) || cleanStr(w.namespace),
          taskQueue: cleanStr(w.taskQueue),
          status: w.status || 'DISCOVERED',
          riskTier: w.riskTier || 'UNKNOWN',
          dataClassification: cleanStr(w.dataClassification) || 'UNKNOWN',
          executionCount: w.executionCount30d || 0,
          avgDuration: w.avgDurationMs ? `${Math.round(w.avgDurationMs / 1000)}s` : 'N/A',
          failureRate: w.failureRate30d != null ? `${(w.failureRate30d * 100).toFixed(1)}%` : 'N/A',
          governanceScore: w.governanceScore || 0,
          activityTypes: flattenArray(w.activityTypes),
          detectedDataFlows: flattenArray(w.detectedDataFlows),
          connectionId: w.connectionId,
          nativeWorkflowId: w.nativeWorkflowId,
          inputSchema: w.inputSchema || null,
          // #68 — surface tombstone metadata so downstream views (Visual
          // Mapper filter, Discovered Workflows badge, grace-period
          // countdown) can read it. The previous projection silently
          // dropped these fields, which is why the Visual Mapper kept
          // showing ORPHANED rows even after the filter was added.
          tombstonedAt: w.tombstonedAt || null,
          previousStatus: w.previousStatus || null,
          tombstoneReason: w.tombstoneReason || null,
        };
      }));
    } catch (err) {
      addToast('Failed to load workflows: ' + (err.message || err), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadGelRegistration = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.discoveryGelGetSummary();
      const data = res?.data || res || {};
      setGelRegistrationData({
        pipelines: (data.activePipelines || data.pipelines || []).map(p => ({
          id: p.id,
          source: p.sourceName || p.source,
          sourceType: p.sourceType || 'WORKFLOW',
          status: p.pipelineStatus || p.status,
          currentStep: p.pipelineStatus || p.currentStep,
          classification: p.classificationData ? (typeof p.classificationData === 'string' ? JSON.parse(p.classificationData) : p.classificationData) : {},
          generatedRulesCount: p.generatedRules ? (Array.isArray(p.generatedRules) ? p.generatedRules.length : 0) : 0,
          customRulesCount: p.customRules ? (Array.isArray(p.customRules) ? p.customRules.length : 0) : 0,
          boundFrameworks: p.boundFrameworks || [],
        })),
        completed: (data.completedPipelines || data.completed || []).map(p => ({
          source: p.sourceName || p.source,
          sourceId: p.sourceId || p.discoveredWorkflowId,
          type: p.sourceType || 'WORKFLOW',
          status: 'ACTIVE',
          activatedDate: p.completedAt ? new Date(p.completedAt).toLocaleString() : 'Unknown',
          timeToActivate: p.timeToActivate || 'N/A',
        })),
      });
    } catch (err) {
      addToast('Failed to load GEL registration: ' + (err.message || err), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadBindings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.discoveryListBindings();
      const bindings = res?.data || res || [];
      setBindingsData(bindings.map(b => ({
        id: b.id,
        discoveredWorkflowId: b.discoveredWorkflowId,
        workflowName: b.workflowName || b.discoveredWorkflowId,
        connectionName: b.connectionName || b.connectionId,
        connectorType: b.connectorType || null,
        enforcementMode: b.enforcementMode || 'MONITOR',
        bindingStatus: b.bindingStatus || 'DRAFT',
        dataClassification: b.dataClassification || 'INTERNAL',
        autonomyLevel: b.autonomyLevel || 'SUPERVISED',
        dpgScanLevel: b.dpgScanLevel || 'STANDARD',
        inputSchema: b.inputSchema || null,
        canvasWorkflowId: b.canvasWorkflowId || null,
        hasEnforcementDag: b.canvasWorkflowId != null && b.canvasWorkflowId !== undefined,
        totalExecutions: b.totalExecutionsMonitored || 0,
        violationsDetected: b.totalViolationsDetected || 0,
        blocks: b.totalBlocks || 0,
        lastEnforcement: b.lastEnforcementAt ? new Date(b.lastEnforcementAt).toLocaleString() : 'Never',
        activatedAt: b.activatedAt ? new Date(b.activatedAt).toLocaleDateString() : null,
      })));
    } catch (err) {
      addToast('Failed to load bindings: ' + (err.message || err), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const loadAuditDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.discoveryGetAuditDashboard();
      setAuditDashboard(res?.data || res || null);
    } catch (err) {
      addToast('Failed to load audit dashboard: ' + (err.message || err), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    // #72 — New IA load triggers. Workflows tab needs the full set
    // (workflows + bindings + GEL state) because the row drawer (#73)
    // pulls govern/audit data on demand without a tab switch.
    if (activeTab === 'overview' || activeTab === 'discovery') {
      loadDiscoveryDashboard();
      loadConnections();
      // #77 — pre-load GEL registration data so the embedded panel
      // hydrates without a tab switch. The data flows into both the
      // active-pipelines list and the completed-registrations table.
      loadGelRegistration();
    } else if (activeTab === 'sources' || activeTab === 'connections') {
      loadConnections();
    } else if (activeTab === 'workflows' || activeTab === 'mapper'
               || activeTab === 'gel-registration' || activeTab === 'bindings') {
      loadWorkflows();
      loadBindings();
      loadGelRegistration();
    } else if (activeTab === 'audit') {
      loadAuditDashboard();
    }
  }, [activeTab, loadDiscoveryDashboard, loadConnections, loadWorkflows, loadGelRegistration, loadBindings, loadAuditDashboard]);

  const handleAddConnection = async (formData) => {
    try {
      // Map frontend display auth type to backend enum values
      const AUTH_TYPE_MAP = {
        'None': 'NONE', 'API Key': 'API_KEY', 'mTLS': 'MTLS', 'OAuth2': 'OAUTH2',
        'Basic': 'BASIC', 'Token': 'TOKEN', 'Token (Conductor OSS)': 'TOKEN', 'IAM Role': 'IAM_ROLE',
      };
      // Normalize host — strip scheme (https://), paths (/api), and trailing slashes
      let normalizedHost = (formData.host || '').trim();
      normalizedHost = normalizedHost.replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/\/+$/, '');

      const payload = {
        connectorType: formData.connectorType,
        name: formData.name,
        host: normalizedHost,
        port: formData.port,
        namespace: formData.namespace || null,
        authType: AUTH_TYPE_MAP[formData.authType] || 'NONE',
        connectorConfig: {},
        authConfig: {},
      };
      // Conductor-specific fields go into connectorConfig JSONB
      if (formData.connectorType === 'CONDUCTOR') {
        if (formData.domain) payload.connectorConfig.domain = formData.domain;
        payload.connectorConfig.apiVersion = formData.apiVersion || 'v3';
        payload.connectorConfig.workflowPrefix = formData.workflowPrefix || '';
        // RC-17 — managed-cluster flag (Conductor OSS managed-cloud variant)
        if (normalizedHost && normalizedHost.includes('conductor-oss.example.com')) {
          payload.connectorConfig.managedCluster = true;
        }
        // Conductor OSS managed clusters don't use namespace — clear it
        if (payload.connectorConfig.managedCluster) {
          payload.namespace = null;
        }
      }
      // Auth credentials go into authConfig JSONB
      if (formData.authType && formData.authType !== 'None') {
        if (formData.authType === 'API Key' || formData.authType === 'Token' || formData.authType === 'Token (Conductor OSS)') {
          payload.authConfig.keyId = formData.authKeyId || '';
          payload.authConfig.keySecret = formData.authKeySecret || '';
        } else if (formData.authType === 'OAuth2') {
          payload.authConfig.clientId = formData.authKeyId || '';
          payload.authConfig.clientSecret = formData.authKeySecret || '';
        } else if (formData.authType === 'mTLS') {
          payload.authConfig.certPath = formData.tlsCertPath || '';
          payload.authConfig.keyPath = formData.tlsKeyPath || '';
          payload.authConfig.caPath = formData.tlsCaPath || '';
        }
      }
      await api.discoveryCreateConnectorConfig(payload);
      addToast('Connector created successfully', 'success');
      loadConnections();
    } catch (err) {
      addToast('Failed to add connection: ' + (err.message || err), 'error');
    }
  };

  const handleTestConnection = async (connId) => {
    try {
      addToast('Testing connection…', 'info');
      const res = await api.discoveryTestConnector(connId);
      const result = res?.data || res || {};
      if (result.success) {
        addToast(`Connection test passed (${result.latencyMs || 0}ms, v${result.serverVersion || 'unknown'})`, 'success');
      } else {
        addToast(`Connection test failed: ${result.message || 'Unknown error'}${result.errorDetail ? ' — ' + result.errorDetail : ''}`, 'error');
      }
      loadConnections(); // Refresh status
    } catch (err) {
      addToast('Failed to test connection: ' + (err.message || err), 'error');
    }
  };

  const [discoveringConnId, setDiscoveringConnId] = useState(null);
  // Reconciliation prompt state — populated when a discovery scan returns
  // orphan candidates. The scan itself is non-destructive; tombstoning
  // only happens after the user confirms inside ReconciliationConfirmModal.
  const [reconcilePrompt, setReconcilePrompt] = useState(null); // { connId, connName, reconciliation }
  const [reconcilingPending, setReconcilingPending] = useState(false);

  const handleDiscoverWorkflows = async (connId) => {
    try {
      setDiscoveringConnId(connId || 'all');
      // Surface drift from each scanned connector. We aggregate across
      // an "all" sweep so the user sees ONE confirm prompt at a time
      // (UI shows the highest-cardinality drift first).
      let pendingPrompt = null;
      if (!connId) {
        // "All Connectors" — discover from each connected connector
        const activeConns = (connectionsData || []).filter(c => c.status === 'CONNECTED');
        if (activeConns.length === 0) {
          addToast('No connected connectors available for discovery', 'warning');
          return;
        }
        addToast(`Discovering workflows from ${activeConns.length} connector(s)…`, 'info');
        let totalFound = 0;
        for (const conn of activeConns) {
          try {
            const res = await api.discoveryTriggerDiscover(conn.id);
            const d = res?.data || res || {};
            totalFound += d.totalDiscovered || 0;
            if (d.reconciliation && d.reconciliation.requiresReconciliation) {
              const candidatesCount = d.reconciliation.totalCandidates || 0;
              if (!pendingPrompt || candidatesCount > (pendingPrompt.reconciliation?.totalCandidates || 0)) {
                pendingPrompt = { connId: conn.id, connName: conn.name, reconciliation: d.reconciliation };
              }
            }
          } catch (e) {
            console.warn(`Discovery failed for ${conn.name}: ${e.message}`);
          }
        }
        addToast(`Discovery complete: ${totalFound} workflow(s) found across ${activeConns.length} connector(s)`, 'success');
      } else {
        addToast('Workflow discovery started — scanning orchestrator…', 'info');
        const res = await api.discoveryTriggerDiscover(connId);
        const discovered = res?.data || res || {};
        const count = discovered.totalDiscovered || 0;
        addToast(`Discovery complete: ${count} workflow(s) found`, 'success');
        if (discovered.reconciliation && discovered.reconciliation.requiresReconciliation) {
          const conn = (connectionsData || []).find(c => c.id === connId);
          pendingPrompt = {
            connId,
            connName: conn?.name,
            reconciliation: discovered.reconciliation,
          };
        }
      }
      // Reload all relevant tabs first so the page shows refreshed
      // counts before the modal mounts.
      loadDiscoveryDashboard();
      loadConnections();
      loadWorkflows();
      if (pendingPrompt) {
        setReconcilePrompt(pendingPrompt);
      }
    } catch (err) {
      addToast('Discovery failed: ' + (err.message || err), 'error');
    } finally {
      setDiscoveringConnId(null);
    }
  };

  const handleConfirmReconciliation = async (orphanIds) => {
    if (!reconcilePrompt) return;
    try {
      setReconcilingPending(true);
      const res = await api.discoveryReconcileConfirm(reconcilePrompt.connId, orphanIds);
      const d = res?.data || res || {};
      const t = d.tombstonedCount || 0;
      const skipped = d.skippedCount || 0;
      addToast(
        skipped > 0
          ? `Tombstoned ${t} workflow${t === 1 ? '' : 's'} (${skipped} skipped — already tombstoned or PROTECTED)`
          : `Tombstoned ${t} workflow${t === 1 ? '' : 's'}. Non-converted rows will purge in 7 days.`,
        'success'
      );
      setReconcilePrompt(null);
      // Reload so the tombstoned rows render with their new status badges.
      loadDiscoveryDashboard();
      loadWorkflows();
    } catch (err) {
      addToast('Reconciliation failed: ' + (err.message || err), 'error');
    } finally {
      setReconcilingPending(false);
    }
  };

  const handleRemoveConnection = async (connId) => {
    try {
      await api.apiFetch(`/api/v1/discovery/connectors/configs/${connId}`, 'DELETE');
      addToast('Connector removed', 'success');
      loadConnections();
    } catch (err) {
      addToast('Failed to remove connection: ' + (err.message || err), 'error');
    }
  };

  const [configureConnId, setConfigureConnId] = useState(null);

  const handleConfigure = async (connId) => {
    setConfigureConnId(connId === configureConnId ? null : connId);
  };

  const handleProtect = async (workflowId, navigateToGel = false) => {
    try {
      if (navigateToGel) {
        // Already registering — just navigate to see the pipeline
        setActiveTab('gel-registration');
        loadGelRegistration();
        return;
      }
      addToast('Initiating GEL protection pipeline…', 'info');
      await api.discoveryProtectWorkflow(workflowId);
      addToast('GEL registration pipeline initiated — classifying workflow', 'success');
      loadGelRegistration();
      loadWorkflows(); // Refresh status to show REGISTERING
      // Navigate to GEL Registration tab to show the new pipeline
      setActiveTab('gel-registration');
    } catch (err) {
      addToast('Failed to protect workflow: ' + (err.message || err), 'error');
    }
  };

  const [nativeConversionPreview, setNativeConversionPreview] = useState(null);
  const [nativeConversionLoading, setNativeConversionLoading] = useState(false);

  const handleConvertNativePreview = async (workflowId, workflowName) => {
    try {
      setNativeConversionLoading(true);
      addToast(`Analyzing "${workflowName}" for native conversion…`, 'info');
      const res = await api.apiFetch(`/api/v1/discovery/workflows/${workflowId}/convert-native/preview`, 'POST');
      if (!res.ok) {
        throw new Error(res.error || 'Preview request failed');
      }
      setNativeConversionPreview({ workflowId, workflowName, report: res.data });
      setNativeConversionLoading(false);
    } catch (err) {
      setNativeConversionLoading(false);
      addToast('Preview failed: ' + (err.message || err), 'error');
    }
  };

  const handleConvertNativeCommit = async () => {
    if (!nativeConversionPreview) return;
    const { workflowId, workflowName } = nativeConversionPreview;
    try {
      setNativeConversionLoading(true);
      addToast(`Converting "${workflowName}" to native Canvas workflow…`, 'info');
      const res = await api.apiFetch(`/api/v1/discovery/workflows/${workflowId}/convert-native`, 'POST', {});
      setNativeConversionLoading(false);
      setNativeConversionPreview(null);
      if (!res.ok) {
        addToast(`Conversion failed: ${res.error}`, 'error');
        return;
      }
      const result = res.data;
      if (result.success) {
        const msg = result.alreadyConverted
          ? `"${workflowName}" was already converted — existing native workflow loaded.`
          : `"${workflowName}" converted to native Canvas DAG: ${result.totalCanvasNodes} nodes, ${result.totalConnections} connections (${result.conversionDurationMs}ms)`;
        addToast(msg, 'success');
        if (result.warningCount > 0) {
          addToast(`${result.warningCount} conversion warning(s) — review in Canvas editor`, 'warning');
        }
        loadWorkflows();
        // Auto-navigate to Canvas editor after short delay
        if (result.nativeWorkflowId) {
          setTimeout(() => handleViewCanvas(result.nativeWorkflowId), 1500);
        }
      } else {
        addToast(`Conversion failed: ${result.error}`, 'error');
      }
    } catch (err) {
      setNativeConversionLoading(false);
      addToast('Native conversion failed: ' + (err.message || err), 'error');
    }
  };

  // #76 — "Run Natively" from a workflow row now jumps into the
  // dedicated Visual Mapper tab with the clicked workflow auto-
  // selected, rather than opening the inline preview modal. The
  // preview modal path (handleConvertNativePreview) is kept intact for
  // any caller that still wants the quick-commit shortcut, but the
  // primary entry is the full pipeline so operators can review the
  // Analyze → Map → Review JSON → Commit steps before committing.
  const handleConvertNative = (workflowId /*, workflowName */) => {
    setAutoSelectMapperWorkflowId(workflowId);
    setActiveTab('mapper');
  };

  const handleViewCanvas = (nativeWorkflowId) => {
    // Navigate to the Canvas page with the workflow ID as a query param
    window.location.href = `/canvas?workflowId=${nativeWorkflowId}`;
  };

  // ── Trigger GEL-Protected External Workflow Execution ─────────────────
  const [executionResult, setExecutionResult] = useState(null);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionModal, setExecutionModal] = useState(null); // { workflowId, workflowName, inputSchema, executionMode }

  const handleTriggerExecution = (workflowId, workflowName, executionMode = 'auto') => {
    // Find the workflow to get its inputSchema — check both workflows and bindings
    const wf = workflowsData.find(w => w.id === workflowId);
    const binding = bindingsData.find(b => b.discoveredWorkflowId === workflowId);
    const resolvedSchema = wf?.inputSchema || binding?.inputSchema || null;
    const resolvedName = workflowName || wf?.name || binding?.workflowName || workflowId;
    setExecutionModal({
      workflowId,
      workflowName: resolvedName,
      name: resolvedName,
      workflowType: resolvedName,
      inputSchema: resolvedSchema,
      executionMode,
    });
  };

  // #79 — Single entry for the two "Execute Externally" buttons on a
  // workflow row. The button is a unified action whose semantics
  // depend on the workflow's current lifecycle:
  //   • PROTECTED — open the execution input modal directly.
  //   • Anything else (DISCOVERED / ANALYZING / MAPPED) — kick off the
  //     GEL protection pipeline; once the row reaches ACTIVE the user
  //     can click the same button again to actually invoke.
  // The mode argument ('direct' vs 'canvas') maps to the executionMode
  // contract handleTriggerExecution already understands.
  const handleExecuteExternally = (workflow, mode) => {
    if (!workflow || !workflow.id) return;
    if (workflow.status === 'PROTECTED') {
      handleTriggerExecution(workflow.id, workflow.name, mode);
      return;
    }
    // Not yet protected — start the registration pipeline. The user
    // will see the new pipeline appear in Active Registration Pipelines
    // on the Overview tab; once it reaches ACTIVE this same button
    // becomes a direct execute.
    addToast(
      `${workflow.name} is not yet GEL-protected. Starting the registration pipeline now — once it reaches ACTIVE you can re-click "Execute Externally" to invoke.`,
      'info'
    );
    handleProtect(workflow.id);
  };

  const handleExecuteWithInput = async (input) => {
    if (!executionModal) return;
    const { workflowId, workflowName, executionMode } = executionModal;
    try {
      setExecutionLoading(true);
      setExecutionResult(null);
      const modeLabel = executionMode === 'canvas' ? 'via Canvas' : executionMode === 'direct' ? 'with GEL (direct)' : 'with GEL';
      addToast(`Executing "${workflowName}" ${modeLabel}…`, 'info');

      const result = await api.discoveryStartExternalWorkflow(workflowId, input || {}, executionMode || 'auto');

      if (result.blocked) {
        addToast(`Execution BLOCKED by GEL: ${result.blockReason || 'policy violation'}`, 'error');
      } else {
        addToast(
          `Workflow started on ${result.connectorType || 'external orchestrator'}: ${result.externalWorkflowId}` +
          (result.gelDecision ? ` — GEL: ${result.gelDecision}` : '') +
          (result.dpgFindingsCount > 0 ? ` — ${result.dpgFindingsCount} DPG finding(s)` : ''),
          result.gelDecision === 'WARN' ? 'warning' : 'success'
        );
      }

      setExecutionResult(result);
      setExecutionModal(null); // Close modal on success
    } catch (err) {
      addToast('Execution failed: ' + (err.message || err), 'error');
    } finally {
      setExecutionLoading(false);
    }
  };

  const handleAdvancePipeline = async (pipelineId) => {
    try {
      addToast('Advancing pipeline…', 'info');
      const res = await api.discoveryGelAdvancePipeline(pipelineId);
      const pipeline = res?.data || res || {};
      addToast(`Pipeline advanced to ${pipeline.pipelineStatus || 'next step'}`, 'success');
      loadGelRegistration();
    } catch (err) {
      addToast('Failed to advance pipeline: ' + (err.message || err), 'error');
    }
  };

  const handleActivatePipeline = async (pipelineId) => {
    try {
      addToast('Activating governance enforcement…', 'info');
      await api.discoveryGelActivatePipeline(pipelineId);
      addToast('Governance enforcement activated — monitoring workflow executions', 'success');
      loadGelRegistration();
      loadBindings(); // New binding should appear
    } catch (err) {
      addToast('Failed to activate pipeline: ' + (err.message || err), 'error');
    }
  };

  const handleCancelPipeline = async (pipelineId) => {
    try {
      await api.apiFetch(`/api/v1/discovery/gel/pipeline/${pipelineId}/cancel`, 'POST');
      addToast('Pipeline cancelled', 'success');
      loadGelRegistration();
    } catch (err) {
      addToast('Failed to cancel pipeline: ' + (err.message || err), 'error');
    }
  };

  const handleChangeEnforcementMode = async (bindingId, newMode) => {
    try {
      await api.discoveryUpdateBindingMode(bindingId, newMode);
      addToast(`Enforcement mode changed to ${newMode}`, 'success');
      loadBindings();
    } catch (err) {
      addToast('Failed to change enforcement mode: ' + (err.message || err), 'error');
    }
  };

  const handleViewAuditTrail = (bindingId) => {
    // Navigate to the GEL Audit tab to see the full enforcement audit
    setActiveTab('audit');
    loadAuditDashboard();
  };

  const handleRematerializeDags = async () => {
    try {
      addToast('Rebuilding all Canvas enforcement DAGs…', 'info');
      const res = await api.discoveryRematerializeDags();
      const data = res?.data || res || {};
      addToast(
        `Rebuilt ${data.rematerialized || 0} Canvas DAG(s)` +
        (data.errors > 0 ? ` (${data.errors} error(s))` : ' — all DAGs updated'),
        data.errors > 0 ? 'warning' : 'success'
      );
      await loadBindings();
    } catch (err) {
      addToast('Failed to rebuild DAGs: ' + (err.message || err), 'error');
    }
  };

  const handleRepairAllChains = async () => {
    try {
      addToast('Repairing all audit hash chains…', 'info');
      const res = await api.discoveryRepairAllAuditChains();
      const data = res?.data || res || {};
      const repaired = data.totalRepaired || 0;
      addToast(
        repaired > 0
          ? `Repaired ${repaired} envelope(s) across ${data.bindingsProcessed} binding(s) — chains verified`
          : `All ${data.bindingsProcessed} chain(s) already valid — no repair needed`,
        repaired > 0 ? 'success' : 'info'
      );
      await loadAuditDashboard();
    } catch (err) {
      addToast('Failed to repair chains: ' + (err.message || err), 'error');
    }
  };

  const handleRepairChain = async (bindingId) => {
    try {
      addToast('Repairing hash chain…', 'info');
      const res = await api.discoveryRepairAuditChain(bindingId);
      const data = res?.data || res || {};
      addToast(
        data.repairedCount > 0
          ? `Repaired ${data.repairedCount} of ${data.totalEnvelopes} envelope(s) — chain verified`
          : `Chain already valid (${data.totalEnvelopes} envelope(s)) — no repair needed`,
        data.repairedCount > 0 ? 'success' : 'info'
      );
      await loadAuditDashboard();
    } catch (err) {
      addToast('Failed to repair chain: ' + (err.message || err), 'error');
    }
  };

  const activeTabMeta = TABS.find(t => t.key === activeTab);
  /* G101 — v2 TabNav config */
  const v2Tabs = TABS.map(t => ({ id: t.key, label: t.label }));

  return (
    <V2PageShell
      title="Workflow Discovery"
      subtitle="Discover, protect, and observe workflows running across your enterprise stack."
      tags={<V2Pill tone="trust" size="sm">Discovery</V2Pill>}
    >
      <GlobalStyles />

      <V2TabNav
        tabs={v2Tabs}
        activeId={activeTab}
        onChange={setActiveTab}
        ariaLabel="Workflow Discovery sections"
      />

      {/* Active tab description (kept from v1) */}
      {activeTabMeta && (
        <div className="px-3 py-2 mt-3 mb-4 rounded-v2-sm border border-v2-border-subtle bg-v2-muted text-[12px] text-v2-ink-secondary flex items-center gap-2">
          {React.createElement(activeTabMeta.icon, { size: 12 })}
          <span>{activeTabMeta.desc}</span>
        </div>
      )}

      {/* Tab Content */}
      <div className="overflow-y-auto">
        {(activeTab === 'overview' || activeTab === 'discovery') && (
          <>
            {/* Hero stats + Discovery Scan Activity placeholder still
                live inside DiscoveryDashboardTab — it owns its own
                padding (24px) so we don't wrap it. The placeholder
                GEL pipeline section it used to host was deleted in
                #77; the rich version renders below as an embedded
                GELRegistrationTab with matching horizontal padding. */}
            <DiscoveryDashboardTab loading={loading} data={discoveryData} connectors={connectionsData} onDiscoverWorkflows={handleDiscoverWorkflows} />
            <div style={{ padding: '0 24px 24px' }}>
              <GELRegistrationTab
                embedded={true}
                loading={loading}
                data={gelRegistrationData}
                onAdvancePipeline={handleAdvancePipeline}
                onActivatePipeline={handleActivatePipeline}
                onCancelPipeline={handleCancelPipeline}
                /* No onTriggerExecution — Execute lives only on the
                   Workflows tab's row primary action (#74). */
              />
            </div>
          </>
        )}
        {(activeTab === 'sources' || activeTab === 'connections') && (
          <WorkflowConnectorsTab
            loading={loading}
            data={connectionsData}
            onAddConnection={handleAddConnection}
            onTestConnection={handleTestConnection}
            onDiscoverWorkflows={handleDiscoverWorkflows}
            onRemoveConnection={handleRemoveConnection}
            onConfigure={handleConfigure}
            configureConnId={configureConnId}
            onSaveConfig={async (connId, cfg) => {
              try {
                // Normalize host before saving
                let cleanHost = (cfg.host || '').trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/\/+$/, '');
                const updatePayload = {
                  host: cleanHost,
                  port: cfg.port,
                  authType: AUTH_TYPE_TO_BACKEND[cfg.authType] || cfg.authType || 'NONE',
                  scanIntervalMinutes: cfg.scanIntervalMinutes,
                  autoScanEnabled: cfg.autoScanEnabled,
                  tlsEnabled: cfg.tlsEnabled,
                  namespace: cfg.namespace,
                  connectorConfig: { domain: cfg.domain, apiVersion: cfg.apiVersion, workflowPrefix: cfg.workflowPrefix },
                };
                // Build authConfig — only send if user actually typed new credentials.
                // If only keyId changed but secret is blank, preserve the existing secret by
                // omitting authConfig entirely (backend only updates fields that are present).
                const authBackend = updatePayload.authType;
                if (authBackend === 'TOKEN' || authBackend === 'API_KEY') {
                  if (cfg.authKeySecret) {
                    // User entered a new secret — send both keyId and keySecret
                    updatePayload.authConfig = { keyId: cfg.authKeyId, keySecret: cfg.authKeySecret };
                  } else if (cfg.authKeyId && !cfg.hasExistingSecret) {
                    // New credential entry (no existing secret) — send what we have
                    updatePayload.authConfig = { keyId: cfg.authKeyId, keySecret: '' };
                  }
                  // If hasExistingSecret && no new secret typed → don't send authConfig, keep existing
                } else if (authBackend === 'OAUTH2') {
                  if (cfg.authKeySecret) {
                    updatePayload.authConfig = { clientId: cfg.authKeyId, clientSecret: cfg.authKeySecret };
                  } else if (cfg.authKeyId && !cfg.hasExistingSecret) {
                    updatePayload.authConfig = { clientId: cfg.authKeyId, clientSecret: '' };
                  }
                } else if (authBackend === 'MTLS') {
                  updatePayload.authConfig = { certPath: cfg.tlsCertPath, keyPath: cfg.tlsKeyPath, caPath: cfg.tlsCaPath };
                }
                await api.discoveryUpdateConnectorConfig(connId, updatePayload);
                addToast('Configuration saved', 'success');
                loadConnections();
              } catch (err) {
                addToast('Failed to save config: ' + (err.message || err), 'error');
              }
            }}
          />
        )}
        {activeTab === 'workflows' && (
          <>
            <DiscoveredWorkflowsTab loading={loading} data={workflowsData} onProtect={handleProtect} onConvertNative={handleConvertNative} onViewCanvas={handleViewCanvas} onTriggerExecution={handleTriggerExecution} onExecuteExternally={handleExecuteExternally} />
            {/* Execution Result Panel */}
            {executionResult && (
              <div style={{
                marginTop: 16, padding: 20, borderRadius: 12,
                background: executionResult.blocked ? '#fef2f2' : '#f0fdf4',
                border: `1px solid ${executionResult.blocked ? '#fecaca' : '#bbf7d0'}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {executionResult.blocked ? <AlertCircle size={18} color="#dc2626" /> : <CheckCircle size={18} color="#16a34a" />}
                    <span style={{ fontSize: 14, fontWeight: 700, color: executionResult.blocked ? '#dc2626' : '#16a34a' }}>
                      {executionResult.blocked ? 'Execution Blocked by GEL' : 'Workflow Started Successfully'}
                    </span>
                  </div>
                  <button onClick={() => setExecutionResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <X size={16} color="#9ca3af" />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, fontSize: 12, color: '#374151' }}>
                  {executionResult.externalWorkflowId && (
                    <div><span style={{ fontWeight: 600, color: '#6b7280' }}>Workflow ID:</span> <span style={{ fontFamily: 'monospace' }}>{executionResult.externalWorkflowId}</span></div>
                  )}
                  <div><span style={{ fontWeight: 600, color: '#6b7280' }}>Status:</span> {executionResult.status}</div>
                  <div><span style={{ fontWeight: 600, color: '#6b7280' }}>Connector:</span> {executionResult.connectorType}</div>
                  {executionResult.gelEnforced && (
                    <>
                      <div><span style={{ fontWeight: 600, color: '#6b7280' }}>GEL Decision:</span>{' '}
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                          background: executionResult.gelDecision === 'ALLOW' ? '#dcfce7' : executionResult.gelDecision === 'WARN' ? '#fef9c3' : '#fee2e2',
                          color: executionResult.gelDecision === 'ALLOW' ? '#166534' : executionResult.gelDecision === 'WARN' ? '#854d0e' : '#991b1b',
                        }}>{executionResult.gelDecision}</span>
                      </div>
                      <div><span style={{ fontWeight: 600, color: '#6b7280' }}>Enforcement:</span> {executionResult.enforcementDurationMs}ms</div>
                      {executionResult.dpgFindingsCount > 0 && (
                        <div><span style={{ fontWeight: 600, color: '#6b7280' }}>DPG Findings:</span> {executionResult.dpgFindingsCount} sensitive field(s) detected</div>
                      )}
                    </>
                  )}
                  {executionResult.blockReason && (
                    <div style={{ gridColumn: '1 / -1' }}><span style={{ fontWeight: 600, color: '#6b7280' }}>Block Reason:</span> {executionResult.blockReason}</div>
                  )}
                  {executionResult.bindingId && (
                    <div><span style={{ fontWeight: 600, color: '#6b7280' }}>Binding:</span> <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{executionResult.bindingId}</span></div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        {activeTab === 'mapper' && (
          <VisualWorkflowMapperTab
            loading={loading}
            workflows={workflowsData}
            onViewCanvas={handleViewCanvas}
            addToast={addToast}
            autoSelectWorkflowId={autoSelectMapperWorkflowId}
            onAutoSelectConsumed={() => setAutoSelectMapperWorkflowId(null)}
          />
        )}
        {activeTab === 'gel-registration' && (
          <GELRegistrationTab
            loading={loading}
            data={gelRegistrationData}
            onAdvancePipeline={handleAdvancePipeline}
            onActivatePipeline={handleActivatePipeline}
            onCancelPipeline={handleCancelPipeline}
            onTriggerExecution={handleTriggerExecution}
          />
        )}
        {activeTab === 'bindings' && (
          <GovernanceBindingsTab
            loading={loading}
            data={bindingsData}
            onChangeEnforcementMode={handleChangeEnforcementMode}
            onViewAuditTrail={handleViewAuditTrail}
            onTriggerExecution={handleTriggerExecution}
            onRematerializeDags={handleRematerializeDags}
          />
        )}
        {activeTab === 'audit' && (
          <AuditEnforcementTab
            loading={loading}
            dashboard={auditDashboard}
            onRefresh={loadAuditDashboard}
            onRepairAll={handleRepairAllChains}
            onRepairChain={handleRepairChain}
          />
        )}
      </div>

      {/* Execution Input Modal */}
      {executionModal && (
        <ExecutionInputModal
          workflow={executionModal}
          onExecute={handleExecuteWithInput}
          onClose={() => setExecutionModal(null)}
        />
      )}

      {/* Native Conversion Preview Modal */}
      {nativeConversionPreview && (
        <NativeConversionPreviewModal
          preview={nativeConversionPreview}
          onCommit={handleConvertNativeCommit}
          onClose={() => setNativeConversionPreview(null)}
          loading={nativeConversionLoading}
        />
      )}

      {/* Reconciliation Confirm Modal — task #64 */}
      {reconcilePrompt && (
        <ReconciliationConfirmModal
          reconciliation={reconcilePrompt.reconciliation}
          connectorName={reconcilePrompt.connName}
          onConfirm={handleConfirmReconciliation}
          onCancel={() => setReconcilePrompt(null)}
          loading={reconcilingPending}
        />
      )}
    </V2PageShell>
  );
}
